
// === resources/js/constants.js ===
// Application Constants - Centralized configuration

const AppConstants = {
    // File handling
    FILE: {
        FILENAME_LENGTH: 6,
        MAX_SIZE_MB: 10,
        VALID_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml']
    },

    // UI Configuration
    UI: {
        ALERT_DURATION: 3000,
        ALERT_FADE_SPEED: 300,
        MAX_ALERTS: 3,
        WIZARD_TOTAL_STEPS: 4
    },

    // Canvas Configuration
    CANVAS: {
        SCALE_MAX_MULTIPLIER: 0.002,
        SNAP_ZONE_RATIO: 20, // canvas.width / 20
        TARGET_FIND_TOLERANCE: 15,
        CORNER_SIZE_MULTIPLIER: 0.03,
        BORDER_SCALE_FACTOR: 2,
        PADDING: 4,
        ROTATION_SNAP_TOLERANCE: 2, // degrees
        ROTATION_SNAP_ANGLES: [0, 90, 180, 270]
    },

    // Export Configuration
    EXPORT: {
        DEFAULT_DPI: 200,
        SCREEN_DPI: 72,
        MAX_PIXELS: 250000000, // 250MP browser limit
        QR_MIN_SIZE: 512,
        QR_MAX_SIZE: 2048,
        QR_BORDER_PERCENT: 0.05
    },

    // Element Scaling
    SCALING: {
        TEXT_DEFAULT_WIDTH_RATIO: 0.8,
        TEXT_DEFAULT_HEIGHT_RATIO: 0.5,
        IMAGE_DEFAULT_WIDTH_RATIO: 0.5,
        IMAGE_DEFAULT_HEIGHT_RATIO: 0.4,
        QR_DEFAULT_WIDTH_RATIO: 0.25,
        QR_DEFAULT_HEIGHT_RATIO: 0.25,
        CIRCLE_DEFAULT_WIDTH_RATIO: 0.3,
        CIRCLE_DEFAULT_HEIGHT_RATIO: 0.3,
        CROSS_DEFAULT_WIDTH_RATIO: 0.4,
        CROSS_DEFAULT_HEIGHT_RATIO: 0.3
    },

    // Circle Clip Sizes
    CIRCLE_SIZES: {
        LARGE: { value: 2, percentage: 0.9 },
        MEDIUM: { value: 3, percentage: 0.7 },
        SMALL: { value: 4, percentage: 0.5 }
    },

    // Canvas colors. These are hex/css literals because they are applied to
    // fabric.js canvas objects, which do not resolve CSS custom properties.
    // The brand colors are kept in sync with the Gruene-AT design-system tokens:
    //   var(--gat-color-primary) = #257639  -> BACKGROUND_*, LOGO_TEXT
    //   var(--gat-color-magenta) = #E6007E  -> PINK_CIRCLE
    // (CORNER_COLOR / BORDER_COLOR are fabric.js selection-handle colors,
    //  not part of the rendered output, and are intentionally left unchanged.)
    // If the DS palette changes, update these constants in lockstep.
    COLORS: {
        BACKGROUND_PRIMARY: "#257639",
        BACKGROUND_SECONDARY: "#257639",
        PINK_CIRCLE: "#E6007E",
        LOGO_TEXT: "#257639",
        // Region name on the logo bar is a transparent KNOCKOUT, not a fill.
        // It is rendered with globalCompositeOperation 'destination-out', so the
        // colour is immaterial (only the glyph alpha erases the white bar). Kept
        // opaque white for a crisp erase. LOGO_TEXT is retained for reference/back-compat.
        LOGO_KNOCKOUT: "#ffffff",
        TEXT_STROKE: "#000000",
        CORNER_COLOR: "yellow",
        BORDER_COLOR: "rgba(88,42,114)",
        CORNER_STROKE: "#000000"
    },

    // Font Configuration
    FONTS: {
        FAMILY: "Gotham Narrow",
        DEFAULT_LOGO: "Gotham Narrow",
        DEFAULT_TEXT: "Gotham Narrow",
        // Gotham Narrow ships as four separate faces, registered under one
        // family in input.css. Only these three weights exist — 500/600/800
        // would silently snap to a neighbour.
        WEIGHT_TEXT: 900,    // default canvas text (Ultra)
        WEIGHT_LOGO: 700,    // logo overlay text (Bold)
        WEIGHT_BOOK: 400,    // body / book equivalent (Book)
        // Manual tracking for fabric.js text (charSpacing, 1/1000 em).
        // 0 = Gotham Narrow's own metrics. The -30 ("-3") tracking existed
        // only to make the wider Raleway approximate Gotham's set width;
        // with the real font it would over-tighten.
        CHAR_SPACING: 0,
        // User-selectable text fonts, labelled DESCRIPTIVELY (type + use),
        // never by brand name. Option ids match #font-style-select values.
        OPTIONS: [
            { id: 'standard', label: 'Standardschrift — Headlines & Fließtext', family: 'Gotham Narrow', weight: 900, style: 'normal' },
            { id: 'accent',   label: 'Betonte Serifenschrift — für Zitate & Akzente', family: 'Vollkorn', weight: 900, style: 'italic' }
        ],
        // Each entry carries its own family so the preload observer binds to
        // the correct font. Mixing families under a single observer family
        // would leave the other font unloaded (serif fallback on first use).
        PRELOAD_FONTS: [
            { family: 'Gotham Narrow', weight: 900, style: 'italic' },  // italic accent
            { family: 'Gotham Narrow', weight: 900 },                   // default text
            { family: 'Gotham Narrow', weight: 400 },                   // book / body
            { family: 'Gotham Narrow', weight: 700 },                   // default logo
            { family: 'Vollkorn', weight: 900, style: 'italic' },              // accent serif (Black Italic)
            { family: 'Vollkorn', weight: 400 }                                // accent serif (book)
        ]
    },

    // Logo Configuration
    LOGO: {
        TEXT_SCALE_LONG: 4.8,
        TEXT_SCALE_SHORT: 6,
        MAX_TEXT_LENGTH: 16,
        LINE_HEIGHT: 0.8,
        ANGLE: -5.0,  // matches the logo bar's actual slope (white bar edges measured at -5.0°); keeps the region name parallel to the bar
        WIDTH_SCALE: 0.95,
        // Bar text positioning - use WIDTH as reference since it's constant
        BAR_OFFSET_FROM_TOP: 0.90, // Single-line text starts at 90% of logo WIDTH from logo top
        BAR_OFFSET_FROM_TOP_LONG: 0.89, // Two-line text starts slightly higher to fit within the taller bar
        // Automatic positioning configuration
        BORDERLESS_MARGIN_PERCENT: 0.02, // For borderless templates: logo bottom margin as percentage of canvas height (2%)
        // Two artworks of the same logo. The "blanko" files are pure white on
        // transparent: over the green canvas the enclosed areas — the counters
        // of the G, the letters in the white bar — show the green through, and
        // that is how the logo is meant to read. Over a background photograph
        // they show the PHOTO, and the wordmark becomes unreadable.
        //
        // The "gruen" files have exactly those enclosed areas filled with the
        // brand green; the outer transparency is untouched, so the logo still
        // has no box around it. Generated by scripts/make-green-logo.py.
        FILES: {
            LONG: "Logo-zweizeilig_blanko.png",
            SHORT: "Logo-einzeilig_blanko.png",
            LONG_FILLED: "Logo-zweizeilig_gruen.png",
            SHORT_FILLED: "Logo-einzeilig_gruen.png"
        }
    }
};

// Template configurations
// NOTE: All templates are borderless (border: 0).
// Logo positioned with 2% margin from canvas bottom (BORDERLESS_MARGIN_PERCENT).
// The white bar offset is at 90% of logo width (BAR_OFFSET_FROM_TOP).
const TemplateConstants = {
    TEMPLATES: {
        feed_post_45: {
            width: 1080, height: 1350, topBorderMultiplier: 1, border: 0, dpi: 200, logoWidth: 163,
        },
        story: {
            width: 1080, height: 1920, topBorderMultiplier: 1, border: 0, dpi: 200, logoWidth: 163,
        },
        event: {
            width: 1920, height: 1005, topBorderMultiplier: 1, border: 0, dpi: 200, logoWidth: 163,
        },
        facebook_header: {
            width: 820, height: 360, topBorderMultiplier: 1, border: 0, dpi: 150, logoWidth: 163,
        },
        // Article image for our own Gemeinde-/Bezirks-Websites. 2:3 portrait is
        // what the sites actually render: their images go through Cloudflare
        // Image Resizing with an explicit height, and the news/person cards ask
        // for ratio 0.667 (e.g. 640x960, up to 1920x2880). Canvas kept at the
        // 1080 width of the social templates so logo sizing stays comparable.
        artikel_23: {
            width: 1080, height: 1620, topBorderMultiplier: 1, border: 0, dpi: 200, logoWidth: 163,
        },
        a4: {
            width: 2480, height: 3508, topBorderMultiplier: 1, border: 0, dpi: 250, logoWidth: 374,
        },
        a4_quer: {
            width: 3508, height: 2480, topBorderMultiplier: 1, border: 0, dpi: 250, logoWidth: 374,
        },
        a5: {
            width: 1748, height: 2480, topBorderMultiplier: 1, border: 0, dpi: 300, logoWidth: 319,
        },
        a5_quer: {
            width: 2480, height: 1748, topBorderMultiplier: 1, border: 0, dpi: 300, logoWidth: 319,
        },
        a6: {
            width: 1240, height: 1748, topBorderMultiplier: 1, border: 0, dpi: 300, logoWidth: 224,
        },
        a6_quer: {
            width: 1748, height: 1240, topBorderMultiplier: 1, border: 0, dpi: 300, logoWidth: 224,
        },
    },

    // Get template by name
    getTemplate(templateName) {
        return this.TEMPLATES[templateName] || null;
    },

    // Get current template
    getCurrentTemplate() {
        const templateName = jQuery("#canvas-template").find(":selected").attr("value");
        return this.getTemplate(templateName);
    }
};

// Legacy support - keep the old template_values for backwards compatibility
const template_values = TemplateConstants.TEMPLATES;

// Helper function for backwards compatibility
function currentTemplate() {
    return TemplateConstants.getCurrentTemplate();
}

// Make available globally
window.AppConstants = AppConstants;
window.TemplateConstants = TemplateConstants;
window.template_values = template_values;
window.currentTemplate = currentTemplate;

// === resources/js/validation.js ===
// Validation Utilities - Centralized validation functions

const ValidationUtils = {
    // File validation
    VALID_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'],
    
    isValidImage(fileType) {
        return this.VALID_IMAGE_TYPES.includes(fileType);
    },

    isValidImageFile(file) {
        if (!file) return false;
        return this.isValidImage(file.type);
    },

    // JSON validation
    isValidJSON(str) {
        try {
            JSON.parse(str);
            return true;
        } catch (e) {
            return false;
        }
    },

    // Canvas validation
    validateStep1() {
        const template = jQuery('#canvas-template').val();
        const logo = jQuery('#logo-selection').val();
        const logoEnabled = LogoState.isLogoEnabled();

        const errors = [];

        if (!template) {
            errors.push('Bitte wählen Sie eine Vorlage aus.');
        }

        // Only validate logo if logo feature is enabled
        if (logoEnabled && !logo) {
            errors.push('Bitte wählen Sie ein Logo aus.');
        }

        return {
            isValid: errors.length === 0,
            errors: errors
        };
    },

    // Text validation
    validateTextInput(text) {
        if (!text || text.trim() === '') {
            return {
                isValid: false,
                error: 'Text field is empty'
            };
        }
        
        return {
            isValid: true,
            error: null
        };
    },

    // QR Code validation
    validateQRInput(text) {
        if (!text || text.trim() === '') {
            return {
                isValid: false,
                error: 'QR Code Text field is empty'
            };
        }
        
        // Check if QRCode library is available
        if (typeof QRCode === "undefined") {
            return {
                isValid: false,
                error: 'QR Code library not loaded'
            };
        }
        
        return {
            isValid: true,
            error: null
        };
    },

    // Canvas object validation
    validateCanvasObject(object, allowedTypes = []) {
        if (!object) {
            return {
                isValid: false,
                error: 'No object selected'
            };
        }

        if (allowedTypes.length > 0 && !allowedTypes.includes(object.type)) {
            return {
                isValid: false,
                error: `Invalid object type. Expected: ${allowedTypes.join(', ')}, got: ${object.type}`
            };
        }

        return {
            isValid: true,
            error: null
        };
    },

    // Circle clip validation
    validateCircleClipTarget(activeObject) {
        if (!activeObject) {
            return {
                isValid: false,
                error: 'Bitte wählen Sie zuerst ein Element aus, um einen Kreis-Ausschnitt anzuwenden.',
                type: 'warning'
            };
        }

        if (activeObject === contentImage) {
            return {
                isValid: false,
                error: 'Kreis-Ausschnitt kann nicht auf das Hintergrundbild angewendet werden. Fügen Sie zuerst ein eigenes Bild über "Bild hinzufügen" ein.',
                type: 'warning'
            };
        }

        if (activeObject === logo || activeObject === logoName) {
            return {
                isValid: false,
                error: 'Kreis-Ausschnitt kann nicht auf das Logo angewendet werden. Wählen Sie ein eingefügtes Bild aus.',
                type: 'warning'
            };
        }

        const isValidForCircle = 
            activeObject.type === "image" ||
            activeObject.type === "rect" ||
            activeObject.type === "circle";

        if (!isValidForCircle) {
            return {
                isValid: false,
                error: 'Kreis-Ausschnitt kann nur auf eingefügte Bilder und Elemente angewendet werden. Wählen Sie zuerst ein Element aus.',
                type: 'warning'
            };
        }

        return {
            isValid: true,
            error: null
        };
    },

    // Download validation
    validateDownload() {
        const logoEnabled = LogoState.isLogoEnabled();

        // Only validate logo if logo feature is enabled
        if (logoEnabled && (!logoText || logoText === "")) {
            return {
                isValid: false,
                error: 'Wähle bitte ein Logo aus vor dem Download!'
            };
        }

        return {
            isValid: true,
            error: null
        };
    },

    // File size validation
    validateFileSize(file, maxSizeMB = 10) {
        if (!file) return { isValid: false, error: 'No file provided' };
        
        const maxSizeBytes = maxSizeMB * 1024 * 1024;
        
        if (file.size > maxSizeBytes) {
            return {
                isValid: false,
                error: `File too large. Maximum size is ${maxSizeMB}MB`
            };
        }
        
        return {
            isValid: true,
            error: null
        };
    }
};

// Make available globally
window.ValidationUtils = ValidationUtils;

// === resources/js/logo-state.js ===
/**
 * LogoState - Logo Enable/Disable State Management
 *
 * Manages the state of whether logos should be automatically added
 * to canvases. Logo is enabled by default and state does NOT persist across sessions.
 */

const LogoState = {
    // In-memory state - always starts as enabled
    _enabled: true,

    // Whether the region name on the logo bar is a transparent KNOCKOUT.
    // True is right on the plain green canvas: the letters show the green
    // behind them. Over a background photo it is wrong — the photo shows
    // through the letters and the name becomes unreadable. Then the name is
    // filled with the brand green instead, which matches the plain canvas and
    // stays legible over anything.
    _knockout: true,

    /**
     * Check if logo feature is enabled
     * @returns {boolean} True if logo should be added to canvas
     */
    isLogoEnabled() {
        return this._enabled;
    },

    /**
     * Set logo enabled state (session only, does not persist)
     * @param {boolean} enabled - True to enable logo, false to disable
     */
    setLogoEnabled(enabled) {
        this._enabled = Boolean(enabled);
    },

    /**
     * Whether the region name is knocked out of the white bar.
     * @returns {boolean}
     */
    isKnockoutEnabled() {
        return this._knockout;
    },

    /**
     * Set the knockout mode (session only, does not persist).
     * @param {boolean} enabled - True for a transparent name, false for green
     */
    setKnockoutEnabled(enabled) {
        this._knockout = Boolean(enabled);
    },

    /**
     * Initialize logo state - always starts enabled
     * Call this on page load
     */
    initialize() {
        // Always start with logo enabled
        this._enabled = true;
        this._knockout = true;
    }
};

// Make LogoState available globally
if (typeof window !== 'undefined') {
    window.LogoState = LogoState;
}

// Also export for Node.js testing
if (typeof global !== 'undefined') {
    global.LogoState = LogoState;
}


// === resources/js/alert-system.js ===
// Unified Alert System - DS v2.2 .gat-toast / .gat-toaster + inline .gat-callout
//
// - Toaster (top-fixed global container) -> .gat-toaster with .gat-toast children
// - Inline alert containers (e.g. #qr-alert-container) -> .gat-callout
//
// Legacy hooks preserved for tests and back-compat:
// - container still accessible via .app-alert-container selector
// - rendered alert nodes still carry the .alert class

const AlertSystem = {
    // Configuration
    config: {
        duration: 5000,
        fadeSpeed: 300,
        maxAlerts: 3
    },

    // Map legacy alert types to DS v2.2 .gat-toast--* variants
    // and DS v2.1 .gat-callout--* variants (for inline qr-alert container).
    alertTypes: {
        success: { toast: 'gat-toast--success', callout: 'gat-callout--success', icon: 'fas fa-check-circle' },
        warning: { toast: 'gat-toast--warn',    callout: 'gat-callout--warn',    icon: 'fas fa-exclamation-triangle' },
        warn:    { toast: 'gat-toast--warn',    callout: 'gat-callout--warn',    icon: 'fas fa-exclamation-triangle' },
        danger:  { toast: 'gat-toast--error',   callout: 'gat-callout--error',   icon: 'fas fa-exclamation-circle' },
        error:   { toast: 'gat-toast--error',   callout: 'gat-callout--error',   icon: 'fas fa-exclamation-circle' },
        info:    { toast: 'gat-toast--info',    callout: 'gat-callout--info',    icon: 'fas fa-info-circle' }
    },

    // Get the global toast region (.gat-toaster). Reuses the legacy
    // .app-alert-container element if it is in the DOM so existing
    // markup/tests keep working.
    getContainer() {
        let container = jQuery('.gat-toaster').first();
        if (container.length === 0) {
            const legacy = jQuery('.app-alert-container').first();
            if (legacy.length) {
                legacy.addClass('gat-toaster').removeClass('hidden');
                container = legacy;
            } else {
                container = jQuery('<div class="gat-toaster app-alert-container" role="region" aria-live="polite" aria-label="Benachrichtigungen"></div>');
                jQuery('body').append(container);
            }
        }
        return container;
    },

    // Show alert with enhanced styling
    show(message, type = 'info', options = {}) {
        const container = options.container ? jQuery(options.container) : this.getContainer();
        return this.showInContainer(message, type, container, options);
    },

    // Show alert in specific container.
    // - .gat-toaster region -> render as .gat-toast (fixed corner)
    // - any other container -> render as inline .gat-callout
    showInContainer(message, type = 'info', container, options = {}) {
        const alertConfig = this.alertTypes[type] || this.alertTypes['info'];

        if (container.length === 0) {
            console.warn('Alert container not found');
            return null;
        }

        const isToastRegion = container.hasClass('gat-toaster') || container.hasClass('app-alert-container');

        // Clear existing alerts if specified in options
        if (options.clearExisting) {
            container.empty();
        } else {
            // Limit number of alerts
            const existingAlerts = container.find('.alert');
            if (existingAlerts.length >= this.config.maxAlerts) {
                existingAlerts.first().remove();
            }
        }

        const alertHTML = isToastRegion
            ? `
                <div class="gat-toast ${alertConfig.toast} alert" role="alert">
                    <span class="gat-toast__icon" aria-hidden="true">
                        <i class="${alertConfig.icon}"></i>
                    </span>
                    <div class="gat-toast__body">${message}</div>
                    <button type="button" class="gat-toast__close alert-close-btn" aria-label="Schließen">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
            `
            : `
                <div class="gat-callout ${alertConfig.callout} alert" role="alert">
                    <div class="gat-callout__body">${message}</div>
                    <button type="button" class="gat-callout__close alert-close-btn" aria-label="Schließen">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
            `;

        container.removeClass('hidden').append(alertHTML);

        // Get the newly added alert
        const newAlert = container.find('.alert').last();

        // Add click handler for close button
        newAlert.find('.alert-close-btn').on('click', () => {
            this.closeAlertInContainer(newAlert, container);
        });

        // Auto-remove after configured duration
        if (options.autoClose !== false) {
            setTimeout(() => {
                this.closeAlertInContainer(newAlert, container);
            }, options.duration || this.config.duration);
        }

        // Scroll into view if specified
        if (options.scrollIntoView) {
            this.scrollAlertIntoView(container);
        }

        return newAlert;
    },

    // Close specific alert
    closeAlert(alertElement) {
        const container = this.getContainer();
        this.closeAlertInContainer(alertElement, container);
    },

    // Close specific alert in container
    closeAlertInContainer(alertElement, container) {
        alertElement.fadeOut(this.config.fadeSpeed, () => {
            alertElement.remove();

            // Hide container if no more alerts (only for inline containers;
            // .gat-toaster is fixed-positioned and harmless when empty)
            if (container.find('.alert').length === 0 && !container.hasClass('gat-toaster')) {
                container.addClass('hidden');
            }
        });
    },

    // Scroll alert into view - completely disabled
    scrollAlertIntoView(container) {
        // Auto-scroll removed completely
        return;
    },

    // Close all alerts
    closeAll() {
        const container = this.getContainer();
        container.find('.alert').fadeOut(this.config.fadeSpeed, function() {
            jQuery(this).remove();
        });
        if (!container.hasClass('gat-toaster')) {
            setTimeout(() => {
                container.addClass('hidden');
            }, this.config.fadeSpeed);
        }
    },

    // Specialized alert methods
    success(message, options = {}) {
        return this.show(message, 'success', options);
    },

    warning(message, options = {}) {
        return this.show(message, 'warning', options);
    },

    error(message, options = {}) {
        return this.show(message, 'error', options);
    },

    danger(message, options = {}) {
        return this.show(message, 'danger', options);
    },

    info(message, options = {}) {
        return this.show(message, 'info', options);
    },

    // QR-specific alert methods
    showQRAlert(message, type = 'info', options = {}) {
        const qrOptions = {
            container: '#qr-alert-container',
            clearExisting: true,
            scrollIntoView: true,
            ...options
        };

        // Show inline in QR container as .gat-callout
        const qrAlert = this.showInContainer(message, type, jQuery('#qr-alert-container'), qrOptions);

        // Also show in global toaster region for dual display
        const mainAlert = this.show(message, type, { autoClose: options.autoClose });

        return { qrAlert, mainAlert };
    },

    // Legacy support for existing code
    showTailwindAlert(message, type = 'info') {
        return this.show(message, type);
    }
};

// Global functions for backwards compatibility
function showAlert(message, type = 'danger') {
    return AlertSystem.show(message, type);
}

function showTailwindAlert(message, type = 'info') {
    return AlertSystem.show(message, type);
}

function showQRAlert(message, type = 'info') {
    return AlertSystem.showQRAlert(message, type);
}

// Make available globally
window.AlertSystem = AlertSystem;
window.showAlert = showAlert;
window.showTailwindAlert = showTailwindAlert;
window.showQRAlert = showQRAlert;


// === resources/js/canvas-utils.js ===
// Canvas Utilities - Centralized canvas manipulation functions

// Canvas scaling and positioning utilities
const CanvasUtils = {
    // Scale element to fit within specified ratios
    scaleElementToFit(element, maxWidthRatio = 0.5, maxHeightRatio = 0.4) {
        const maxWidth = canvas.width * maxWidthRatio;
        const maxHeight = canvas.height * maxHeightRatio;

        const elementWidth = element.width || element.getScaledWidth();
        const elementHeight = element.height || element.getScaledHeight();

        const widthScale = maxWidth / elementWidth;
        const heightScale = maxHeight / elementHeight;
        const scale = Math.min(widthScale, heightScale);

        if (element.type === "text") {
            if (widthScale < heightScale) {
                element.scaleToWidth(maxWidth);
            } else {
                element.scaleToHeight(maxHeight);
            }
        } else if (element.scaleToWidth && element.scaleToHeight) {
            if (widthScale < heightScale) {
                element.scaleToWidth(maxWidth);
            } else {
                element.scaleToHeight(maxHeight);
            }
        } else {
            element.scale(scale);
        }

        element.setCoords();
    },

    // Control visibility configurations
    disableScalingControls(object) {
        object.setControlsVisibility({
            mt: false,
            mb: false,
            ml: false,
            mr: false,
            bl: false,
            br: false,
            tl: false,
            tr: false,
            mtr: false,
        });
    },

    relativeScalingControlsOnly(object) {
        object.setControlsVisibility({
            mt: false,
            mb: false,
            ml: false,
            mr: false,
            bl: true,
            br: true,
            tl: true,
            tr: true,
            mtr: true,
        });
    },

    // Canvas snapping functionality
    enableSnap(canvasInstance, snapZone = null) {
        console.log('[CanvasUtils.enableSnap] Initializing snap with canvas:', canvasInstance ? 'defined' : 'undefined');

        if (!snapZone) {
            snapZone = canvasInstance.width / AppConstants.CANVAS.SNAP_ZONE_RATIO;
        }

        console.log('[CanvasUtils.enableSnap] Snap zone calculated:', snapZone);

        canvasInstance.on("object:moving", function (options) {
            const target = options.target;

            // Skip if not eligible for snapping
            if (target === contentImage || target === logo || target === logoName) {
                return;
            }

            // Safety check for getCenterPoint method
            if (!target || typeof target.getCenterPoint !== 'function') {
                return;
            }

            const objectCenter = target.getCenterPoint();
            const canvasCenter = canvasInstance.width / 2;
            const distanceFromCenter = Math.abs(objectCenter.x - canvasCenter);

            if (distanceFromCenter < snapZone) {
                // Store center point before adjustment
                const centerBefore = target.getCenterPoint();

                // Calculate horizontal adjustment needed to center
                const deltaX = canvasCenter - centerBefore.x;

                // Apply adjustment while preserving vertical position
                target.set({
                    left: target.left + deltaX
                });
                target.setCoords();

                // Verify center after adjustment
                const centerAfter = target.getCenterPoint();

                console.log(
                    '%c[SNAP] CENTER SNAP',
                    'background: #257639; color: white; font-weight: bold; padding: 2px 6px;',
                    '\n  Object type:', target.type,
                    '\n  Center before:', `(${centerBefore.x.toFixed(2)}, ${centerBefore.y.toFixed(2)})`,
                    '\n  Center after:', `(${centerAfter.x.toFixed(2)}, ${centerAfter.y.toFixed(2)})`,
                    '\n  Canvas center:', canvasCenter.toFixed(2),
                    '\n  Adjustment:', deltaX.toFixed(2) + 'px',
                    '\n  Snap zone:', snapZone.toFixed(2) + 'px'
                );
            }
        });
    },

    // Rotation snapping functionality - snaps in real-time during rotation
    enableRotationSnap(canvasInstance, tolerance = null) {
        console.log('[CanvasUtils.enableRotationSnap] Initializing rotation snap with canvas:', canvasInstance ? 'defined' : 'undefined');

        if (!tolerance) {
            tolerance = AppConstants.CANVAS.ROTATION_SNAP_TOLERANCE;
        }

        const snapAngles = AppConstants.CANVAS.ROTATION_SNAP_ANGLES;
        console.log('[CanvasUtils.enableRotationSnap] Tolerance:', tolerance, 'Snap angles:', snapAngles);

        // Use object:rotating for real-time snapping while mouse is down
        canvasInstance.on("object:rotating", function (e) {
            const target = e.target;

            if (!target) {
                return;
            }

            // Exclude protected objects from rotation snapping
            if (target === contentImage || target === logo || target === logoName) {
                return;
            }

            // Safety check for getCenterPoint method
            if (typeof target.getCenterPoint !== 'function') {
                return;
            }

            // Normalize angle to 0-360 range
            let currentAngle = target.angle % 360;
            if (currentAngle < 0) {
                currentAngle += 360;
            }

            // Find closest snap angle
            let closestAngle = null;
            let minDifference = Infinity;

            for (const snapAngle of snapAngles) {
                // Calculate difference considering angle wrapping
                let difference = Math.abs(currentAngle - snapAngle);

                // Handle wrap-around (e.g., 359° to 0°)
                if (difference > 180) {
                    difference = 360 - difference;
                }

                if (difference < minDifference) {
                    minDifference = difference;
                    closestAngle = snapAngle;
                }
            }

            // Apply snap if within tolerance
            if (closestAngle !== null && minDifference <= tolerance) {
                // Store visual center point BEFORE rotation change
                const centerBefore = target.getCenterPoint();

                // Apply rotation snap
                target.set({ angle: closestAngle });

                // Get visual center point AFTER rotation change
                const centerAfter = target.getCenterPoint();

                // Calculate position adjustment to restore visual center
                const deltaX = centerBefore.x - centerAfter.x;
                const deltaY = centerBefore.y - centerAfter.y;

                // Restore original visual position
                target.set({
                    left: target.left + deltaX,
                    top: target.top + deltaY
                });

                target.setCoords();

                // Only log once per snap (reduce console spam)
                if (!target._isSnapped || target._snappedTo !== closestAngle) {
                    console.log(
                        '%c[SNAP] ROTATION SNAP',
                        'background: #e10078; color: white; font-weight: bold; padding: 2px 6px;',
                        '\n  Object type:', target.type,
                        '\n  Original angle:', currentAngle.toFixed(2) + '°',
                        '\n  Snapped to:', closestAngle + '°',
                        '\n  Difference:', minDifference.toFixed(2) + '°',
                        '\n  Tolerance:', tolerance + '°',
                        '\n  Center before:', `(${centerBefore.x.toFixed(2)}, ${centerBefore.y.toFixed(2)})`,
                        '\n  Center after:', `(${centerAfter.x.toFixed(2)}, ${centerAfter.y.toFixed(2)})`,
                        '\n  Position adjustment:', `(${deltaX.toFixed(2)}, ${deltaY.toFixed(2)})`
                    );
                }
                target._isSnapped = true;
                target._snappedTo = closestAngle;
            } else {
                // Clear snap state when outside tolerance
                target._isSnapped = false;
                target._snappedTo = null;
            }
        });
    },

    // Picture movement constraints
    //
    // The background is cover-scaled (see positionBackgroundImage), so it always
    // covers the content rect on both axes and overflows on the longer one. As
    // the user pans the image we clamp it so the content rect stays fully
    // covered — the image edge can never move inside the rect (which would
    // expose the green background). We clamp against the ACTUAL scaled size
    // rather than re-deriving a single-axis fit, so the logic is correct for
    // any aspect ratio.
    enablePictureMove(canvasInstance) {
        canvasInstance.on("object:moving", function (options) {
            if (options.target === contentImage) {
                const scaledWidth = options.target.getScaledWidth();
                const scaledHeight = options.target.getScaledHeight();

                // Allowed range for the image's top-left so the rect stays
                // covered: max edge flush at rect start, min edge flush at end.
                const maxLeft = contentRect.left;
                const minLeft = contentRect.left + contentRect.width - scaledWidth;
                const maxTop = contentRect.top;
                const minTop = contentRect.top + contentRect.height - scaledHeight;

                let left = options.target.left;
                let top = options.target.top;

                if (left > maxLeft) left = maxLeft;
                if (left < minLeft) left = minLeft;
                if (top > maxTop) top = maxTop;
                if (top < minTop) top = minTop;

                if (left !== options.target.left || top !== options.target.top) {
                    options.target.set({ left, top }).setCoords();
                }
            }
        });
    },

    // Background image positioning
    /**
     * Where the background photo is anchored when it overflows the frame.
     * 0.5/0.5 is the centre — what the app has always done. 0 shows the left
     * or top edge, 1 the right or bottom.
     *
     * The photo is cover-scaled, so one axis almost always overflows and
     * something gets cropped. Centring is a guess: a portrait in a landscape
     * template loses the top of the head, a group photo loses the people at
     * the edges. A person can drag the image to fix that (the overflowing
     * axis is left unlocked below); this is the same choice for callers who
     * cannot drag.
     */
    backgroundFocus: { x: 0.5, y: 0.5 },

    setBackgroundFocus(x, y) {
        const clamp = function (v, fallback) {
            return typeof v === "number" && v >= 0 && v <= 1 ? v : fallback;
        };
        this.backgroundFocus = {
            x: clamp(x, this.backgroundFocus.x),
            y: clamp(y, this.backgroundFocus.y),
        };
    },

    positionBackgroundImage() {
        if (contentImage != null) {
            canvas.remove(contentRect);
            canvas.remove(contentImage);
            
            contentImage.selectable = true;
            this.disableScalingControls(contentImage);

            const clipRect = new fabric.Rect({
                left: contentRect.left,
                top: contentRect.top,
                width: contentRect.width,
                height: contentRect.height,
                absolutePositioned: true,
            });

            // Cover-scale: pick the larger of the two fit ratios so the image
            // always fully covers the content rect on BOTH axes. The clipPath
            // crops whatever overflows. This guarantees no background gap
            // (green border) for any image/canvas aspect-ratio combination.
            const coverScale = Math.max(
                contentRect.width / contentImage.width,
                contentRect.height / contentImage.height
            );
            contentImage.scale(coverScale);

            // Lock the axis that fits exactly; leave the overflowing axis free
            // so the user can still pan the image to reframe it. When the
            // image covers exactly (same aspect) both axes lock.
            const scaledWidth = contentImage.getScaledWidth();
            const scaledHeight = contentImage.getScaledHeight();
            const widthOverflow = scaledWidth - contentRect.width;
            const heightOverflow = scaledHeight - contentRect.height;
            const EPS = 0.5; // sub-pixel tolerance
            contentImage.lockMovementX = widthOverflow <= EPS;
            contentImage.lockMovementY = heightOverflow <= EPS;

            contentImage.clipPath = clipRect;
            canvas.add(contentImage);
            canvas.sendToBack(contentImage);
            // Anchor the image over the CONTENT RECT (not the whole canvas, so
            // bordered templates stay correct). The overflow is distributed
            // according to backgroundFocus: 0.5 splits it evenly, which is the
            // long-standing centred behaviour; 0 pins the left/top edge, 1 the
            // right/bottom. Either way the rect stays fully covered.
            const focus = this.backgroundFocus;
            contentImage.set({
                left: contentRect.left - (scaledWidth - contentRect.width) * focus.x,
                top: contentRect.top - (scaledHeight - contentRect.height) * focus.y,
            });
            contentImage.setCoords();
        }
    },

    // Logo management
    bringLogoToFront() {
        if (logo) canvas.bringToFront(logo);
        if (logoName) canvas.bringToFront(logoName);
    },

    // Circle clipping utility
    applyCircleClip(activeObject, sizePercentage = 0.7) {
        if (!activeObject) return false;

        const isValidForCircle = 
            activeObject &&
            activeObject !== contentImage &&
            activeObject !== contentRect &&
            activeObject !== logo &&
            activeObject !== logoName &&
            (activeObject.type === "image" ||
             activeObject.type === "rect" ||
             activeObject.type === "circle");

        if (!isValidForCircle) return false;

        const originalWidth = activeObject.width;
        const originalHeight = activeObject.height;
        const smallestOriginalDimension = Math.min(originalHeight, originalWidth);
        const radius = (smallestOriginalDimension * sizePercentage) / 2;

        if (activeObject.clipPath) {
            activeObject.clipPath = null;
        } else {
            const clipPath = new fabric.Circle({
                radius: radius,
                left: 0,
                top: 0,
                originX: "center",
                originY: "center",
            });
            activeObject.clipPath = clipPath;
        }

        canvas.renderAll();
        return true;
    },

    // Canvas export with DPI handling
    async exportCanvas(format, quality, targetDPI = 200) {
        const screenDPI = 72;
        const maxPixels = 250000000;

        // Make the web fonts canvas-available BEFORE re-measuring/rendering.
        // The 2D canvas only uses a loaded web font after an explicit
        // document.fonts.load() for that exact weight/style — document.fonts.ready
        // and .check() are NOT sufficient. Awaiting the load here makes the
        // export deterministic regardless of how late the font finished loading
        // relative to text being added (fixed fallback glyphs in headless
        // reference runs and guarantees real downloads use Gotham Narrow too).
        if (typeof document !== "undefined" && document.fonts && document.fonts.load) {
            try {
                const fontSpecs = (typeof AppConstants !== "undefined" &&
                    AppConstants.FONTS && AppConstants.FONTS.PRELOAD_FONTS)
                    ? AppConstants.FONTS.PRELOAD_FONTS.map(function (f) {
                        const style = f.style && f.style !== "normal" ? f.style + " " : "";
                        return style + (f.weight || 400) + ' 16px "' + f.family + '"';
                    })
                    : [];
                await Promise.all(fontSpecs.map(function (spec) {
                    return document.fonts.load(spec).catch(function () {});
                }));
            } catch (e) {
                // Non-fatal: fall through to render with whatever is available.
            }
        }

        // Re-measure text with the now-loaded fonts before exporting.
        // Fabric caches a text object's char metrics at creation time; if a
        // web font (Gotham Narrow) finished loading AFTER the text was added, the
        // cached fallback metrics — and thus the exported PNG — stick unless we
        // force a re-measure. This makes the export deterministic regardless of
        // font-load timing (fixes fallback glyphs in headless reference runs).
        canvas.getObjects().forEach(function (obj) {
            if (obj && (obj.type === "text" || obj.type === "i-text" || obj.type === "textbox")) {
                obj.dirty = true;
                if (typeof obj.initDimensions === "function") obj.initDimensions();
                if (typeof obj.setCoords === "function") obj.setCoords();
            }
        });
        canvas.renderAll();

        const baseMultiplier = targetDPI / screenDPI;
        const finalWidth = canvas.width * baseMultiplier;
        const finalHeight = canvas.height * baseMultiplier;
        const totalPixels = finalWidth * finalHeight;

        let actualMultiplier = baseMultiplier;
        let actualDPI = targetDPI;

        if (totalPixels > maxPixels) {
            actualMultiplier = Math.sqrt(maxPixels / (canvas.width * canvas.height));
            actualDPI = Math.round(actualMultiplier * screenDPI);
            console.warn(`Canvas too large for ${targetDPI} DPI. Reduced to ${actualDPI} DPI`);
        }

        try {
            const dataURL = canvas.toDataURL({
                format: format,
                quality: quality,
                multiplier: actualMultiplier,
            });

            if (dataURL === "data:,") {
                throw new Error("Canvas export failed - empty result");
            }

            return { dataURL, actualDPI, targetDPI };
        } catch (error) {
            console.error("Canvas export error:", error);
            throw error;
        }
    }
};

// Make available globally
window.CanvasUtils = CanvasUtils;

// === resources/js/event-handlers.js ===
// Event Handler Utilities - Centralized event handling patterns

const EventHandlerUtils = {
    // Generic event binding utility
    bindHandler(selector, event, handler) {
        jQuery(selector).off(event).on(event, handler);
    },

    // Batch event binding
    bindHandlers(handlerMap) {
        Object.keys(handlerMap).forEach(selector => {
            const handlers = handlerMap[selector];
            Object.keys(handlers).forEach(event => {
                this.bindHandler(selector, event, handlers[event]);
            });
        });
    },

    // Canvas object handlers
    setupCanvasObjectHandlers() {
        const handlers = {
            '#text': {
                'input': function() {
                    setValue("text", jQuery(this).val());
                }
            },
            '#scale': {
                'input': function() {
                    const activeObject = canvas.getActiveObject();
                    if (activeObject && activeObject !== contentImage) {
                        activeObject.scale(parseFloat(this.value)).setCoords();
                        canvas.renderAll();
                        updateScale(activeObject);
                    }
                }
            },
            '#text-color': {
                'change': function() {
                    const activeObject = canvas.getActiveObject();
                    if (activeObject && activeObject.get('type') === "text") {
                        setValue("fill", jQuery(this).find(":selected").attr('value'));
                    }
                }
            },
            'input[name="align"]': {
                'change': function() {
                    const alignValue = jQuery(this).attr('id');
                    setValue("textAlign", alignValue);
                    jQuery(this).parent().trigger('update-status');
                }
            },
            '#stroke-width': {
                'input': function() {
                    const width = jQuery(this).val();
                    setValue("strokeWidth", width == 0 ? null : width);
                }
            },
            '#shadow-depth': {
                'input': function() {
                    setValue("shadow", createShadow('black', jQuery(this).val()));
                }
            },
            '#line-height': {
                'change': function() {
                    const activeObject = canvas.getActiveObject();
                    if (activeObject && activeObject.get('type') === "text") {
                        const lineHeight = parseFloat(jQuery(this).val());
                        setValue("lineHeight", lineHeight);
                    }
                }
            },
            '#font-style-select': {
                'change': function() {
                    const activeObject = canvas.getActiveObject();
                    if (activeObject && activeObject.get('type') === "text") {
                        const optId = jQuery(this).val();
                        const opt = AppConstants.FONTS.OPTIONS.find(o => o.id === optId)
                            || AppConstants.FONTS.OPTIONS[0];
                        // Set weight/style before the family swap so the
                        // FontFaceObserver-gated re-render uses them.
                        activeObject.set("fontWeight", opt.weight);
                        activeObject.set("fontStyle", opt.style);
                        loadFont(opt.family);
                    }
                }
            }
        };

        this.bindHandlers(handlers);
    },

    // Main application handlers
    setupMainAppHandlers() {
        const handlers = {
            '#canvas-template': {
                'change': function() {
                    replaceCanvas();
                    if (typeof updateCanvasDimensions === "function") {
                        setTimeout(() => {
                            updateCanvasDimensions();
                        }, 100);
                    }
                }
            },
            '#logo-selection': {
                'change': function() {
                    // Only add logo if feature is enabled
                    if (LogoState.isLogoEnabled()) {
                        addLogo();
                    }
                }
            },
            '#scale-direction': {
                'change': function() {
                    CanvasUtils.positionBackgroundImage();
                }
            },
            '#remove-element': {
                'click': function() {
                    const activeObject = canvas.getActiveObject();
                    if (activeObject && 
                        activeObject !== contentImage && 
                        activeObject !== logo) {
                        canvas.remove(activeObject);
                    }
                }
            },
            '#bring-to-front': {
                'click': function() {
                    const activeObject = canvas.getActiveObject();
                    if (activeObject && 
                        activeObject !== contentImage && 
                        activeObject !== contentRect) {
                        canvas.bringToFront(activeObject);
                        CanvasUtils.bringLogoToFront();
                        canvas.renderAll();
                    }
                }
            }
        };

        this.bindHandlers(handlers);
    },

    // Text creation handler
    setupTextHandler() {
        this.bindHandler('#add-text', 'click', function() {
            const textValidation = ValidationUtils.validateTextInput(jQuery("#text").val());
            
            if (!textValidation.isValid) {
                showAlert("Error! " + textValidation.error);
                return;
            }

            const initialFontSize = canvas.width / 2;
            const optId = jQuery('#font-style-select').val();
            const opt = AppConstants.FONTS.OPTIONS.find(o => o.id === optId)
                || AppConstants.FONTS.OPTIONS[0];
            const selectedFont = opt.family;

            const text = new fabric.Text(jQuery("#text").val(), {
                fontFamily: selectedFont,
                fontSize: initialFontSize,
                fontWeight: opt.weight,
                charSpacing: AppConstants.FONTS.CHAR_SPACING,
                fontStyle: opt.style,
                textAlign: jQuery('input[name="align"]:checked').val(),
                fill: jQuery("#text-color").find(":selected").attr("value"),
                stroke: "#000000",
                strokeWidth: 0,
                shadow: createShadow("#000000", jQuery("#shadow-depth").val()),
                objectCaching: false,
                lineHeight: parseFloat(jQuery("#line-height").val()) || 1.0,
                centeredScaling: false,
                selectable: true,
                moveable: true,
                hasControls: true,
                hasBorders: true,
                perPixelTargetFind: true,
            });

            canvas.add(text);
            CanvasUtils.relativeScalingControlsOnly(text);
            CanvasUtils.scaleElementToFit(text, 0.8, 0.5);
            canvas.centerObject(text);
            text.set({
                top: canvas.height / 2 - text.getScaledHeight() / 2,
            });

            text.setCoords();
            canvas.setActiveObject(text);
            updateScale(text);
            // Keep the organisation logo on top. Without this a headline
            // placed near the bottom covers it, which the other add handlers
            // already guard against.
            CanvasUtils.bringLogoToFront();
            canvas.renderAll();
        });
    },

    // Image upload handler
    setupImageUploadHandler() {
        this.bindHandler('#add-image', 'input', function() {
            const file = this.files[0];
            if (!file) return;

            const fileValidation = ValidationUtils.isValidImageFile(file);
            jQuery("#add-image").val("");

            if (!fileValidation) {
                showAlert("Error! Invalid Image");
                return;
            }

            const reader = new FileReader();
            reader.onload = function() {
                const image = new Image();
                image.src = reader.result;
                image.onload = function() {
                    fabric.Image.fromURL(reader.result, function(fabricImage) {
                        CanvasUtils.scaleElementToFit(fabricImage, 0.5, 0.4);
                        CanvasUtils.relativeScalingControlsOnly(fabricImage);
                        canvas.add(fabricImage).setActiveObject(fabricImage);
                        canvas.centerObject(fabricImage);
                        updateScale(fabricImage);
                        CanvasUtils.bringLogoToFront();
                    }, {
                        opacity: jQuery("#opacity").val(),
                    });
                };
            };
            reader.readAsDataURL(file);
        });
    },

    // Circle clip handler
    setupCircleClipHandler() {
        this.bindHandler('#add-circle', 'click', function() {
            const activeObject = canvas.getActiveObject();
            const sizeString = jQuery("#circle-radius").val();
            const size = parseInt(sizeString) || 2;

            const validation = ValidationUtils.validateCircleClipTarget(activeObject);
            
            if (!validation.isValid) {
                showAlert(validation.error, validation.type || 'warning');
                return;
            }

            // Map size values to percentages
            const sizePercentageMap = {
                2: 0.9, // Groß - 90%
                3: 0.7, // Mittel - 70%
                4: 0.5  // Klein - 50%
            };
            
            const sizePercentage = sizePercentageMap[size] || 0.7;
            CanvasUtils.applyCircleClip(activeObject, sizePercentage);
        });
    },

    // Pink circle handler
    setupPinkCircleHandler() {
        this.bindHandler('#add-pink-circle', 'click', function() {
            const radius = contentRect.width / 4;
            const pinkCircle = new fabric.Circle({
                top: contentRect.height / 3,
                left: contentRect.width / 3,
                radius: radius,
                fill: "rgb(225,0,120)",
            });
            
            CanvasUtils.scaleElementToFit(pinkCircle, 0.3, 0.3);
            CanvasUtils.relativeScalingControlsOnly(pinkCircle);
            canvas.add(pinkCircle);
            
            // Bring text objects to front
            canvas.getObjects().forEach(object => {
                if (object.get("type") === "text") {
                    canvas.bringToFront(object);
                }
            });

            // ...and the organisation logo above those.
            CanvasUtils.bringLogoToFront();
            canvas.renderAll();
        });
    },

    // Green panel handler.
    //
    // The brand guide requires type to sit on a green surface. Without a
    // photo the whole canvas already is one, so this is only needed over a
    // background image — but there it is not optional: white text laid
    // straight over a photograph breaks the rule, however legible it looks.
    setupPanelHandler() {
        this.bindHandler('#add-panel', 'click', function() {
            const panel = new fabric.Rect({
                width: contentRect.width * 0.8,
                height: contentRect.height * 0.28,
                fill: AppConstants.COLORS.BACKGROUND_SECONDARY,
                objectCaching: false,
            });

            CanvasUtils.relativeScalingControlsOnly(panel);
            canvas.add(panel);
            canvas.centerObject(panel);

            // Behind every text already on the canvas — a panel added after a
            // headline is meant to go under it, not over it.
            canvas.getObjects().forEach(function (object) {
                if (object.get("type") === "text") {
                    canvas.bringToFront(object);
                }
            });
            CanvasUtils.bringLogoToFront();

            canvas.setActiveObject(panel);
            updateScale(panel);
            canvas.renderAll();
        });
    },

    // Cross/check mark handler
    setupCrossHandler() {
        this.bindHandler('#add-cross', 'click', function() {
            fabric.Image.fromURL(
                generatorApplicationURL + "resources/images/Wahlkreuz.png",
                function(image) {
                    CanvasUtils.scaleElementToFit(image, 0.4, 0.3);
                    canvas.add(image);
                    canvas.centerObject(image);
                    canvas.bringToFront(image);
                    // The Wahlkreuz is brought to the very front above, which
                    // would otherwise put it over the organisation logo.
                    CanvasUtils.bringLogoToFront();
                }
            );
        });
    },

    // QR Code handler
    setupQRCodeHandler() {
        this.bindHandler('#add-qr-code', 'click', function() {
            const qrText = jQuery("#qr-text").val().trim();
            const validation = ValidationUtils.validateQRInput(qrText);
            
            if (!validation.isValid) {
                showAlert("Error! " + validation.error);
                return;
            }

            const tempDiv = document.createElement("div");
            tempDiv.style.display = "none";
            document.body.appendChild(tempDiv);

            try {
                const targetQRSize = Math.max(512, Math.min(2048, Math.floor(canvas.width / 4)));
                const qrColor = jQuery("#qr-color").val() || "#000000";
                
                const qr = new QRCode(tempDiv, {
                    text: qrText,
                    width: targetQRSize,
                    height: targetQRSize,
                    colorDark: qrColor,
                    colorLight: "#ffffff",
                    correctLevel: QRCode.CorrectLevel.M,
                });

                setTimeout(function() {
                    const qrImg = tempDiv.querySelector("img");
                    if (qrImg) {
                        const borderPercent = 0.05;
                        const borderSize = Math.floor(targetQRSize * borderPercent);
                        const totalSize = targetQRSize + borderSize * 2;

                        const tempCanvas = document.createElement("canvas");
                        tempCanvas.width = totalSize;
                        tempCanvas.height = totalSize;
                        const ctx = tempCanvas.getContext("2d");

                        ctx.fillStyle = "#ffffff";
                        ctx.fillRect(0, 0, totalSize, totalSize);
                        ctx.drawImage(qrImg, borderSize, borderSize, targetQRSize, targetQRSize);

                        fabric.Image.fromURL(tempCanvas.toDataURL(), function(qrImage) {
                            CanvasUtils.scaleElementToFit(qrImage, 0.25, 0.25);
                            CanvasUtils.relativeScalingControlsOnly(qrImage);
                            canvas.add(qrImage).setActiveObject(qrImage);
                            canvas.centerObject(qrImage);
                            updateScale(qrImage);
                            CanvasUtils.bringLogoToFront();
                            document.body.removeChild(tempDiv);
                        });
                    } else {
                        showAlert("Error generating QR Code image");
                        document.body.removeChild(tempDiv);
                    }
                }, 100);
            } catch (error) {
                showAlert("Error generating QR Code: " + error.message);
                document.body.removeChild(tempDiv);
            }
        });
    },

    // Download handler
    setupDownloadHandler() {
        this.bindHandler('#generate-meme', 'click', async function() {
            const validation = ValidationUtils.validateDownload();

            if (!validation.isValid) {
                alert(validation.error);
                return;
            }

            if (confirm("Hast du das Copyright bei Fotos überprüft und angegeben und das Impressum wo notwendig hinzugefügt?")) {
                const currentTemplate = template_values[template];
                const targetDPI = currentTemplate.dpi || 200;
                const format = jQuery("#image-format").find(":selected").attr("value");
                const quality = parseFloat(jQuery("#image-quality").find(":selected").attr("value"));

                try {
                    const exportResult = await CanvasUtils.exportCanvas(format, quality, targetDPI);

                    const link = document.createElement("a");
                    link.href = exportResult.dataURL;
                    link.download = createImgName();
                    link.click();

                    if (exportResult.actualDPI < exportResult.targetDPI) {
                        alert(`Download erfolgreich mit ${exportResult.actualDPI} DPI (reduziert von ${exportResult.targetDPI} DPI für Kompatibilität)`);
                    }
                } catch (error) {
                    alert("Fehler beim Erstellen des Bildes. Das Format ist möglicherweise zu groß für diesen Browser.");
                }
            }
        });
    },

    // Knockout toggle: transparent region name versus a green one.
    setupLogoKnockoutHandler() {
        this.bindHandler('#logo-knockout-toggle', 'change', function() {
            LogoState.setKnockoutEnabled(jQuery(this).is(':checked'));
            if (LogoState.isLogoEnabled()) {
                addLogo();
            }
        });
    },

    // Logo toggle handler
    setupLogoToggleHandler() {
        this.bindHandler('#logo-toggle', 'change', function() {
            const isChecked = jQuery(this).is(':checked');
            LogoState.setLogoEnabled(isChecked);

            if (isChecked) {
                // Re-add logo
                addLogo();

                // Re-enable logo selection dropdown
                jQuery('#logo-selection').prop('disabled', false).removeClass('opacity-50');
            } else {
                // Remove logo from canvas
                if (logo != null) {
                    canvas.remove(logo);
                    canvas.remove(logoName);
                    logo = null;
                    logoName = null;
                    canvas.renderAll();
                }

                // Disable logo selection dropdown
                jQuery('#logo-selection').prop('disabled', true).addClass('opacity-50');
            }
        });
    },

    // Initialize all handlers
    initializeAllHandlers() {
        this.setupCanvasObjectHandlers();
        this.setupMainAppHandlers();
        this.setupTextHandler();
        this.setupImageUploadHandler();
        this.setupCircleClipHandler();
        this.setupPinkCircleHandler();
        this.setupCrossHandler();
        this.setupPanelHandler();
        this.setupQRCodeHandler();
        this.setupDownloadHandler();
        this.setupLogoToggleHandler();
        this.setupLogoKnockoutHandler();
    }
};

// Make available globally
window.EventHandlerUtils = EventHandlerUtils;

// === resources/js/modal.js ===
// Modal functionality - uses the native <dialog> API
// (the design-system .gat-modal class is applied to a <dialog> element).
jQuery(function() {
    const dialog = document.getElementById('videoModal');
    const iframe = document.getElementById('video-iframe');

    function closeVideoModal() {
        if (!dialog) return;
        if (dialog.open) {
            dialog.close();
        }
        if (iframe) {
            iframe.setAttribute('src', '');
        }
    }

    // Open modal
    jQuery('#video-modal-btn').on('click', function() {
        if (!dialog) return;
        const videoUrl = jQuery(this).data('video');
        if (iframe) {
            iframe.setAttribute('src', videoUrl);
        }
        // showModal() puts the dialog in the top-layer and triggers ::backdrop.
        if (typeof dialog.showModal === 'function') {
            dialog.showModal();
        } else {
            dialog.setAttribute('open', '');
        }
    });

    // Close via the close button
    jQuery('#close-modal').on('click', closeVideoModal);

    // Close when clicking the backdrop (clicks on the <dialog> element itself,
    // not its child content). With native <dialog> the backdrop is the dialog
    // element's own click area outside the content box.
    if (dialog) {
        dialog.addEventListener('click', function(e) {
            if (e.target === dialog) {
                closeVideoModal();
            }
        });

        // Reset iframe when the dialog is closed (Esc, form submit, etc.).
        dialog.addEventListener('close', function() {
            if (iframe) {
                iframe.setAttribute('src', '');
            }
        });

        // The native <dialog> handles Esc automatically; the 'close' listener
        // above will then clear the iframe src.
    }
});

// Font-examples modal: shows the example gallery in-app so the user never
// leaves their in-progress canvas. Same native <dialog> mechanism as the
// video modal; schriften.html stays as a deep-linkable standalone fallback.
jQuery(function() {
    const dialog = document.getElementById('fontExamplesModal');
    if (!dialog) return;

    function openFontExamples() {
        if (typeof dialog.showModal === 'function') {
            dialog.showModal();
        } else {
            dialog.setAttribute('open', '');
        }
    }

    function closeFontExamples() {
        if (dialog.open) {
            dialog.close();
        } else {
            dialog.removeAttribute('open');
        }
    }

    // Every entry point opens the SAME dialog: the picker hint under the font
    // select plus the prominent, always-visible header buttons (desktop +
    // mobile). One handler, no duplicated modal.
    jQuery('#font-examples-btn, #header-examples-btn, #header-examples-btn-mobile').on(
        'click',
        openFontExamples
    );
    jQuery('#close-font-examples').on('click', closeFontExamples);

    // Close when clicking the backdrop (the dialog element itself, outside the
    // content box).
    dialog.addEventListener('click', function(e) {
        if (e.target === dialog) {
            closeFontExamples();
        }
    });
});

// Button group functionality
jQuery(function() {
    jQuery('.btn-group label').on('click', function() {
        const $label = jQuery(this);
        const $group = $label.closest('.btn-group');
        const $radio = $label.find('input[type="radio"]');
        
        // Remove active class from siblings
        $group.find('label').removeClass('active').removeClass('bg-gruene-secondary');
        
        // Add active class to clicked label
        $label.addClass('active').addClass('bg-gruene-secondary');
        
        // Check the radio button and trigger change event
        $radio.prop('checked', true).trigger('change');
        
        console.log('Alignment button clicked:', $radio.attr('id'), 'value:', $radio.val());
    });
});

// Alert functionality now handled by centralized AlertSystem

// === resources/js/searchable-select.js ===
// Searchable Select Component
class SearchableSelect {
    constructor(element, options = {}) {
        this.element = jQuery(element);
        this.options = {
            placeholder: 'Select an option...',
            searchPlaceholder: 'Search...',
            noResultsText: 'No results found',
            ...options
        };
        
        this.isOpen = false;
        this.selectedValue = '';
        this.selectedText = '';
        this.originalOptions = [];
        
        this.init();
    }
    
    init() {
        // Store original options
        this.storeOriginalOptions();
        
        // Create custom select structure
        this.createCustomSelect();
        
        // Hide original select
        this.element.hide();
        
        // Bind events
        this.bindEvents();
        
        // Set initial value
        const initialValue = this.element.val();
        if (initialValue) {
            this.selectOption(initialValue);
        }
    }
    
    storeOriginalOptions() {
        this.originalOptions = [];
        
        this.element.find('optgroup, option').each((index, el) => {
            const $el = jQuery(el);
            
            if (el.tagName === 'OPTGROUP') {
                this.originalOptions.push({
                    type: 'group',
                    label: $el.attr('label'),
                    options: []
                });
            } else {
                const option = {
                    type: 'option',
                    value: $el.attr('value') || '',
                    text: $el.text(),
                    selected: $el.prop('selected')
                };
                
                if (this.originalOptions.length > 0 && this.originalOptions[this.originalOptions.length - 1].type === 'group') {
                    this.originalOptions[this.originalOptions.length - 1].options.push(option);
                } else {
                    this.originalOptions.push(option);
                }
            }
        });
    }
    
    createCustomSelect() {
        const customSelect = `
            <div class="app-searchable-select">
                <button type="button" class="app-searchable-select-button" aria-expanded="false">
                    <span class="app-searchable-select-text">${this.options.placeholder}</span>
                    <span class="app-searchable-select-chevron">
                        <svg class="h-5 w-5 text-gray-400 transform transition-transform" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                        </svg>
                    </span>
                </button>
                <div class="app-searchable-select-dropdown hidden">
                    <input type="text" class="app-searchable-select-search" placeholder="${this.options.searchPlaceholder}">
                    <div class="app-searchable-select-options">
                        ${this.renderOptions()}
                    </div>
                </div>
            </div>
        `;
        
        this.customSelect = jQuery(customSelect);
        this.element.after(this.customSelect);
        
        // Cache elements
        this.button = this.customSelect.find('.app-searchable-select-button');
        this.dropdown = this.customSelect.find('.app-searchable-select-dropdown');
        this.searchInput = this.customSelect.find('.app-searchable-select-search');
        this.optionsContainer = this.customSelect.find('.app-searchable-select-options');
        this.textElement = this.customSelect.find('.app-searchable-select-text');
    }
    
    renderOptions(searchTerm = '') {
        let html = '';
        
        this.originalOptions.forEach(item => {
            if (item.type === 'group') {
                const groupOptions = item.options.filter(opt => {
                    // Search in both display text and original value (which may contain %)
                    const searchLower = searchTerm.toLowerCase();
                    const textMatch = opt.text.toLowerCase().includes(searchLower);
                    const valueMatch = opt.value.toLowerCase().includes(searchLower);
                    // Also search in value with % replaced by space for better matching
                    const valueWithSpace = opt.value.replace(/%/g, ' ').toLowerCase();
                    const valueSpaceMatch = valueWithSpace.includes(searchLower);
                    return textMatch || valueMatch || valueSpaceMatch;
                });
                
                if (groupOptions.length > 0) {
                    html += `<div class="app-searchable-select-option-group">${item.label}</div>`;
                    groupOptions.forEach(option => {
                        const selectedClass = option.value === this.selectedValue ? 'selected' : '';
                        html += `<div class="app-searchable-select-option ${selectedClass}" data-value="${option.value}">${option.text}</div>`;
                    });
                }
            } else if (item.type === 'option') {
                // Search in both display text and original value (which may contain %)
                const searchLower = searchTerm.toLowerCase();
                const textMatch = item.text.toLowerCase().includes(searchLower);
                const valueMatch = item.value.toLowerCase().includes(searchLower);
                // Also search in value with % replaced by space for better matching
                const valueWithSpace = item.value.replace(/%/g, ' ').toLowerCase();
                const valueSpaceMatch = valueWithSpace.includes(searchLower);
                
                if (textMatch || valueMatch || valueSpaceMatch) {
                    const selectedClass = item.value === this.selectedValue ? 'selected' : '';
                    html += `<div class="app-searchable-select-option ${selectedClass}" data-value="${item.value}">${item.text}</div>`;
                }
            }
        });
        
        if (html === '' && searchTerm !== '') {
            html = `<div class="px-3 py-2 text-gray-500 text-sm">${this.options.noResultsText}</div>`;
        }
        
        return html;
    }
    
    bindEvents() {
        // Toggle dropdown
        this.button.on('click', (e) => {
            e.preventDefault();
            this.toggle();
        });
        
        // Close on outside click
        jQuery(document).on('click', (e) => {
            if (!this.customSelect.is(e.target) && this.customSelect.has(e.target).length === 0) {
                this.close();
            }
        });
        
        // Search functionality
        this.searchInput.on('input', (e) => {
            const searchTerm = e.target.value;
            this.optionsContainer.html(this.renderOptions(searchTerm));
            this.bindOptionEvents();
        });
        
        // Keyboard navigation
        this.searchInput.on('keydown', (e) => {
            if (e.key === 'Escape') {
                this.close();
            }
        });
        
        // Initial option events
        this.bindOptionEvents();
    }
    
    bindOptionEvents() {
        this.optionsContainer.find('.app-searchable-select-option').off('click').on('click', (e) => {
            const value = jQuery(e.target).data('value');
            const text = jQuery(e.target).text();
            
            this.selectOption(value, text);
            this.close();
        });
    }
    
    selectOption(value, text = null) {
        this.selectedValue = value;
        
        if (text === null) {
            // Find text from original options
            const findOption = (items) => {
                for (let item of items) {
                    if (item.type === 'group') {
                        const found = findOption(item.options);
                        if (found) return found;
                    } else if (item.value === value) {
                        return item;
                    }
                }
                return null;
            };
            
            const option = findOption(this.originalOptions);
            text = option ? option.text : value;
        }
        
        this.selectedText = text;
        this.textElement.text(text || this.options.placeholder);
        
        // Update original select
        this.element.val(value);
        
        // Trigger change event
        this.element.trigger('change');
        
        // Update option states
        this.updateOptionStates();
    }
    
    updateOptionStates() {
        this.optionsContainer.find('.app-searchable-select-option').removeClass('selected');
        this.optionsContainer.find(`[data-value="${this.selectedValue}"]`).addClass('selected');
    }
    
    toggle() {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }
    
    open() {
        this.dropdown.removeClass('hidden');
        this.button.attr('aria-expanded', 'true');
        this.customSelect.find('.app-searchable-select-chevron svg').addClass('rotate-180');
        this.isOpen = true;
        
        // Focus search input
        setTimeout(() => {
            this.searchInput.focus();
        }, 10);
        
        // Clear search
        this.searchInput.val('');
        this.optionsContainer.html(this.renderOptions());
        this.bindOptionEvents();
    }
    
    close() {
        this.dropdown.addClass('hidden');
        this.button.attr('aria-expanded', 'false');
        this.customSelect.find('.app-searchable-select-chevron svg').removeClass('rotate-180');
        this.isOpen = false;
    }
    
    // Method to refresh options (useful when options are added dynamically)
    refresh() {
        this.storeOriginalOptions();
        this.optionsContainer.html(this.renderOptions());
        this.bindOptionEvents();
    }
    
    // Method to add options programmatically
    addOption(value, text, groupLabel = null) {
        const option = jQuery(`<option value="${value}">${text}</option>`);
        
        if (groupLabel) {
            let optgroup = this.element.find(`optgroup[label="${groupLabel}"]`);
            if (optgroup.length === 0) {
                optgroup = jQuery(`<optgroup label="${groupLabel}"></optgroup>`);
                this.element.append(optgroup);
            }
            optgroup.append(option);
        } else {
            this.element.append(option);
        }
        
        this.refresh();
    }
    
    // Method to clear all options
    clearOptions() {
        this.element.empty().append('<option value="">Select an option...</option>');
        this.selectedValue = '';
        this.selectedText = '';
        this.textElement.text(this.options.placeholder);
        this.refresh();
    }
}

// jQuery plugin wrapper
jQuery.fn.searchableSelect = function(options) {
    return this.each(function() {
        if (!jQuery(this).data('searchable-select')) {
            jQuery(this).data('searchable-select', new SearchableSelect(this, options));
        }
    });
};

// Auto-initialize searchable selects
jQuery(function() {
    // Initialize logo selection as searchable
    jQuery('#logo-selection').searchableSelect({
        placeholder: 'Logo Auswählen',
        searchPlaceholder: 'Logo suchen...',
        noResultsText: 'Keine Logos gefunden'
    });
});

// === resources/js/initialization.js ===
// Initialize UI components
jQuery(function() {
    // Wait for fabric to be available before initializing canvas-dependent code
    function waitForFabric() {
        if (typeof fabric !== 'undefined') {
            console.log('Fabric.js loaded, initializing application...');
            
            // Initialize the application now that fabric is available
            if (typeof initializeApplication === 'function') {
                initializeApplication();
            }
            
            console.log('UI components initialized with Tailwind CSS');
        } else {
            console.log('Waiting for Fabric.js to load...');
            setTimeout(waitForFabric, 50);
        }
    }
    
    waitForFabric();
});

// === resources/js/helpers.js ===
// Legacy constants reference - use AppConstants instead

// Canvas utilities
function setValue(key, value) {
    const activeObject = canvas.getActiveObject();
    console.log('setValue called:', key, '=', value, 'on object:', activeObject);
    
    if (activeObject) {
        activeObject.set(key, value);
        canvas.renderAll();
        console.log('Value set successfully. New value:', activeObject.get(key));
    } else {
        console.log('No active object to set value on');
    }
}

function getBackgroundColor(color) {
    return jQuery('#bg-option').hasClass('active') ? color : '';
}

function setBackgroundColor(color) {
    setValue("textBackgroundColor", getBackgroundColor(color));
}

function createShadow(color, width) {
    return `${color} 2px 2px ${width}`;
}

// UI utilities
// refreshSelectPicker function removed - no longer needed with Tailwind CSS

function toggleTextMethods(enable) {
    const $textMethods = jQuery('.text-method');
    const $alignButtons = jQuery('.align');
    
    if (enable) {
        $textMethods.attr('disabled', false);
        $alignButtons.removeClass('disabled');
    } else {
        $textMethods.attr('disabled', 'disabled');
        $alignButtons.addClass('disabled');
    }
}

function disableTextMethods() {
    toggleTextMethods(false);
}

function enableTextMethods() {
    toggleTextMethods(true);
}

// Validation utilities - using centralized ValidationUtils
function isImage(fileType) {
    return ValidationUtils.isValidImage(fileType);
}

// File utilities
function createImgName() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    
    for (let i = 0; i < AppConstants.FILE.FILENAME_LENGTH; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    
    const format = jQuery('#image-format').find(":selected").attr('value');
    return `${result}.${format}`;
}

// Alert utilities now handled by AlertSystem - see alert-system.js

// === resources/js/choice-image.js ===
jQuery(function () {
    // Event: Choice meme from gallery
    jQuery('.app-memes-container').delegate('img', 'click', function () {
        var $img = jQuery(this);
        var imgInfo = {
            url: $img.attr('src'),
            height: $img.attr('img-height'),
            width: $img.attr('img-width'),
        };
        processMeme(imgInfo);
    });

    // Read a File object, validate it as an image, and feed it through processMeme.
    // Shared between the <input type="file"> change handler and drag-and-drop on
    // the .gat-dropzone label.
    function loadMemeFile(file) {
        if (!file) return;

        if (!ValidationUtils.isValidImageFile(file)) {
            showAlert('Error! Invalid Image');
            return;
        }

        const reader = new FileReader();
        reader.onload = function () {
            var meme = new Image();
            meme.onload = function () {
                processMeme({
                    url: reader.result,
                    height: meme.height,
                    width: meme.width,
                });
            };
            meme.onerror = function () {
                showAlert('Error loading image');
            };
            meme.src = reader.result;
        };
        reader.onerror = function () {
            showAlert('Error reading file');
        };
        reader.readAsDataURL(file);
    }

    // Event: Upload local image via file input
    jQuery('#meme-input').on('change', function () {
        const file = this.files[0];
        jQuery('#meme-input').val(''); // Reset file input
        loadMemeFile(file);
    });

    // Drag-and-drop on the .gat-dropzone label. The label already forwards
    // clicks to #meme-input (native for=…); we add drag-over visual feedback
    // and dropped-file handling on top.
    const dropzone = document.getElementById('meme-dropzone');
    if (dropzone) {
        ['dragenter', 'dragover'].forEach(function (evt) {
            dropzone.addEventListener(evt, function (e) {
                e.preventDefault();
                e.stopPropagation();
                dropzone.classList.add('is-dragover');
            });
        });
        ['dragleave', 'dragend', 'drop'].forEach(function (evt) {
            dropzone.addEventListener(evt, function (e) {
                e.preventDefault();
                e.stopPropagation();
                dropzone.classList.remove('is-dragover');
            });
        });
        dropzone.addEventListener('drop', function (e) {
            const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
            loadMemeFile(file);
        });
    }
});


// === resources/js/handlers.js ===
// Font management
function loadFont(font) {
    const customFonts = ['Gotham Narrow', 'Vollkorn'];
    const text = canvas.getActiveObject();
    
    if (!text) return;
    
    if (customFonts.includes(font)) {
        const fontObserver = new FontFaceObserver(font);
        fontObserver.load().then(function () {
            text.set("fontFamily", "");
            text.set("fontFamily", font);
            canvas.renderAll();
        });
    } else {
        text.set("fontFamily", font);
        canvas.renderAll();
    }
}

// UI state management
function updateInputs() {
    const activeObject = canvas.getActiveObject();

    if (activeObject && activeObject.get('type') === "text") {
        enableTextMethods();
        jQuery('#text').val(activeObject.text);
        jQuery('#text-color').val(activeObject.fill);
        jQuery('#line-height').val(activeObject.lineHeight);
        jQuery(`input[value="${activeObject.textAlign}"]`).parent().trigger('update-status');
        updateScale(activeObject);
    }
}

function updateScale(activeObject) {
    const scale = Number(parseFloat(activeObject.scaleX)).toFixed(2);
    jQuery('#scale').val(scale);
}

// Event handler utility - using centralized EventHandlerUtils
function bindHandler(selector, event, handler) {
    return EventHandlerUtils.bindHandler(selector, event, handler);
}

// Section toggle functionality
function toggleSection(sectionId) {
    const section = document.getElementById(sectionId);
    const chevron = document.getElementById(sectionId + '-chevron');
    const button = document.querySelector(`[onclick="toggleSection('${sectionId}')"]`);
    
    if (section.style.display === 'none' || section.classList.contains('hidden')) {
        // Show section
        section.style.display = 'block';
        section.classList.remove('hidden');
        chevron.style.transform = 'rotate(0deg)';
        button.setAttribute('aria-expanded', 'true');
    } else {
        // Hide section
        section.style.display = 'none';
        section.classList.add('hidden');
        chevron.style.transform = 'rotate(-90deg)';
        button.setAttribute('aria-expanded', 'false');
    }
}

// Canvas object event handlers - now managed by EventHandlerUtils
function loadObjectHandlers() {
    EventHandlerUtils.setupCanvasObjectHandlers();
}

/*****************************
 * Handle edit buttons style *
*****************************/

// Update style of font-style buttons
jQuery('.edit-btn').on('update-status', function () {
    if (jQuery(this).attr('data') == '') {
        jQuery(this).removeClass('active')
    } else {
        jQuery(this).addClass('active')
    }
})

//  Toggle font-style buttons
jQuery('#font-style .btn').on('click', function () {
    if (jQuery(this).attr('data') == '') {
        jQuery(this).addClass('active')
        jQuery(this).attr('data', jQuery(this).attr('value'))
    } else {
        jQuery(this).removeClass('active')
        jQuery(this).attr('data', '')
    }
    jQuery(this).trigger('change-value')
})

// Update style of text align buttons
jQuery('.align').on('update-status', function () {
    jQuery('.align').removeClass('active')
    jQuery(this).addClass('active')
})

// Expose functions to global scope for inline event handlers
window.toggleSection = toggleSection;

// === resources/js/main.js ===
// Application state
let canvas;
let contentRect;
let contentImage;
let logo;
let logoName;
let logoText;
// Set once the first background image has flipped the knockout off, so a
// later deliberate re-enable by the user survives further uploads.
let knockoutAutoDisabled = false;
let scaleMax;
let template;

// Application URL fallback - must be preserved for external scripts
if (typeof generatorApplicationURL === "undefined") {
  var generatorApplicationURL = "";
}

// Explicitly expose generatorApplicationURL to prevent minification
window.generatorApplicationURL = generatorApplicationURL;

function replaceCanvas() {
  template = jQuery("#canvas-template").find(":selected").attr("value");

  // Cleanup existing canvas
  if (canvas) {
    canvas.dispose();
  }

  const currentTemplate = TemplateConstants.getTemplate(template);
  const { width, height, topBorderMultiplier, border } = currentTemplate;

  // Setup responsive canvas container
  function resizeCanvas() {
    const wrapperWidth = jQuery(".app-canvas-wrapper").width();
    const $container = jQuery(".canvas-container");
    $container.css({
      width: wrapperWidth,
      height: (wrapperWidth * height) / width,
    });
  }

  jQuery(window).off("resize.canvas").on("resize.canvas", resizeCanvas);

  // Initialize fabric canvas
  canvas = new fabric.Canvas("meme-canvas", {
    width,
    height,
    selection: true,
    allowTouchScrolling: true,
    backgroundColor: AppConstants.COLORS.BACKGROUND_PRIMARY,
    preserveObjectStacking: true,
    targetFindTolerance: AppConstants.CANVAS.TARGET_FIND_TOLERANCE,
    perPixelTargetFind: true,
  });

  // Setup scale limits
  scaleMax = canvas.width * AppConstants.CANVAS.SCALE_MAX_MULTIPLIER;
  jQuery("#scale").attr("max", scaleMax);

  // Calculate border distances
  // For borderless templates (border: 0), borderDistance should be 0
  const borderDistance = border > 0 ? canvas.width / border : 0;
  const topDistance = borderDistance * topBorderMultiplier;

  // Create content rectangle
  contentRect = new fabric.Rect({
    top: topDistance,
    left: borderDistance,
    width: canvas.width - borderDistance * 2,
    height: canvas.height - (topDistance + borderDistance),
    fill: AppConstants.COLORS.BACKGROUND_SECONDARY,
    selectable: false,
  });

  canvas.add(contentRect);

  // Initialize canvas features
  console.log('[replaceCanvas] Initializing canvas features with canvas:', canvas ? 'defined' : 'undefined');
  resizeCanvas();
  CanvasUtils.enableSnap(canvas);
  CanvasUtils.enableRotationSnap(canvas);
  CanvasUtils.enablePictureMove(canvas);
  enableScalingUpdates();

  // Only add logo if enabled
  if (LogoState.isLogoEnabled()) {
    addLogo();
  }

  canvas.renderAll();

  // Keep the globally exposed reference pointing at the live canvas. Without
  // this, window.canvas only ever tracked the first canvas built during setup;
  // every later template switch replaced the internal canvas but left the
  // global stale, so tooling and tests reading window.canvas saw a disposed,
  // empty instance after any non-default template was selected.
  window.canvas = canvas;
  window.contentRect = contentRect;

  // Update dimensions display if the function exists (from wizard.js)
  if (typeof updateCanvasDimensions === "function") {
    setTimeout(() => {
      updateCanvasDimensions();
    }, 50);
  }
}

/**
 * Calculate the optimal logo top position based on template configuration.
 * All templates are borderless (border: 0).
 * Logo bottom margin is BORDERLESS_MARGIN_PERCENT of canvas height.
 */
function calculateLogoTop(logoHeight, template) {
  // Validate inputs
  if (!canvas || !template) {
    console.error('calculateLogoTop: canvas or template is not defined');
    return 0;
  }

  if (typeof logoHeight !== 'number' || logoHeight <= 0) {
    console.error('calculateLogoTop: invalid logoHeight', logoHeight);
    return 0;
  }

  const marginFromBottom = canvas.height * AppConstants.LOGO.BORDERLESS_MARGIN_PERCENT;
  const logoBottom = canvas.height - marginFromBottom;
  const logoTop = logoBottom - logoHeight;
  return logoTop;
}

function addLogo() {
  // If logo is disabled, remove existing logo and return early
  if (!LogoState.isLogoEnabled()) {
    if (logo != null) {
      canvas.remove(logo);
      canvas.remove(logoName);
      logo = null;
      logoName = null;
      canvas.renderAll();
    }
    return;
  }

  if (logo != null) {
    canvas.remove(logo);
    canvas.remove(logoName);
  }

  const template = currentTemplate();
  const scaleTo = template.logoWidth;
  logoText = (jQuery("#logo-selection").find(":selected").attr("value") || "")
    .trim()
    .toUpperCase();

  let logoFilename, textScaleTo, isLongLogo;

  // Same decision as for the region name: with the knockout the artwork
  // relies on green lying behind it, which only holds on the plain canvas.
  // Over a photo the filled variant is the one that reads.
  const useFilled = !LogoState.isKnockoutEnabled();

  // Check if text needs breaking (either has % or is too long)
  if (logoText.includes("%") || logoText.length > AppConstants.LOGO.MAX_TEXT_LENGTH) {
    logoFilename = useFilled
      ? AppConstants.LOGO.FILES.LONG_FILLED
      : AppConstants.LOGO.FILES.LONG;
    isLongLogo = true;

    if (logoText.includes("%")) {
      // Split by % and trim each part to remove whitespace
      const parts = logoText.split("%").map(part => part.trim());
      logoText = parts.join("\n");
    } else if (logoText.length > AppConstants.LOGO.MAX_TEXT_LENGTH) {
      // For long names without %, break at last space
      const lastSpace = logoText.lastIndexOf(" ");
      if (lastSpace > 0) {
        logoText = logoText.substring(0, lastSpace).trim() + "\n" +
                   logoText.substring(lastSpace).trim();
      }
    }

    textScaleTo = AppConstants.LOGO.TEXT_SCALE_LONG;
  } else {
    logoFilename = useFilled
      ? AppConstants.LOGO.FILES.SHORT_FILLED
      : AppConstants.LOGO.FILES.SHORT;
    isLongLogo = false;
    textScaleTo = AppConstants.LOGO.TEXT_SCALE_SHORT;
  }

  fabric.Image.fromURL(
    generatorApplicationURL + "resources/images/logos/" + logoFilename,
    function (image) {
      // Error handling: Check if image loaded successfully
      if (!image || !image.width || !image.height) {
        console.error('Failed to load logo image:', logoFilename);
        return;
      }

      try {
        image.scaleToWidth(scaleTo);
        image.lockMovementX = true;
        image.lockMovementY = true;

        // Calculate optimal logo position automatically based on template type
        const logoHeight = image.getScaledHeight();
        image.top = calculateLogoTop(logoHeight, template);

        CanvasUtils.disableScalingControls(image);
        image.selectable = false;
        canvas.add(image);
        canvas.centerObjectH(image);
        canvas.bringToFront(image);

        // Calculate text position relative to logo top
        // Using logo WIDTH as reference since it's constant between short and long logos
        const barOffset = isLongLogo ? AppConstants.LOGO.BAR_OFFSET_FROM_TOP_LONG : AppConstants.LOGO.BAR_OFFSET_FROM_TOP;
        const offsetFromTop = image.getScaledWidth() * barOffset;
        const textTopPosition = image.top + offsetFromTop;

        // The region name is a transparent KNOCKOUT cut out of the white bar,
        // NOT solid green fill. The text uses 'destination-out' compositing so it
        // erases the bar pixels of the (white) logo. Because the text and the logo
        // image live in a single cached fabric.Group, that erase happens inside the
        // group's own offscreen cache: the letters become genuinely transparent in
        // the cache, and whatever is BEHIND the logo (the green canvas or an
        // uploaded background photo) shows through them once the group is composited
        // onto the canvas. A plain destination-out on a loose text object would
        // instead punch a hole straight through to nothing; isolating it in the
        // group is what makes it a true knockout limited to the bar.
        // Knockout or green fill. The knockout erases the letters out of the
        // white bar, so whatever lies BEHIND the logo shows through them. On
        // the plain green canvas that is the green — exactly right. Over a
        // background photo it is wrong: the photo shows through the letters
        // and the region name becomes unreadable. In that case the name is
        // filled with the brand green instead, which looks identical on the
        // plain canvas and stays legible over any photo.
        const knockout = LogoState.isKnockoutEnabled();
        logoName = new fabric.Text(logoText, {
          top: textTopPosition,
          fontFamily: AppConstants.FONTS.DEFAULT_LOGO,
          fontSize: Math.floor(image.getScaledWidth() / 10),
          fontWeight: AppConstants.FONTS.WEIGHT_LOGO,
          charSpacing: AppConstants.FONTS.CHAR_SPACING,
          fontStyle: "normal",
          textAlign: "right",
          // Under destination-out only the glyph alpha matters, so the colour
          // is immaterial there; an opaque white keeps the erase crisp. With a
          // normal composite the colour IS the result.
          // Not a second constant that happens to match: this IS the fill of
          // the canvas rectangle, i.e. exactly what the knockout would have
          // shown through on a plain background. If that colour ever changes,
          // the filled name follows it.
          fill: knockout
            ? AppConstants.COLORS.LOGO_KNOCKOUT
            : AppConstants.COLORS.BACKGROUND_SECONDARY,
          stroke: AppConstants.COLORS.TEXT_STROKE,
          strokeWidth: 0,
          objectCaching: false,
          lineHeight: AppConstants.LOGO.LINE_HEIGHT,
          angle: AppConstants.LOGO.ANGLE,
          selectable: false,
          globalCompositeOperation: knockout ? "destination-out" : "source-over",
        });

        // Add the text to the canvas only to size/position it with the exact same
        // logic as before; it is removed again before grouping so the knockout is
        // not double-rendered.
        canvas.add(logoName);

        // Fit the region name to the bar by ACTUAL rendered width, not by a
        // character-count heuristic. Some names that stay on one line (e.g.
        // "OBERHOFEN/IRRSEE", or any name without a space to break on) are still
        // wider than the bar; the old length check left them unscaled so the
        // right-aligned text overflowed and clipped the leading letter. Whenever
        // the text is wider than the available bar width we scale it down to fit;
        // otherwise we keep its natural size and just set the box width so the
        // right-alignment/centering stays unchanged for names that already fit.
        const availableWidth = image.getScaledWidth() * AppConstants.LOGO.WIDTH_SCALE;
        if (logoName.getScaledWidth() > availableWidth) {
          logoName.scaleToWidth(availableWidth);
          const topAdd = Math.floor((logoName.height - logoName.getScaledHeight()) / 2);
          logoName.top = logoName.top + topAdd;
        } else {
          logoName.width = availableWidth;
        }

        canvas.centerObjectH(logoName);

        // Combine the white logo image and the knockout text into one cached group.
        // Fabric recomputes each child's coordinates relative to the group, so the
        // absolute positioning computed above is preserved. objectCaching:true makes
        // the group render to its own cache, where the destination-out text cuts the
        // letters out of the white bar before the group is drawn to the canvas.
        canvas.remove(image);
        canvas.remove(logoName);

        const knockoutGroup = new fabric.Group([image, logoName], {
          selectable: false,
          objectCaching: true,
          lockMovementX: true,
          lockMovementY: true,
        });
        canvas.centerObjectH(knockoutGroup);

        // FLATTEN the destination-out group into a single PNG with real
        // transparent letter-holes, then place THAT plain image on the canvas.
        // Rationale: a live destination-out object renders fine in the editor but
        // the high-DPI download (canvas.toDataURL({multiplier})) re-renders it and
        // leaks the erase to solid BLACK. A plain image with baked alpha holes
        // scales cleanly at any export multiplier — the background (green canvas or
        // photo) shows through the holes both on screen and in the download.
        // Bake at the logo's native resolution so the upscaled export stays crisp.
        const gx = knockoutGroup.left;
        const gy = knockoutGroup.top;
        const gw = knockoutGroup.getScaledWidth();
        const nativeMultiplier = Math.max(1, (image.width || gw) / gw);
        const flatUrl = knockoutGroup.toDataURL({
          format: "png",
          multiplier: nativeMultiplier,
          enableRetinaScaling: false,
        });

        fabric.Image.fromURL(flatUrl, function (flat) {
          flat.set({ left: gx, top: gy });
          flat.scaleToWidth(gw);
          flat.selectable = false;
          flat.lockMovementX = true;
          flat.lockMovementY = true;
          CanvasUtils.disableScalingControls(flat);
          logo = flat;
          logoName = null;
          canvas.add(flat);
          CanvasUtils.bringLogoToFront();
          canvas.renderAll();
        });
      } catch (error) {
        console.error('Error while adding logo to canvas:', error);
      }
    },
    null, // crossOrigin
    {
      // Error callback for image loading failure
      onError: function() {
        console.error('Failed to load logo image file:', logoFilename);
      }
    }
  );
}

function logo_to_front() {
  CanvasUtils.bringLogoToFront();
}

function enableScalingUpdates() {
  canvas.on("object:scaling", function (options) {
    console.log("isScaling");
    updateScale(options.target);
  });
}

function enablePictureMove() {
  CanvasUtils.enablePictureMove(canvas);
}

function disableScalingControls(object) {
  CanvasUtils.disableScalingControls(object);
}

function relativeScalingControlsOnly(object) {
  CanvasUtils.relativeScalingControlsOnly(object);
}

function enableSnap() {
  CanvasUtils.enableSnap(canvas);
}

// Application initialization function - called after fabric is ready
function initializeApplication() {
  // Initialize canvas first
  replaceCanvas();
  
  // Initialize fabric object defaults
  fabric.Object.prototype.set({
    transparentCorners: false,
    cornerColor: AppConstants.COLORS.CORNER_COLOR,
    borderColor: AppConstants.COLORS.BORDER_COLOR,
    cornerSize: parseInt(canvas.width) * AppConstants.CANVAS.CORNER_SIZE_MULTIPLIER,
    cornerStrokeColor: AppConstants.COLORS.CORNER_STROKE,
    borderScaleFactor: AppConstants.CANVAS.BORDER_SCALE_FACTOR,
    padding: AppConstants.CANVAS.PADDING,
  });

  // Initialize all event handlers
  EventHandlerUtils.initializeAllHandlers();

  // Update edit methods values to the selected canvas text
  canvas.on({
    "selection:created": updateInputs,
    "selection:updated": updateInputs,
    "selection:cleared": enableTextMethods,
  });

  // Expose canvas and other functions to global scope for testing
  window.canvas = canvas;
  window.processMeme = processMeme;
  window.contentImage = contentImage;
  window.contentRect = contentRect;
  window.logo = logo;
  window.logoName = logoName;
}

function processMeme(memeInfo) {
  // Add meme template as canvas background
  fabric.Image.fromURL(
    `${memeInfo.url}`,
    function (meme) {
      if (contentImage != null) {
        canvas.remove(contentImage);
      }
      contentImage = meme;
      // Update global reference for testing
      window.contentImage = contentImage;
      positionBackgroundImage();

      // A transparent region name shows the background THROUGH the letters.
      // On the plain green canvas that is the point; over a photo it makes
      // the name unreadable. Switch to the filled name the first time a
      // background arrives — and only then, so a deliberate choice by the
      // user is not overruled on every later image.
      if (LogoState.isKnockoutEnabled() && !knockoutAutoDisabled) {
        knockoutAutoDisabled = true;
        LogoState.setKnockoutEnabled(false);
        jQuery("#logo-knockout-toggle").prop("checked", false);
        if (LogoState.isLogoEnabled()) {
          addLogo();
        }
        if (typeof showAlert === "function") {
          showAlert(
            "Hinweis: Der Ortsname im Logo ist jetzt grün gefüllt statt durchsichtig — " +
            "über einem Foto wäre er sonst nicht lesbar. Umschaltbar in Schritt 1.",
            "info"
          );
        }
      }
    },
    {
      crossOrigin: "anonymous",
    }
  );
}

function positionBackgroundImage() {
  CanvasUtils.positionBackgroundImage();
}

function generateLogoSelection(data) {
  const $logoSelect = jQuery("#logo-selection");

  jQuery.each(data, function (index, names) {
    var items = [];
    jQuery.each(names.sort(), function (index, name) {
      items.push(
        '<option value="' +
          name.toUpperCase() +
          '">' +
          name.replace("%", " ").toUpperCase() +
          "</option>"
      );
    });
    $logoSelect.append(
      '<optgroup label="' + index + '">' + items.join("") + "</optgroup>"
    );
  });

  // Refresh the searchable select component
  const searchableSelect = $logoSelect.data("searchable-select");
  if (searchableSelect) {
    searchableSelect.refresh();
  }
}

function loadLogoSelection() {
  // Check for embedded logo data first (production mode)
  if (typeof window.EMBEDDED_LOGO_DATA !== "undefined") {
    console.log("Using embedded logo data for faster loading");
    generateLogoSelection(window.EMBEDDED_LOGO_DATA);
    return;
  }

  // Fallback to AJAX loading (development mode)
  console.log("Loading logo data via AJAX request");
  const defaultIndex =
    generatorApplicationURL + "resources/images/logos/index.json";

  if (typeof logoDataOverride !== "undefined") {
    if (ValidationUtils.isValidJSON(logoDataOverride)) {
      generateLogoSelection(jQuery.parseJSON(logoDataOverride));
      return;
    }
  }

  let logoIndex;
  if (typeof logoIndexOverride !== "undefined") {
    logoIndex = logoIndexOverride;
  } else {
    logoIndex = defaultIndex;
  }

  jQuery
    .getJSON(logoIndex, function (data) {
      generateLogoSelection(data);
    })
    .fail(function (jqxhr, textStatus, error) {
      console.error("Failed to load logo data:", textStatus, error);
      showAlert(
        "Fehler beim Laden der Logo-Daten. Bitte Seite neu laden.",
        "warning"
      );
    });
}

loadLogoSelection();

function autoPlayYouTubeModal() {
  var trigger = jQuery("body").find('[data-toggle="modal"]');
  trigger.click(function () {
    var theModal = jQuery(this).data("target"),
      videoSRC = jQuery(this).attr("data-theVideo"),
      videoSRCauto = videoSRC + "?autoplay=1";
    jQuery(theModal + " iframe").attr("src", videoSRCauto);
    jQuery(theModal + " button.close").click(function () {
      jQuery(theModal + " iframe").attr("src", videoSRC);
    });
  });
}
jQuery(document).ready(function () {
  autoPlayYouTubeModal();
});


// === resources/js/wizard.js ===
// Wizard Navigation and UI Enhancement
let currentStep = 1;
const totalSteps = 4;

// Initialize wizard functionality
function initializeWizard() {
    // Ensure we always start from step 1
    currentStep = 1;
    
    // Preload fonts to ensure they're available when needed
    preloadFonts();
    
    setupStepNavigation();
    setupAdvancedOptions();
    setupQRSection();
    updateCanvasDimensions();
    goToStep(1);
}

// Preload custom fonts.
//
// Uses the native FontFaceSet API (document.fonts.load) rather than
// FontFaceObserver: a font that FontFaceObserver has "loaded" (and that
// document.fonts.check() reports as available) is still NOT usable by the 2D
// canvas until it has been document.fonts.load()-ed for that exact
// weight/style. Fabric then keeps rendering the fallback — which silently
// poisoned the visual-regression baselines (fallback glyphs/metrics) in
// headless runs. Loading each preload spec explicitly makes the web font
// canvas-available; once all have settled we re-measure any text already on
// the canvas so it switches from the fallback to the real font.
function preloadFonts() {
    const specs = AppConstants.FONTS.PRELOAD_FONTS.map(function (f) {
        const style = f.style && f.style !== 'normal' ? f.style + ' ' : '';
        return style + (f.weight || 400) + ' 16px "' + f.family + '"';
    });

    if (!(document.fonts && document.fonts.load)) {
        return; // very old browser: nothing to force, CSS swap still applies
    }

    Promise.all(specs.map(function (spec) {
        return document.fonts.load(spec).catch(function () {
            console.log('Font failed to load:', spec);
        });
    })).then(function () {
        // The web fonts are now canvas-available. Re-measure any text already
        // placed so it picks up the real font's metrics instead of the
        // fallback it may have been created with.
        if (typeof canvas !== 'undefined' && canvas && canvas.getObjects) {
            canvas.getObjects().forEach(function (o) {
                if (o && (o.type === 'text' || o.type === 'i-text' || o.type === 'textbox')) {
                    o.dirty = true;
                    if (typeof o.initDimensions === 'function') o.initDimensions();
                    if (typeof o.setCoords === 'function') o.setCoords();
                }
            });
            canvas.renderAll();
        }
    });
}

// Step Navigation
function setupStepNavigation() {
    // Step indicator click navigation (desktop)
    jQuery('#step-1-indicator').on('click', function() {
        goToStep(1);
    });
    
    jQuery('#step-2-indicator').on('click', function() {
        goToStep(2);
    });
    
    jQuery('#step-3-indicator').on('click', function() {
        goToStep(3);
    });
    
    jQuery('#step-4-indicator').on('click', function() {
        goToStep(4);
    });
    
    // Step indicator click navigation (mobile)
    jQuery('#step-1-indicator-mobile').on('click', function() {
        goToStep(1);
    });
    
    jQuery('#step-2-indicator-mobile').on('click', function() {
        goToStep(2);
    });
    
    jQuery('#step-3-indicator-mobile').on('click', function() {
        goToStep(3);
    });
    
    jQuery('#step-4-indicator-mobile').on('click', function() {
        goToStep(4);
    });

    // Next buttons
    jQuery('#step-1-next').on('click', function() {
        if (validateStep1()) {
            goToStep(2);
        }
    });
    
    jQuery('#step-2-next').on('click', function() {
        goToStep(3);
    });
    
    jQuery('#step-3-next').on('click', function() {
        goToStep(4);
    });
    
    // Back buttons
    jQuery('#step-2-back').on('click', function() {
        goToStep(1);
    });
    
    jQuery('#step-3-back').on('click', function() {
        goToStep(2);
    });
    
    jQuery('#step-4-back').on('click', function() {
        goToStep(3);
    });
    
    // Start over button
    jQuery('#start-over').on('click', function() {
        if (confirm('Möchten Sie wirklich von vorne beginnen? Alle Änderungen gehen verloren.')) {
            resetWizard();
        }
    });
}

function validateStep1() {
    const validation = ValidationUtils.validateStep1();
    
    if (!validation.isValid) {
        validation.errors.forEach(error => {
            showAlert(error, 'warning');
        });
        return false;
    }
    
    return true;
}

function goToStep(stepNumber) {
    if (stepNumber < 1 || stepNumber > totalSteps) return;
    
    // Hide current step
    jQuery(`.app-step-content`).addClass('hidden');
    
    // Show target step
    jQuery(`#step-${stepNumber}`).removeClass('hidden');
    
    // Update step indicators
    updateStepIndicators(stepNumber);
    
    // Update current step
    currentStep = stepNumber;
    
    // Auto-scroll removed completely
}

function updateStepIndicators(activeStep) {
    // Update desktop indicators
    for (let i = 1; i <= totalSteps; i++) {
        const indicator = jQuery(`#step-${i}-indicator`);
        const mobileIndicator = jQuery(`#step-${i}-indicator-mobile`);
        const stepContainer = indicator.closest('.flex.flex-col');
        const mobileContainer = mobileIndicator.closest('.flex.items-center');
        
        if (i <= activeStep) {
            // Desktop indicators
            indicator.removeClass('bg-gray-300 text-gray-600')
                    .addClass('bg-green-600 text-white active');
            stepContainer.find('p').first().removeClass('text-gray-500').addClass('text-gray-900');
            stepContainer.find('p').last().removeClass('text-gray-500').addClass('text-gray-600');
            
            // Mobile indicators
            mobileIndicator.removeClass('bg-gray-300 text-gray-600')
                           .addClass('bg-green-600 text-white active');
            mobileContainer.removeClass('bg-gray-50 border-gray-200').addClass('bg-green-50 border-green-200');
            mobileContainer.find('p').first().removeClass('text-gray-500').addClass('text-gray-900');
            mobileContainer.find('p').last().removeClass('text-gray-500').addClass('text-gray-600');
        } else {
            // Desktop indicators
            indicator.removeClass('bg-green-600 text-white active')
                    .addClass('bg-gray-300 text-gray-600');
            stepContainer.find('p').first().removeClass('text-gray-900').addClass('text-gray-500');
            stepContainer.find('p').last().removeClass('text-gray-600').addClass('text-gray-500');
            
            // Mobile indicators
            mobileIndicator.removeClass('bg-green-600 text-white active')
                           .addClass('bg-gray-300 text-gray-600');
            mobileContainer.removeClass('bg-green-50 border-green-200').addClass('bg-gray-50 border-gray-200');
            mobileContainer.find('p').first().removeClass('text-gray-900').addClass('text-gray-500');
            mobileContainer.find('p').last().removeClass('text-gray-600').addClass('text-gray-500');
        }
    }
    
    // Update progress bars
    for (let i = 1; i < totalSteps; i++) {
        const progressBar = jQuery(`#progress-${i}-${i+1}`);
        if (i < activeStep) {
            progressBar.css('width', '100%');
        } else {
            progressBar.css('width', '0%');
        }
    }
}

function resetWizard() {
    // Reset to step 1
    goToStep(1);
    
    // Clear all form inputs
    jQuery('#text').val('');
    jQuery('#qr-text').val('');
    
    // Reset canvas
    if (canvas) {
        canvas.clear();
        replaceCanvas();
    }
    
    // Hide QR section
    jQuery('#qr-section').addClass('hidden');
    
    showAlert('Neues Bild wird erstellt...', 'success');
}

// Advanced Options Toggle
function setupAdvancedOptions() {
    jQuery('#toggle-advanced').on('click', function() {
        const advancedOptions = jQuery('#advanced-options');
        const isExpanded = !advancedOptions.hasClass('hidden');
        
        if (isExpanded) {
            advancedOptions.addClass('hidden');
            jQuery(this).attr('aria-expanded', 'false');
        } else {
            advancedOptions.removeClass('hidden');
            jQuery(this).attr('aria-expanded', 'true');
        }
    });
    
    // Handle format change for quality section
    jQuery('#image-format').on('change', function() {
        const qualitySection = jQuery('#quality-section');
        if (jQuery(this).val() === 'jpeg') {
            qualitySection.show();
        } else {
            qualitySection.hide();
        }
    });
}

// QR Code Section Toggle
function setupQRSection() {
    jQuery('#show-qr-section').on('click', function() {
        const qrSection = jQuery('#qr-section');
        qrSection.toggleClass('hidden');
        
        if (!qrSection.hasClass('hidden')) {
            jQuery('#qr-text').focus();
        }
        
        // Debug log
        console.log('QR section toggled, hidden class:', qrSection.hasClass('hidden'));
    });
}

// Canvas Dimensions Display
function updateCanvasDimensions() {
    if (canvas && typeof currentTemplate === 'function') {
        const template = currentTemplate();
        if (template) {
            const dimensions = `${template.width} × ${template.height} px`;
            const dpi = template.dpi || 200;
            const displayText = `${dimensions} • ${dpi} DPI`;
            jQuery('#canvas-dimensions').text(displayText);
        }
    } else {
        // Fallback if canvas isn't ready yet
        setTimeout(updateCanvasDimensions, 500);
    }
}

// Alert system now handled by centralized AlertSystem

// LocalStorage functions for organization selection
function saveSelectedOrganization() {
    const selectedOrg = jQuery('#logo-selection').val();
    if (selectedOrg) {
        localStorage.setItem('gruener-bildgenerator-organisation', selectedOrg);
    }
}

function restoreSelectedOrganization() {
    const savedOrg = localStorage.getItem('gruener-bildgenerator-organisation');
    if (savedOrg) {
        const $logoSelect = jQuery('#logo-selection');
        const searchableSelect = $logoSelect.data('searchable-select');
        
        // Check if options are already loaded and component is ready
        if ($logoSelect.find('option').length > 1 && searchableSelect) {
            if ($logoSelect.find(`option[value="${savedOrg}"]`).length > 0) {
                // Use searchable select's method to properly update both the select and UI
                searchableSelect.selectOption(savedOrg);
                console.log('Restored organization selection:', savedOrg);
            }
        } else if ($logoSelect.find('option').length > 1) {
            // Fallback for regular select without searchable component
            if ($logoSelect.find(`option[value="${savedOrg}"]`).length > 0) {
                $logoSelect.val(savedOrg);
                $logoSelect.trigger('change');
                console.log('Restored organization selection (fallback):', savedOrg);
            }
        } else {
            // If options aren't loaded yet, wait for them with a minimal delay
            setTimeout(() => restoreSelectedOrganization(), 50);
        }
    }
}

// Auto-advance logic
function setupAutoAdvance() {
    // Auto-advance from step 1 when template and logo are selected
    jQuery('#canvas-template, #logo-selection').on('change', function() {
        // Save organization selection when it changes
        if (this.id === 'logo-selection') {
            saveSelectedOrganization();
        }

        // Always update dimensions when template changes
        setTimeout(() => {
            updateCanvasDimensions();
        }, 200);

        // Show Facebook mobile format warning when Facebook Header template is selected
        if (this.id === 'canvas-template') {
            const templateName = jQuery('#canvas-template').val();

            // Clear previous template warnings
            if (window.AlertSystem) {
                window.AlertSystem.closeAll();
            }

            // Article images are delivered through Cloudflare Image Resizing
            // with fit=cover and a centred gravity, so a block that asks for a
            // different ratio than 2:3 crops the edges away rather than
            // letterboxing. Keeping text central keeps it readable everywhere.
            if (templateName === 'artikel_23') {
                if (window.AlertSystem) {
                    window.AlertSystem.show(
                        'Hinweis: Artikelbilder werden je nach Block der Website unterschiedlich beschnitten — immer aus der Mitte heraus. Text und wichtige Bildteile daher möglichst mittig platzieren.',
                        'info',
                        {
                            autoClose: false,
                            scrollIntoView: false
                        }
                    );
                }
            }

            // Show Facebook mobile warning
            if (templateName === 'facebook_header') {
                if (window.AlertSystem) {
                    window.AlertSystem.show(
                        'Hinweis: Facebook verwendet auf Mobilgeräten ein anderes Header-Format. Bitte Text möglichst mittig platzieren für optimale Darstellung.',
                        'info',
                        {
                            autoClose: false,
                            scrollIntoView: false
                        }
                    );
                }
            }
        }

        if (currentStep === 1) {
            const template = jQuery('#canvas-template').val();
            const logo = jQuery('#logo-selection').val();

            // Template selection notification removed
        }
    });
    
    // Auto-advance from step 2 when background image is selected
    jQuery('#meme-input').on('change', function() {
        // Background image notification removed
    });
}

// Enhanced Canvas Integration
function enhanceCanvasIntegration() {
    // Override the original replaceCanvas function to include dimension updates
    const originalReplaceCanvas = window.replaceCanvas;
    window.replaceCanvas = function() {
        originalReplaceCanvas.apply(this, arguments);
        // Update dimensions after canvas is fully replaced
        setTimeout(() => {
            updateCanvasDimensions();
        }, 100);
    };
    
    // Canvas event listeners for better UX
    if (canvas) {
        canvas.on('object:added', function(e) {
            // Object added - no auto-advance to maintain user control
        });
        
        canvas.on('object:removed', function(e) {
            // Element removed silently - no notification needed
        });
    }
}

// Mobile-specific enhancements
function setupMobileEnhancements() {
    // Better mobile navigation
    if (window.innerWidth <= 768) {
        // Simplified step indicators for mobile
        jQuery('.app-step-indicator').addClass('mobile-compact');
        
        // Touch-friendly buttons
        jQuery('.btn-primary, .btn-secondary').addClass('touch-friendly');
    }
}

// Initialize everything when document is ready
jQuery(document).ready(function() {
    // Initialize logo state first
    LogoState.initialize();

    // Set initial UI state based on logo enabled/disabled
    const isLogoEnabled = LogoState.isLogoEnabled();
    jQuery('#logo-toggle').prop('checked', isLogoEnabled);

    if (!isLogoEnabled) {
        // If logo is disabled, disable the logo selection dropdown
        jQuery('#logo-selection').prop('disabled', true).addClass('opacity-50');
    }

    initializeWizard();
    setupAutoAdvance();
    enhanceCanvasIntegration();
    setupMobileEnhancements();

    // Restore saved organization selection (now immediate with embedded logos)
    setTimeout(() => {
        restoreSelectedOrganization();
    }, 100);

    // Initial state - no welcome message on first load

    // Update dimensions after a short delay to ensure canvas is ready
    setTimeout(() => {
        updateCanvasDimensions();
    }, 1000);
});

// Window resize handler for responsive updates
jQuery(window).on('resize', function() {
    updateCanvasDimensions();
    setupMobileEnhancements();
});

// Expose functions to global scope for inline event handlers
window.goToStep = goToStep;

// === resources/js/qrcode/qrcode-helpers.js ===
// QR Code Helper Functions and Utilities

// QR Code Data Validation Helpers
const QRValidationHelpers = {
    // Validate text input
    validateText: function(text) {
        if (!text || typeof text !== 'string') {
            return { valid: false, error: 'Text ist erforderlich' };
        }
        
        const trimmed = text.trim();
        if (trimmed.length === 0) {
            return { valid: false, error: 'Text darf nicht leer sein' };
        }
        
        if (trimmed.length > 2000) {
            return { valid: false, error: 'Text ist zu lang (maximal 2000 Zeichen)' };
        }
        
        return { valid: true, data: trimmed };
    },
    
    // Validate and normalize URL
    validateURL: function(url) {
        if (!url || typeof url !== 'string') {
            return { valid: false, error: 'URL ist erforderlich' };
        }
        
        let trimmed = url.trim();
        if (trimmed.length === 0) {
            return { valid: false, error: 'URL darf nicht leer sein' };
        }
        
        // Try to add protocol if missing
        if (!trimmed.match(/^https?:\/\//i)) {
            trimmed = 'https://' + trimmed;
        }
        
        // Validate URL format
        try {
            const urlObj = new URL(trimmed);
            
            // Basic security checks
            if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
                return { valid: false, error: 'Nur HTTP und HTTPS URLs sind erlaubt' };
            }
            
            return { valid: true, data: urlObj.toString() };
        } catch (error) {
            return { valid: false, error: 'Ungültige URL' };
        }
    },
    
    // Validate email address
    validateEmail: function(email) {
        if (!email || typeof email !== 'string') {
            return { valid: false, error: 'E-Mail-Adresse ist erforderlich' };
        }
        
        const trimmed = email.trim();
        if (trimmed.length === 0) {
            return { valid: false, error: 'E-Mail-Adresse darf nicht leer sein' };
        }
        
        // Basic email regex
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmed)) {
            return { valid: false, error: 'Ungültige E-Mail-Adresse' };
        }
        
        return { valid: true, data: trimmed };
    },
    
    // Validate vCard data
    validateVCard: function(data) {
        if (!data || typeof data !== 'object') {
            return { valid: false, error: 'Ungültige Visitenkarten-Daten' };
        }
        
        // At least last name is required
        if (!data.lastname || data.lastname.trim().length === 0) {
            return { valid: false, error: 'Nachname ist erforderlich' };
        }
        
        // Validate email if provided
        if (data.email && data.email.trim().length > 0) {
            const emailValidation = this.validateEmail(data.email);
            if (!emailValidation.valid) {
                return { valid: false, error: 'Ungültige E-Mail-Adresse: ' + emailValidation.error };
            }
        }
        
        // Validate website if provided
        if (data.website && data.website.trim().length > 0) {
            const urlValidation = this.validateURL(data.website);
            if (!urlValidation.valid) {
                return { valid: false, error: 'Ungültige Website-URL: ' + urlValidation.error };
            }
        }
        
        return { valid: true, data: data };
    }
};

// QR Code Format Helpers
const QRFormatHelpers = {
    // Format text for QR code
    formatText: function(text) {
        return text.trim();
    },
    
    // Format URL for QR code
    formatURL: function(url) {
        let formatted = url.trim();
        
        // Add https:// if no protocol specified
        if (!formatted.match(/^https?:\/\//i)) {
            formatted = 'https://' + formatted;
        }
        
        return formatted;
    },
    
    // Format email data into mailto URL
    formatEmail: function(email, subject = '', body = '') {
        let mailto = `mailto:${email.trim()}`;
        const params = [];
        
        if (subject && subject.trim().length > 0) {
            params.push(`subject=${encodeURIComponent(subject.trim())}`);
        }
        
        if (body && body.trim().length > 0) {
            params.push(`body=${encodeURIComponent(body.trim())}`);
        }
        
        if (params.length > 0) {
            mailto += '?' + params.join('&');
        }
        
        return mailto;
    },
    
    // Format vCard data
    formatVCard: function(data) {
        let vcard = 'BEGIN:VCARD\n';
        vcard += 'VERSION:3.0\n';
        
        // Name components
        const title = (data.title || '').trim();
        const firstname = (data.firstname || '').trim();
        const lastname = (data.lastname || '').trim();
        
        // Full name
        const fullName = [title, firstname, lastname].filter(n => n.length > 0).join(' ');
        vcard += `FN:${fullName}\n`;
        
        // Structured name (Last;First;Middle;Prefix;Suffix)
        vcard += `N:${lastname};${firstname};;${title};\n`;
        
        // Contact information
        if (data.phone && data.phone.trim().length > 0) {
            vcard += `TEL:${data.phone.trim()}\n`;
        }
        
        if (data.email && data.email.trim().length > 0) {
            vcard += `EMAIL:${data.email.trim()}\n`;
        }
        
        // Address
        if (data.address || data.city || data.zip) {
            const address = (data.address || '').trim();
            const city = (data.city || '').trim();
            const zip = (data.zip || '').trim();
            
            vcard += `ADR:;;${address};${city};;${zip};\n`;
        }
        
        // Website
        if (data.website && data.website.trim().length > 0) {
            let website = data.website.trim();
            if (!website.match(/^https?:\/\//i)) {
                website = 'https://' + website;
            }
            vcard += `URL:${website}\n`;
        }
        
        vcard += 'END:VCARD';
        
        return vcard;
    }
};

// QR Code Display Helpers
const QRDisplayHelpers = {
    // Get display name for QR type
    getTypeDisplayName: function(type) {
        const names = {
            'text': 'Text',
            'url': 'Homepage',
            'email': 'E-Mail',
            'vcard': 'Visitenkarte'
        };
        return names[type] || type;
    },
    
    // Get icon class for QR type
    getTypeIcon: function(type) {
        const icons = {
            'text': 'fas fa-font',
            'url': 'fas fa-globe',
            'email': 'fas fa-envelope',
            'vcard': 'fas fa-user'
        };
        return icons[type] || 'fas fa-qrcode';
    },
    
    // Format color name for display
    getColorDisplayName: function(colorValue) {
        const colorNames = {
            '#000000': 'Schwarz',
            '#FFFFFF': 'Weiß',
            '#257639': 'Dunkelgrün',
            '#56AF31': 'Hellgrün',
            '#FFED00': 'Gelb',
            '#E6007E': 'Magenta'
        };
        return colorNames[colorValue] || colorValue;
    },
    
    // Format background name for display
    getBackgroundDisplayName: function(bgValue) {
        const bgNames = {
            '#FFFFFF': 'Weiß',
            '#000000': 'Schwarz',
            'transparent': 'Transparent'
        };
        return bgNames[bgValue] || bgValue;
    },
    
    // Generate preview text for QR content
    generatePreviewText: function(type, data, maxLength = 50) {
        if (!data) return 'Keine Daten';
        
        let preview = '';
        
        switch (type) {
            case 'text':
                preview = data;
                break;
                
            case 'url':
                preview = data;
                break;
                
            case 'email':
                if (data.startsWith('mailto:')) {
                    const url = new URL(data);
                    preview = url.pathname; // The email address
                } else {
                    preview = data;
                }
                break;
                
            case 'vcard':
                // Extract name from vCard
                const nameMatch = data.match(/FN:(.+)/);
                if (nameMatch) {
                    preview = nameMatch[1];
                } else {
                    preview = 'Visitenkarte';
                }
                break;
                
            default:
                preview = data;
        }
        
        // Truncate if too long
        if (preview.length > maxLength) {
            preview = preview.substring(0, maxLength - 3) + '...';
        }
        
        return preview;
    }
};

// QR Code Storage Helpers
const QRStorageHelpers = {
    // Save QR settings to localStorage
    saveSettings: function(settings) {
        try {
            localStorage.setItem('qr-generator-settings', JSON.stringify(settings));
        } catch (error) {
            console.warn('Could not save QR settings to localStorage:', error);
        }
    },
    
    // Load QR settings from localStorage
    loadSettings: function() {
        try {
            const saved = localStorage.getItem('qr-generator-settings');
            return saved ? JSON.parse(saved) : null;
        } catch (error) {
            console.warn('Could not load QR settings from localStorage:', error);
            return null;
        }
    },
    
    // Save recent QR data for quick access
    saveRecentQR: function(qrData) {
        try {
            let recent = this.getRecentQRs();
            
            // Add new item to beginning
            recent.unshift({
                ...qrData,
                timestamp: Date.now()
            });
            
            // Keep only last 10 items
            recent = recent.slice(0, 10);
            
            localStorage.setItem('qr-generator-recent', JSON.stringify(recent));
        } catch (error) {
            console.warn('Could not save recent QR:', error);
        }
    },
    
    // Get recent QR codes
    getRecentQRs: function() {
        try {
            const saved = localStorage.getItem('qr-generator-recent');
            return saved ? JSON.parse(saved) : [];
        } catch (error) {
            console.warn('Could not load recent QRs:', error);
            return [];
        }
    },
    
    // Clear recent QR codes
    clearRecentQRs: function() {
        try {
            localStorage.removeItem('qr-generator-recent');
        } catch (error) {
            console.warn('Could not clear recent QRs:', error);
        }
    }
};

// QR Code Analytics Helpers
const QRAnalyticsHelpers = {
    // Track QR code generation
    trackGeneration: function(type, success = true) {
        try {
            const stats = this.getStats();
            
            if (!stats[type]) {
                stats[type] = { total: 0, successful: 0, failed: 0 };
            }
            
            stats[type].total++;
            if (success) {
                stats[type].successful++;
            } else {
                stats[type].failed++;
            }
            
            stats.lastUsed = Date.now();
            
            localStorage.setItem('qr-generator-stats', JSON.stringify(stats));
        } catch (error) {
            console.warn('Could not track QR generation:', error);
        }
    },
    
    // Get usage statistics
    getStats: function() {
        try {
            const saved = localStorage.getItem('qr-generator-stats');
            return saved ? JSON.parse(saved) : {};
        } catch (error) {
            console.warn('Could not load QR stats:', error);
            return {};
        }
    },
    
    // Get most used QR type
    getMostUsedType: function() {
        const stats = this.getStats();
        let maxType = null;
        let maxCount = 0;
        
        Object.keys(stats).forEach(type => {
            if (type !== 'lastUsed' && stats[type].total > maxCount) {
                maxCount = stats[type].total;
                maxType = type;
            }
        });
        
        return maxType;
    }
};

// QR Code Error Helpers
const QRErrorHelpers = {
    // Format error message for user display
    formatErrorMessage: function(error, context = '') {
        if (!error) return 'Unbekannter Fehler';
        
        let message = '';
        
        if (typeof error === 'string') {
            message = error;
        } else if (error.message) {
            message = error.message;
        } else {
            message = 'Ein unerwarteter Fehler ist aufgetreten';
        }
        
        // Add context if provided
        if (context) {
            message = `${context}: ${message}`;
        }
        
        return message;
    },
    
    // Check if error is recoverable
    isRecoverableError: function(error) {
        if (!error) return false;
        
        const message = error.message || error.toString();
        
        // Network or temporary errors are usually recoverable
        const recoverablePatterns = [
            /network/i,
            /timeout/i,
            /temporary/i,
            /try again/i,
            /rate limit/i
        ];
        
        return recoverablePatterns.some(pattern => pattern.test(message));
    },
    
    // Get suggested action for error
    getSuggestedAction: function(error) {
        if (!error) return 'Seite neu laden und erneut versuchen';
        
        const message = error.message || error.toString();
        
        if (message.includes('size') || message.includes('too large')) {
            return 'Versuchen Sie kürzeren Text oder weniger Daten';
        }
        
        if (message.includes('invalid') || message.includes('ungültig')) {
            return 'Überprüfen Sie Ihre Eingaben auf Richtigkeit';
        }
        
        if (message.includes('network') || message.includes('fetch')) {
            return 'Überprüfen Sie Ihre Internetverbindung';
        }
        
        if (this.isRecoverableError(error)) {
            return 'Einen Moment warten und erneut versuchen';
        }
        
        return 'Seite neu laden und erneut versuchen';
    }
};

// Export helpers for global access
window.QRValidationHelpers = QRValidationHelpers;
window.QRFormatHelpers = QRFormatHelpers;
window.QRDisplayHelpers = QRDisplayHelpers;
window.QRStorageHelpers = QRStorageHelpers;
window.QRAnalyticsHelpers = QRAnalyticsHelpers;
window.QRErrorHelpers = QRErrorHelpers;

// Utility function to safely call helper methods
function safeHelperCall(helperObject, methodName, ...args) {
    try {
        if (helperObject && typeof helperObject[methodName] === 'function') {
            return helperObject[methodName](...args);
        } else {
            console.warn(`Helper method ${methodName} not found`);
            return null;
        }
    } catch (error) {
        console.error(`Error calling helper method ${methodName}:`, error);
        return null;
    }
}

// Initialize helpers when document is ready
jQuery(document).ready(function() {
    console.log('QR Code Helpers loaded');
    
    // Auto-restore settings if available
    const savedSettings = QRStorageHelpers.loadSettings();
    if (savedSettings) {
        console.log('Loaded saved QR settings:', savedSettings);
    }
});

// === resources/js/qrcode/qrcode-generator.js ===
// QR Code Generation Logic
let qrCodeInstance = null;
let qrPreviewCanvas = null;

// QR Code Colors (from plan)
const QR_COLORS = {
    '#000000': 'Schwarz',
    '#FFFFFF': 'Weiß',
    '#257639': 'Dunkelgrün',
    '#56AF31': 'Hellgrün',
    '#FFED00': 'Gelb',
    '#E6007E': 'Magenta'
};

const QR_BACKGROUNDS = {
    '#FFFFFF': 'Weiß',
    '#000000': 'Schwarz',
    'transparent': 'Transparent'
};

// Generate QR Code Preview
function generateQRPreview(isLiveUpdate = false) {
    if (!qrSelectedType) {
        if (!isLiveUpdate) {
            showQRAlert('Kein Inhaltstyp ausgewählt', 'error');
        }
        return;
    }
    
    try {
        const qrData = collectQRData();
        
        if (!qrData) {
            if (!isLiveUpdate) {
                clearQRPreview();
                updateQRStatus('Geben Sie Daten ein für die Vorschau');
            }
            return;
        }
        
        // Update status
        updateQRStatus('QR-Code wird generiert...');
        
        // Generate QR code
        const qrColor = jQuery('#qr-color-select').val() || '#000000';
        const qrBackground = jQuery('#qr-background-select').val() || '#FFFFFF';
        
        generateQRCode(qrData, qrColor, qrBackground)
            .then((qrCanvas) => {
                if (qrCanvas) {
                    displayQRPreview(qrCanvas);
                    qrGeneratedCode = {
                        data: qrData,
                        color: qrColor,
                        background: qrBackground,
                        canvas: qrCanvas
                    };
                    
                    if (!isLiveUpdate) {
                        updateQRStatus('QR-Code erfolgreich erstellt');
                    } else {
                        updateQRStatus('Vorschau aktualisiert');
                    }
                }
            })
            .catch((error) => {
                console.error('QR Code generation error:', error);
                if (!isLiveUpdate) {
                    showQRAlert('Fehler beim Erstellen des QR-Codes: ' + error.message, 'error');
                }
                updateQRStatus('Fehler bei der Generierung');
            });
            
    } catch (error) {
        console.error('QR Preview error:', error);
        if (!isLiveUpdate) {
            showQRAlert('Fehler bei der Vorschau: ' + error.message, 'error');
        }
        updateQRStatus('Vorschau-Fehler');
    }
}

// Collect QR Data from form inputs
function collectQRData() {
    if (!qrSelectedType) return null;
    
    try {
        switch (qrSelectedType) {
            case 'text':
                const text = jQuery('#qr-text-input').val().trim();
                return text || null;
                
            case 'url':
                let url = jQuery('#qr-url-input').val().trim();
                if (!url) return null;
                
                // Add https:// if missing
                if (!url.match(/^https?:\/\//)) {
                    url = 'https://' + url;
                }
                return url;
                
            case 'email':
                const email = jQuery('#qr-email-address').val().trim();
                if (!email) return null;
                
                const subject = jQuery('#qr-email-subject').val().trim();
                const body = jQuery('#qr-email-body').val().trim();
                
                let mailto = `mailto:${email}`;
                const params = [];
                
                if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
                if (body) params.push(`body=${encodeURIComponent(body)}`);
                
                if (params.length > 0) {
                    mailto += '?' + params.join('&');
                }
                
                return mailto;
                
            case 'vcard':
                const lastname = jQuery('#qr-vcard-lastname').val().trim();
                if (!lastname) return null;
                
                const vcard = generateVCard();
                return vcard;
                
            default:
                return null;
        }
    } catch (error) {
        console.error('Error collecting QR data:', error);
        return null;
    }
}

// Generate vCard string
function generateVCard() {
    const fields = {
        title: jQuery('#qr-vcard-title').val().trim(),
        firstname: jQuery('#qr-vcard-firstname').val().trim(),
        lastname: jQuery('#qr-vcard-lastname').val().trim(),
        phone: jQuery('#qr-vcard-phone').val().trim(),
        email: jQuery('#qr-vcard-email').val().trim(),
        address: jQuery('#qr-vcard-address').val().trim(),
        zip: jQuery('#qr-vcard-zip').val().trim(),
        city: jQuery('#qr-vcard-city').val().trim(),
        website: jQuery('#qr-vcard-website').val().trim()
    };
    
    let vcard = 'BEGIN:VCARD\n';
    vcard += 'VERSION:3.0\n';
    
    // Name (required)
    const fullName = `${fields.title} ${fields.firstname} ${fields.lastname}`.trim();
    vcard += `FN:${fullName}\n`;
    vcard += `N:${fields.lastname};${fields.firstname};;${fields.title};\n`;
    
    // Optional fields
    if (fields.phone) vcard += `TEL:${fields.phone}\n`;
    if (fields.email) vcard += `EMAIL:${fields.email}\n`;
    
    if (fields.address || fields.zip || fields.city) {
        vcard += `ADR:;;${fields.address};${fields.city};;${fields.zip};\n`;
    }
    
    if (fields.website) {
        let website = fields.website;
        if (!website.match(/^https?:\/\//)) {
            website = 'https://' + website;
        }
        vcard += `URL:${website}\n`;
    }
    
    vcard += 'END:VCARD';
    
    return vcard;
}

// Generate QR Code using qrcode.js library
function generateQRCode(data, foregroundColor, backgroundColor) {
    return new Promise((resolve, reject) => {
        try {
            // Create temporary container for QR generation
            const tempDiv = document.createElement('div');
            tempDiv.style.display = 'none';
            document.body.appendChild(tempDiv);

            // Determine QR code size
            const qrSize = 512; // High resolution for quality

            // Check if foreground is white or near-white
            const isWhiteForeground = isColorWhiteOrNearWhite(foregroundColor);

            // Use a placeholder color for background when:
            // 1. We want transparency AND
            // 2. The foreground is white/near-white
            // This prevents white foreground pixels from being made transparent
            const PLACEHOLDER_COLOR = '#FF00FF'; // Magenta as placeholder
            const useBackgroundPlaceholder = backgroundColor === 'transparent' && isWhiteForeground;
            const actualColorLight = backgroundColor === 'transparent'
                ? (useBackgroundPlaceholder ? PLACEHOLDER_COLOR : '#FFFFFF')
                : backgroundColor;

            // QR code options
            const options = {
                text: data,
                width: qrSize,
                height: qrSize,
                colorDark: foregroundColor,
                colorLight: actualColorLight,
                correctLevel: QRCode.CorrectLevel.M, // Medium error correction
                quietZone: backgroundColor !== 'transparent' ? Math.floor(qrSize * 0.05) : 0,
                quietZoneColor: backgroundColor === 'transparent' ? 'transparent' : backgroundColor
            };

            // Check for contrast issues
            if (foregroundColor === backgroundColor && backgroundColor !== 'transparent') {
                reject(new Error('Vordergrund- und Hintergrundfarbe müssen unterschiedlich sein.'));
                return;
            }

            // Generate QR code
            const qr = new QRCode(tempDiv, options);

            // Wait for generation and extract canvas
            setTimeout(() => {
                try {
                    const qrImg = tempDiv.querySelector('img');
                    if (!qrImg) {
                        document.body.removeChild(tempDiv);
                        reject(new Error('QR-Code Bild konnte nicht generiert werden.'));
                        return;
                    }

                    // Create final canvas with proper quiet zone
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    let finalSize = qrSize;
                    let quietZoneSize = 0;

                    if (backgroundColor !== 'transparent') {
                        quietZoneSize = Math.floor(qrSize * 0.05);
                        finalSize = qrSize + (quietZoneSize * 2);
                    }

                    canvas.width = finalSize;
                    canvas.height = finalSize;

                    // Fill background
                    if (backgroundColor !== 'transparent') {
                        ctx.fillStyle = backgroundColor;
                        ctx.fillRect(0, 0, finalSize, finalSize);
                    }

                    // Helper function to make background pixels transparent
                    const makeBackgroundTransparent = () => {
                        if (backgroundColor === 'transparent') {
                            const imageData = ctx.getImageData(0, 0, finalSize, finalSize);
                            const pixelData = imageData.data;

                            for (let i = 0; i < pixelData.length; i += 4) {
                                const r = pixelData[i];
                                const g = pixelData[i + 1];
                                const b = pixelData[i + 2];

                                if (useBackgroundPlaceholder) {
                                    // When using placeholder, only make magenta pixels transparent
                                    // Magenta is RGB(255, 0, 255)
                                    if (r > 250 && g < 5 && b > 250) {
                                        pixelData[i + 3] = 0; // Set alpha to 0 (fully transparent)
                                    }
                                } else {
                                    // Standard case: make white/near-white pixels transparent
                                    // This is safe because foreground is NOT white
                                    if (r > 250 && g > 250 && b > 250) {
                                        pixelData[i + 3] = 0; // Set alpha to 0 (fully transparent)
                                    }
                                }
                            }

                            ctx.putImageData(imageData, 0, 0);
                        }
                    };

                    // Draw QR code
                    qrImg.onload = () => {
                        ctx.drawImage(qrImg, quietZoneSize, quietZoneSize, qrSize, qrSize);
                        makeBackgroundTransparent();

                        // Clean up
                        document.body.removeChild(tempDiv);

                        resolve(canvas);
                    };

                    // If image is already loaded
                    if (qrImg.complete) {
                        ctx.drawImage(qrImg, quietZoneSize, quietZoneSize, qrSize, qrSize);
                        makeBackgroundTransparent();
                        document.body.removeChild(tempDiv);
                        resolve(canvas);
                    }

                } catch (error) {
                    document.body.removeChild(tempDiv);
                    reject(error);
                }
            }, 200); // Wait for QR generation

        } catch (error) {
            reject(error);
        }
    });
}

// Helper function to check if a color is white or near-white
function isColorWhiteOrNearWhite(hexColor) {
    // Remove # if present
    const hex = hexColor.replace('#', '');

    // Parse RGB values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Check if all RGB values are above 250 (near-white threshold)
    return r > 250 && g > 250 && b > 250;
}

// Display QR Preview
function displayQRPreview(canvas) {
    const previewContainer = jQuery('#qr-preview-display');
    const placeholder = jQuery('#qr-preview-placeholder');
    
    // Clear previous preview
    previewContainer.empty();
    
    // Create display canvas (scaled down for preview)
    const displayCanvas = document.createElement('canvas');
    const displayCtx = displayCanvas.getContext('2d');
    
    const maxSize = 300; // Maximum preview size
    const scale = Math.min(maxSize / canvas.width, maxSize / canvas.height);
    
    displayCanvas.width = canvas.width * scale;
    displayCanvas.height = canvas.height * scale;
    
    // Enable crisp pixel rendering
    displayCtx.imageSmoothingEnabled = false;
    displayCtx.webkitImageSmoothingEnabled = false;
    displayCtx.mozImageSmoothingEnabled = false;
    displayCtx.msImageSmoothingEnabled = false;
    
    displayCtx.drawImage(canvas, 0, 0, displayCanvas.width, displayCanvas.height);
    
    // Add styling
    displayCanvas.style.maxWidth = '100%';
    displayCanvas.style.height = 'auto';
    displayCanvas.style.border = '1px solid #e5e7eb';
    displayCanvas.style.borderRadius = '8px';
    displayCanvas.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
    
    // Show preview
    previewContainer.append(displayCanvas).removeClass('hidden');
    placeholder.addClass('hidden');
    
    // Store reference for download
    qrPreviewCanvas = canvas;
}

// Clear QR Preview
function clearQRPreview() {
    const previewContainer = jQuery('#qr-preview-display');
    const placeholder = jQuery('#qr-preview-placeholder');
    
    previewContainer.addClass('hidden').empty();
    placeholder.removeClass('hidden');
    
    qrPreviewCanvas = null;
    qrGeneratedCode = null;
    
    updateQRStatus('Wählen Sie einen Inhaltstyp');
}

// Download QR Code
function downloadQRCode() {
    if (!qrGeneratedCode || !qrPreviewCanvas) {
        showQRAlert('Kein QR-Code zum Herunterladen verfügbar.', 'warning');
        return;
    }
    
    try {
        // Get current colors (in case they were changed)
        const currentColor = jQuery('#qr-color-select').val();
        const currentBackground = jQuery('#qr-background-select').val();
        
        // Regenerate with current settings if they changed
        if (currentColor !== qrGeneratedCode.color || currentBackground !== qrGeneratedCode.background) {
            updateQRStatus('QR-Code wird mit neuen Einstellungen generiert...');
            
            generateQRCode(qrGeneratedCode.data, currentColor, currentBackground)
                .then((newCanvas) => {
                    performDownload(newCanvas);
                })
                .catch((error) => {
                    showQRAlert('Fehler beim Erstellen des finalen QR-Codes: ' + error.message, 'error');
                });
        } else {
            performDownload(qrPreviewCanvas);
        }
        
    } catch (error) {
        console.error('Download error:', error);
        showQRAlert('Fehler beim Download: ' + error.message, 'error');
    }
}

// Perform actual download
function performDownload(canvas) {
    try {
        // Convert to blob
        canvas.toBlob((blob) => {
            if (!blob) {
                showQRAlert('Fehler beim Erstellen der Bild-Datei.', 'error');
                return;
            }
            
            // Create download link
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = generateQRFileName();
            
            // Trigger download
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up
            URL.revokeObjectURL(url);
            
            updateQRStatus('QR-Code erfolgreich heruntergeladen');
            showQRAlert('QR-Code wurde heruntergeladen!', 'success');
            
        }, 'image/png', 1.0);
        
    } catch (error) {
        console.error('Download execution error:', error);
        showQRAlert('Fehler beim Download-Vorgang: ' + error.message, 'error');
    }
}

// Generate filename for QR code
function generateQRFileName() {
    const type = qrSelectedType || 'qrcode';
    const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    return `qrcode-${type}-${timestamp}.png`;
}

// Setup color change handlers
function setupQRColorHandlers() {
    jQuery('#qr-color-select, #qr-background-select').on('change', function() {
        if (qrGeneratedCode && qrCurrentStep === 3) {
            // Regenerate preview with new colors
            setTimeout(() => {
                generateQRPreview();
            }, 100);
        }
    });
}

// Initialize QR Generator
function initializeQRGenerator() {
    setupQRColorHandlers();
    console.log('QR Code Generator initialized');
}

// Initialize when document is ready
jQuery(document).ready(function() {
    // Don't auto-initialize - will be called when needed
    console.log('QR Code Generator module loaded');
});

// === resources/js/qrcode/qrcode-wizard.js ===
// QR Code Wizard Navigation and State Management
let qrCurrentStep = 1;
const qrTotalSteps = 3;
let qrSelectedType = null;
let qrGeneratedCode = null;

// QR Code Wizard Initialization
function initializeQRWizard() {
    qrCurrentStep = 1;
    qrSelectedType = null;
    qrGeneratedCode = null;
    
    setupQRStepNavigation();
    setupQRTypeSelection();
    qrGoToStep(1);
    
    console.log('QR Code Wizard initialized');
}

// QR Code Step Navigation Setup
function setupQRStepNavigation() {
    // Next buttons
    jQuery('#qr-step-1-next').on('click', function() {
        if (validateQRStep1()) {
            qrGoToStep(2);
        }
    });
    
    jQuery('#qr-step-2-next').on('click', function() {
        if (validateQRStep2()) {
            qrGoToStep(3);
        }
    });
    
    // Back buttons
    jQuery('#qr-step-2-back').on('click', function() {
        qrGoToStep(1);
    });
    
    jQuery('#qr-step-3-back').on('click', function() {
        qrGoToStep(2);
    });
    
    // Start over button
    jQuery('#qr-start-over').on('click', function() {
        if (confirm('Möchten Sie wirklich einen neuen QR-Code erstellen? Alle Eingaben gehen verloren.')) {
            resetQRWizard();
        }
    });
}

// QR Code Type Selection Setup
function setupQRTypeSelection() {
    jQuery('.app-qr-type-button').on('click', function() {
        const type = jQuery(this).data('type');
        selectQRType(type);
    });
}

// Select QR Code Type
function selectQRType(type) {
    qrSelectedType = type;
    
    // Update button states
    jQuery('.app-qr-type-button').removeClass('active bg-gruene-primary text-white')
                             .addClass('bg-white text-gray-700 border-gray-300');
    
    jQuery(`.app-qr-type-button[data-type="${type}"]`)
        .removeClass('bg-white text-gray-700 border-gray-300')
        .addClass('active bg-gruene-primary text-white');
    
    // Enable next button
    jQuery('#qr-step-1-next').prop('disabled', false).removeClass('disabled:opacity-50');
    
    // Update status
    updateQRStatus(`${getQRTypeDisplayName(type)} ausgewählt`);
    
    console.log('QR Type selected:', type);
}

// Get display name for QR type
function getQRTypeDisplayName(type) {
    const names = {
        'text': 'Text',
        'url': 'Homepage',
        'email': 'E-Mail',
        'vcard': 'Visitenkarte'
    };
    return names[type] || type;
}

// QR Code Step Navigation
function qrGoToStep(stepNumber) {
    if (stepNumber < 1 || stepNumber > qrTotalSteps) return;
    
    // Hide current step
    jQuery('.app-qr-step-content').addClass('hidden');
    
    // Show target step
    jQuery(`#qr-step-${stepNumber}`).removeClass('hidden');
    
    // Update step indicators
    updateQRStepIndicators(stepNumber);
    
    // Update current step
    qrCurrentStep = stepNumber;
    
    // Handle step-specific logic
    if (stepNumber === 2) {
        renderQRForm();
    } else if (stepNumber === 3) {
        if (typeof generateQRPreview === 'function') {
            generateQRPreview();
        }
    }
    
    // Auto-scroll removed completely
    
    console.log('QR Step:', stepNumber);
}

// Update QR Step Indicators
function updateQRStepIndicators(activeStep) {
    for (let i = 1; i <= qrTotalSteps; i++) {
        const indicator = jQuery(`#qr-step-${i}-indicator`);
        const stepContainer = indicator.closest('.flex.flex-col');
        
        if (i <= activeStep) {
            indicator.removeClass('bg-gray-300 text-gray-600')
                    .addClass('bg-green-600 text-white active');
            stepContainer.find('p').first().removeClass('text-gray-500').addClass('text-gray-900');
            stepContainer.find('p').last().removeClass('text-gray-500').addClass('text-gray-600');
        } else {
            indicator.removeClass('bg-green-600 text-white active')
                    .addClass('bg-gray-300 text-gray-600');
            stepContainer.find('p').first().removeClass('text-gray-900').addClass('text-gray-500');
            stepContainer.find('p').last().removeClass('text-gray-600').addClass('text-gray-500');
        }
    }
    
    // Update progress bars
    for (let i = 1; i < qrTotalSteps; i++) {
        const progressBar = jQuery(`#qr-progress-${i}-${i+1}`);
        if (i < activeStep) {
            progressBar.css('width', '100%');
        } else {
            progressBar.css('width', '0%');
        }
    }
}

// Render QR Form based on selected type
function renderQRForm() {
    const container = jQuery('#qr-form-container');
    container.empty();
    
    if (!qrSelectedType) {
        container.html('<p class="text-gray-500">Kein Inhaltstyp ausgewählt</p>');
        return;
    }
    
    let formHTML = '';
    
    switch (qrSelectedType) {
        case 'text':
            formHTML = `
                <div>
                    <label for="qr-text-input" class="block text-sm font-medium text-gray-700 mb-2">
                        Ihr Text:
                    </label>
                    <textarea
                        id="qr-text-input"
                        class="gat-textarea w-full"
                        rows="5"
                        placeholder="Geben Sie Ihren Text hier ein..."
                        required
                    ></textarea>
                </div>
            `;
            break;
            
        case 'url':
            formHTML = `
                <div>
                    <label for="qr-url-input" class="block text-sm font-medium text-gray-700 mb-2">
                        Website-URL:
                    </label>
                    <input
                        type="url"
                        id="qr-url-input"
                        class="gat-input w-full"
                        placeholder="https://www.beispiel.de"
                        required
                    />
                </div>
            `;
            break;
            
        case 'email':
            formHTML = `
                <div class="space-y-4">
                    <div>
                        <label for="qr-email-address" class="block text-sm font-medium text-gray-700 mb-2">
                            E-Mail-Adresse:
                        </label>
                        <input
                            type="email"
                            id="qr-email-address"
                            class="gat-input w-full"
                            placeholder="beispiel@gruene.at"
                            required
                        />
                    </div>
                    <div>
                        <label for="qr-email-subject" class="block text-sm font-medium text-gray-700 mb-2">
                            Betreff:
                        </label>
                        <input
                            type="text"
                            id="qr-email-subject"
                            class="gat-input w-full"
                            placeholder="Betreff der E-Mail"
                        />
                    </div>
                    <div>
                        <label for="qr-email-body" class="block text-sm font-medium text-gray-700 mb-2">
                            Nachricht:
                        </label>
                        <textarea
                            id="qr-email-body"
                            class="gat-textarea w-full"
                            rows="5"
                            placeholder="Nachrichtentext..."
                        ></textarea>
                    </div>
                </div>
            `;
            break;
            
        case 'vcard':
            formHTML = `
                <div class="space-y-4">
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label for="qr-vcard-title" class="block text-sm font-medium text-gray-700 mb-2">
                                Titel:
                            </label>
                            <input
                                type="text"
                                id="qr-vcard-title"
                                class="gat-input w-full"
                                placeholder="Dr., Mag., etc."
                            />
                        </div>
                        <div>
                            <label for="qr-vcard-firstname" class="block text-sm font-medium text-gray-700 mb-2">
                                Vorname:
                            </label>
                            <input
                                type="text"
                                id="qr-vcard-firstname"
                                class="gat-input w-full"
                                placeholder="Max"
                            />
                        </div>
                    </div>
                    <div>
                        <label for="qr-vcard-lastname" class="block text-sm font-medium text-gray-700 mb-2">
                            Nachname: *
                        </label>
                        <input
                            type="text"
                            id="qr-vcard-lastname"
                            class="gat-input w-full"
                            placeholder="Mustermann"
                            required
                        />
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label for="qr-vcard-phone" class="block text-sm font-medium text-gray-700 mb-2">
                                Telefonnummer:
                            </label>
                            <input
                                type="tel"
                                id="qr-vcard-phone"
                                class="gat-input w-full"
                                placeholder="+43 1 234 5678"
                            />
                        </div>
                        <div>
                            <label for="qr-vcard-email" class="block text-sm font-medium text-gray-700 mb-2">
                                E-Mail:
                            </label>
                            <input
                                type="email"
                                id="qr-vcard-email"
                                class="gat-input w-full"
                                placeholder="max.mustermann@gruene.at"
                            />
                        </div>
                    </div>
                    <div>
                        <label for="qr-vcard-address" class="block text-sm font-medium text-gray-700 mb-2">
                            Straße und Hausnummer:
                        </label>
                        <input
                            type="text"
                            id="qr-vcard-address"
                            class="gat-input w-full"
                            placeholder="Musterstraße 123"
                        />
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label for="qr-vcard-zip" class="block text-sm font-medium text-gray-700 mb-2">
                                PLZ:
                            </label>
                            <input
                                type="text"
                                id="qr-vcard-zip"
                                class="gat-input w-full"
                                placeholder="1010"
                            />
                        </div>
                        <div>
                            <label for="qr-vcard-city" class="block text-sm font-medium text-gray-700 mb-2">
                                Stadt:
                            </label>
                            <input
                                type="text"
                                id="qr-vcard-city"
                                class="gat-input w-full"
                                placeholder="Wien"
                            />
                        </div>
                    </div>
                    <div>
                        <label for="qr-vcard-website" class="block text-sm font-medium text-gray-700 mb-2">
                            Homepage:
                        </label>
                        <input
                            type="url"
                            id="qr-vcard-website"
                            class="gat-input w-full"
                            placeholder="https://www.gruene.at"
                        />
                    </div>
                    <div class="gat-callout gat-callout--info">
                        <p>
                            Alle Felder außer dem Nachnamen sind optional.
                            Der QR-Code wird auch bei Teilbefüllung erstellt.
                        </p>
                    </div>
                </div>
            `;
            break;
    }
    
    container.html(formHTML);
    
    // Focus first input
    container.find('input, textarea').first().focus();
    
    // Setup live preview updates
    setupQRLivePreview();
}

// Setup live preview updates
function setupQRLivePreview() {
    const inputs = jQuery('#qr-form-container input, #qr-form-container textarea');
    
    inputs.on('input', debounce(function() {
        if (qrCurrentStep === 2) {
            // Update status to show preview is being generated
            updateQRStatus('Vorschau wird aktualisiert...');
            
            // Generate preview with a slight delay
            setTimeout(() => {
                if (typeof generateQRPreview === 'function') {
                    generateQRPreview(true);
                }
            }, 100);
        }
    }, 300));
}

// Validate QR Step 1
function validateQRStep1() {
    if (!qrSelectedType) {
        showQRAlert('Bitte wählen Sie einen Inhaltstyp aus.', 'warning');
        return false;
    }
    return true;
}

// Validate QR Step 2
function validateQRStep2() {
    if (!qrSelectedType) {
        showQRAlert('Kein Inhaltstyp ausgewählt.', 'error');
        return false;
    }
    
    // Type-specific validation
    switch (qrSelectedType) {
        case 'text':
            if (!jQuery('#qr-text-input').val().trim()) {
                showQRAlert('Bitte geben Sie einen Text ein.', 'warning');
                jQuery('#qr-text-input').focus();
                return false;
            }
            break;
            
        case 'url':
            const url = jQuery('#qr-url-input').val().trim();
            if (!url) {
                showQRAlert('Bitte geben Sie eine URL ein.', 'warning');
                jQuery('#qr-url-input').focus();
                return false;
            }
            if (!isValidURL(url)) {
                showQRAlert('Bitte geben Sie eine gültige URL ein.', 'warning');
                jQuery('#qr-url-input').focus();
                return false;
            }
            break;
            
        case 'email':
            if (!jQuery('#qr-email-address').val().trim()) {
                showQRAlert('Bitte geben Sie eine E-Mail-Adresse ein.', 'warning');
                jQuery('#qr-email-address').focus();
                return false;
            }
            break;
            
        case 'vcard':
            if (!jQuery('#qr-vcard-lastname').val().trim()) {
                showQRAlert('Bitte geben Sie mindestens einen Nachnamen ein.', 'warning');
                jQuery('#qr-vcard-lastname').focus();
                return false;
            }
            break;
    }
    
    return true;
}

// Reset QR Wizard
function resetQRWizard() {
    qrCurrentStep = 1;
    qrSelectedType = null;
    qrGeneratedCode = null;
    
    // Clear form inputs
    jQuery('#qr-form-container').empty();
    
    // Reset type buttons
    jQuery('.app-qr-type-button').removeClass('active bg-gruene-primary text-white')
                             .addClass('bg-white text-gray-700 border-gray-300');
    
    // Disable next button
    jQuery('#qr-step-1-next').prop('disabled', true).addClass('disabled:opacity-50');
    
    // Clear preview
    if (typeof clearQRPreview === 'function') {
        clearQRPreview();
    }
    
    // Go to step 1
    qrGoToStep(1);
    
    updateQRStatus('Wählen Sie einen Inhaltstyp');
    showQRAlert('Neuer QR-Code wird erstellt...', 'success');
}

// Update QR Status
function updateQRStatus(message) {
    jQuery('#qr-status').text(message);
}

// Show QR Alert - Now uses unified alert system
function showQRAlert(message, type = 'info') {
    // Use the unified alert system if available
    if (typeof window.AlertSystem !== 'undefined' && window.AlertSystem.showQRAlert) {
        return window.AlertSystem.showQRAlert(message, type);
    }
    
    // Fallback to legacy system
    if (typeof showAlert === 'function') {
        showAlert(message, type);
    } else if (typeof showTailwindAlert === 'function') {
        showTailwindAlert(message, type);
    } else {
        // Final fallback to console
        console.log(`QR Alert (${type}):`, message);
    }
}

// Utility: Debounce function
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func.apply(this, args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

// Utility: Validate URL
function isValidURL(string) {
    try {
        new URL(string);
        return true;
    } catch (_) {
        // Try adding https:// if it's missing
        try {
            new URL('https://' + string);
            return true;
        } catch (_) {
            return false;
        }
    }
}

// Initialize QR Wizard when module is loaded
jQuery(document).ready(function() {
    // Don't auto-initialize - will be called when QR tab is activated
    console.log('QR Code Wizard module loaded');
});

// Expose functions to global scope for inline event handlers
window.qrGoToStep = qrGoToStep;

// === resources/js/qrcode/qrcode-handlers.js ===
// QR Code Event Handlers
let qrHandlersInitialized = false;

// Initialize all QR Code event handlers
function initializeQRHandlers() {
    if (qrHandlersInitialized) {
        console.log('QR Handlers already initialized');
        return;
    }
    
    setupTabNavigationHandlers();
    setupQRDownloadHandler();
    
    qrHandlersInitialized = true;
    console.log('QR Code Handlers initialized');
}

// Setup Tab Navigation Handlers
function setupTabNavigationHandlers() {
    // Desktop tab handlers
    jQuery('#tab-generator').on('click', function() {
        switchToTab('generator');
    });
    
    jQuery('#tab-qrcode').on('click', function() {
        switchToTab('qrcode');
    });
    
    // Mobile tab handlers
    jQuery('#tab-generator-mobile').on('click', function() {
        switchToTab('generator');
    });
    
    jQuery('#tab-qrcode-mobile').on('click', function() {
        switchToTab('qrcode');
    });
}

// Switch between tabs
function switchToTab(tab) {
    // Update tab button states
    updateTabButtonStates(tab);
    
    // Show/hide containers
    if (tab === 'generator') {
        jQuery('#generator').removeClass('d-none').show();
        jQuery('#qrcode-container').addClass('d-none').hide();
        
        // Update page title context
        document.title = 'Grüner Bildgenerator';
        
    } else if (tab === 'qrcode') {
        jQuery('#generator').addClass('d-none').hide();
        jQuery('#qrcode-container').removeClass('d-none').show();
        
        // Initialize QR wizard if needed
        if (typeof initializeQRWizard === 'function') {
            initializeQRWizard();
        }
        
        // Initialize QR generator if needed
        if (typeof initializeQRGenerator === 'function') {
            initializeQRGenerator();
        }
        
        // Update page title context
        document.title = 'QR-Code Generator - Grüne';
    }
    
    // Store current tab in localStorage
    localStorage.setItem('gruener-generator-current-tab', tab);
    
    console.log('Switched to tab:', tab);
}

// Update tab button visual states
function updateTabButtonStates(activeTab) {
    // Desktop tabs
    const desktopTabs = ['#tab-generator', '#tab-qrcode'];
    desktopTabs.forEach(tabSelector => {
        const tab = jQuery(tabSelector);
        const isActive = (activeTab === 'generator' && tabSelector.includes('generator')) ||
                        (activeTab === 'qrcode' && tabSelector.includes('qrcode'));
        
        if (isActive) {
            tab.removeClass('bg-gray-200 text-gray-700')
               .addClass('bg-gruene-primary text-white active');
        } else {
            tab.removeClass('bg-gruene-primary text-white active')
               .addClass('bg-gray-200 text-gray-700');
        }
    });
    
    // Mobile tabs
    const mobileTabs = ['#tab-generator-mobile', '#tab-qrcode-mobile'];
    mobileTabs.forEach(tabSelector => {
        const tab = jQuery(tabSelector);
        const isActive = (activeTab === 'generator' && tabSelector.includes('generator')) ||
                        (activeTab === 'qrcode' && tabSelector.includes('qrcode'));
        
        if (isActive) {
            tab.removeClass('bg-gray-200 text-gray-700')
               .addClass('bg-gruene-primary text-white active');
        } else {
            tab.removeClass('bg-gruene-primary text-white active')
               .addClass('bg-gray-200 text-gray-700');
        }
    });
}

// Setup QR Download Handler
function setupQRDownloadHandler() {
    jQuery('#qr-download').on('click', function() {
        if (typeof downloadQRCode === 'function') {
            downloadQRCode();
        } else {
            console.error('downloadQRCode function not found');
            showQRAlert('Download-Funktion nicht verfügbar', 'error');
        }
    });
}

// Handle URL hash changes for direct linking
function handleQRHashNavigation() {
    const hash = window.location.hash;
    
    if (hash === '#qrcode-tab') {
        switchToTab('qrcode');
    } else if (hash === '#generator-tab' || hash === '') {
        switchToTab('generator');
    }
}

// Setup hash navigation
function setupQRHashNavigation() {
    // Handle initial hash
    handleQRHashNavigation();
    
    // Handle hash changes
    jQuery(window).on('hashchange', handleQRHashNavigation);
    
    // Update hash when tabs are clicked
    jQuery('#tab-generator, #tab-generator-mobile').on('click', function() {
        window.location.hash = '#generator-tab';
    });
    
    jQuery('#tab-qrcode, #tab-qrcode-mobile').on('click', function() {
        window.location.hash = '#qrcode-tab';
    });
}

// Restore last used tab
function restoreLastTab() {
    const savedTab = localStorage.getItem('gruener-generator-current-tab');
    
    // Check URL hash first
    const hash = window.location.hash;
    if (hash === '#qrcode-tab') {
        switchToTab('qrcode');
        return;
    }
    
    // Then check saved preference
    if (savedTab && (savedTab === 'generator' || savedTab === 'qrcode')) {
        switchToTab(savedTab);
    } else {
        // Default to generator
        switchToTab('generator');
    }
}

// Handle browser back/forward buttons
function setupQRBrowserNavigation() {
    window.addEventListener('popstate', function(event) {
        handleQRHashNavigation();
    });
}

// Keyboard shortcuts for tab navigation
function setupQRKeyboardShortcuts() {
    jQuery(document).on('keydown', function(e) {
        // Only handle if no input is focused
        if (jQuery('input:focus, textarea:focus, select:focus').length > 0) {
            return;
        }
        
        // Alt+1 for Generator, Alt+2 for QR Code
        if (e.altKey && e.key === '1') {
            e.preventDefault();
            switchToTab('generator');
            window.location.hash = '#generator-tab';
        } else if (e.altKey && e.key === '2') {
            e.preventDefault();
            switchToTab('qrcode');
            window.location.hash = '#qrcode-tab';
        }
    });
}

// Enhanced error handling for QR operations
function setupQRErrorHandling() {
    // Global error handler for QR-related errors
    window.addEventListener('error', function(event) {
        if (event.filename && event.filename.includes('qrcode')) {
            console.error('QR Code related error:', event.error);
            
            // Show user-friendly error message
            if (typeof showQRAlert === 'function') {
                showQRAlert('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.', 'error');
            }
        }
    });
    
    // Promise rejection handler for QR operations
    window.addEventListener('unhandledrejection', function(event) {
        if (event.reason && typeof event.reason === 'object' && event.reason.message) {
            if (event.reason.message.includes('QR') || event.reason.message.includes('qr')) {
                console.error('QR Code promise rejection:', event.reason);
                
                if (typeof showQRAlert === 'function') {
                    showQRAlert('Fehler bei der QR-Code Verarbeitung: ' + event.reason.message, 'error');
                }
                
                event.preventDefault(); // Prevent browser default handling
            }
        }
    });
}

// Initialize responsive behavior for QR interface
function setupQRResponsiveBehavior() {
    function handleResize() {
        const width = window.innerWidth;
        
        // Handle mobile/desktop tab visibility
        if (width >= 768) { // md breakpoint
            // Desktop - show desktop tabs, hide mobile tabs
            jQuery('.nav-tab').removeClass('hidden').show();
            jQuery('.nav-tab-mobile').addClass('hidden').hide();
        } else {
            // Mobile - show mobile tabs, hide desktop tabs
            jQuery('.nav-tab').addClass('hidden').hide();
            jQuery('.nav-tab-mobile').removeClass('hidden').show();
        }
        
        // Adjust QR preview size on mobile
        if (width < 640) { // sm breakpoint
            jQuery('.app-qr-preview-container canvas').css({
                'max-width': '280px',
                'max-height': '280px'
            });
        } else {
            jQuery('.app-qr-preview-container canvas').css({
                'max-width': '300px',
                'max-height': '300px'
            });
        }
    }
    
    // Initial setup
    handleResize();
    
    // Handle window resize
    jQuery(window).on('resize', debounce(handleResize, 250));
}

// Form validation helpers
function setupQRFormValidation() {
    // Real-time validation for URL fields
    jQuery(document).on('input', 'input[type="url"]', function() {
        const input = jQuery(this);
        const value = input.val().trim();
        
        if (value && !isValidURL(value)) {
            input.addClass('border-red-300 focus:border-red-500');
            
            // Show validation hint
            let hint = input.next('.validation-hint');
            if (hint.length === 0) {
                hint = jQuery('<div class="validation-hint text-xs text-red-600 mt-1">Gültige URL eingeben (z.B. https://www.beispiel.de)</div>');
                input.after(hint);
            }
        } else {
            input.removeClass('border-red-300 focus:border-red-500');
            input.next('.validation-hint').remove();
        }
    });
    
    // Real-time validation for email fields
    jQuery(document).on('input', 'input[type="email"]', function() {
        const input = jQuery(this);
        const value = input.val().trim();
        
        if (value && !isValidEmail(value)) {
            input.addClass('border-red-300 focus:border-red-500');
            
            let hint = input.next('.validation-hint');
            if (hint.length === 0) {
                hint = jQuery('<div class="validation-hint text-xs text-red-600 mt-1">Gültige E-Mail-Adresse eingeben</div>');
                input.after(hint);
            }
        } else {
            input.removeClass('border-red-300 focus:border-red-500');
            input.next('.validation-hint').remove();
        }
    });
}

// Utility: Email validation
function isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

// Enhanced initialization with error handling
function initializeQRHandlersWithErrorHandling() {
    try {
        initializeQRHandlers();
        setupQRHashNavigation();
        setupQRBrowserNavigation();
        setupQRKeyboardShortcuts();
        setupQRErrorHandling();
        setupQRResponsiveBehavior();
        setupQRFormValidation();
        
        // Restore last tab after short delay to ensure DOM is ready
        setTimeout(restoreLastTab, 100);
        
        console.log('QR Code Handlers fully initialized with enhancements');
        
    } catch (error) {
        console.error('Error initializing QR handlers:', error);
        
        // Fallback initialization
        setTimeout(() => {
            try {
                initializeQRHandlers();
                restoreLastTab();
            } catch (fallbackError) {
                console.error('Fallback QR handler initialization also failed:', fallbackError);
            }
        }, 500);
    }
}

// Initialize when document is ready
jQuery(document).ready(function() {
    initializeQRHandlersWithErrorHandling();
});

// === resources/js/quality.js ===
/**
 * QualityCheck — measurable rules for "is this image any good".
 *
 * Taste is not testable, but several of the things that make a sujet look
 * wrong are: elements that collide, type that breaks the protective margin,
 * text so small it will not survive the export, a composition that leans to
 * one side, accents that compete with each other.
 *
 * Every rule below is computed from the canvas object model alone — no second
 * rendering pass, no pixel sampling. That was a deliberate constraint: a
 * checker that needs its own render would be a second source of truth, and
 * this whole interface exists to avoid exactly that.
 *
 * Thresholds come from the three independent external reviews (Claude Opus 5,
 * GPT-5.6, Antigravity); where they disagreed the most permissive value is
 * used, so a warning means something.
 */
const QualityCheck = {
    /**
     * Rules that are brand requirements rather than judgement calls, and are
     * therefore reported as errors.
     */
    SEVERITY: { ERROR: "error", WARNING: "warning" },

    /**
     * Inspect the current canvas.
     *
     * @returns {{ok: boolean, errors: Array, warnings: Array, findings: Array}}
     */
    run() {
        const findings = [];
        const objects = this._subjects();

        this._checkMargin(objects, findings);
        this._checkOverlap(objects, findings);
        this._checkTextGaps(objects, findings);
        this._checkTextWidth(objects, findings);
        this._checkLegibility(objects, findings);
        this._checkAccents(objects, findings);
        this._checkBalance(objects, findings);
        this._checkTypeOnGreen(objects, findings);

        const errors = findings.filter((f) => f.severity === this.SEVERITY.ERROR);
        const warnings = findings.filter((f) => f.severity === this.SEVERITY.WARNING);
        return { ok: errors.length === 0, errors: errors, warnings: warnings, findings: findings };
    },

    /**
     * Everything a caller put on the canvas. The surface, the background photo
     * and the organisation logo are placed by the app at fixed positions, so
     * judging them would only produce findings nobody can act on.
     */
    _subjects() {
        const logo = this._logo();
        return canvas.getObjects().filter(function (o) {
            return o !== contentRect && o !== contentImage && o !== logo;
        });
    },

    _logo() {
        const images = canvas.getObjects().filter(function (o) {
            return o.type === "image" && o.selectable === false;
        });
        return images.length ? images[images.length - 1] : null;
    },

    _margin() {
        return 0.06 * Math.min(canvas.width, canvas.height);
    },

    /** Rotation-aware bounds — a rotated cross occupies more than its width. */
    _box(o) {
        return o.getBoundingRect(true, true);
    },

    _add(findings, severity, rule, message, detail) {
        findings.push({
            severity: severity,
            rule: rule,
            message: message,
            detail: detail || {},
        });
    },

    // ---------------------------------------------------------------- rules

    /**
     * The protective margin is a brand rule (M = 0.06 x short edge), not a
     * preference, so any breach is an error. Measured on all four edges and
     * after rotation.
     */
    _checkMargin(objects, findings) {
        const m = this._margin();
        const self = this;
        objects.forEach(function (o) {
            const b = self._box(o);
            const over = {
                left: m - b.left,
                top: m - b.top,
                right: b.left + b.width - (canvas.width - m),
                bottom: b.top + b.height - (canvas.height - m),
            };
            const worst = Math.max(over.left, over.top, over.right, over.bottom);
            if (worst > 0.5) {
                self._add(findings, self.SEVERITY.ERROR, "margin",
                    `Ein ${self._name(o)} ragt ${Math.round(worst)} px in die Schutzzone`,
                    { type: o.type, overshoot: Math.round(worst) });
            }
        });
    },

    /**
     * Elements that cover each other. A text sitting on its own green panel is
     * the intended case and is excluded, as is a Störer with its own text.
     */
    _checkOverlap(objects, findings) {
        const self = this;
        for (let i = 0; i < objects.length; i++) {
            for (let j = i + 1; j < objects.length; j++) {
                const a = objects[i];
                const b = objects[j];
                if (a._gatPanel === b || b._gatPanel === a) continue;

                const ratio = self._overlapRatio(self._box(a), self._box(b));
                if (ratio <= 0.01) continue;

                const bothText = a.type === "text" && b.type === "text";
                if (bothText || ratio > 0.35) {
                    self._add(findings, self.SEVERITY.ERROR, "overlap",
                        `${self._name(a)} und ${self._name(b)} überdecken sich zu ${Math.round(ratio * 100)} %`,
                        { ratio: ratio });
                } else if (ratio > 0.10) {
                    self._add(findings, self.SEVERITY.WARNING, "overlap",
                        `${self._name(a)} und ${self._name(b)} überdecken sich zu ${Math.round(ratio * 100)} %`,
                        { ratio: ratio });
                }
            }
        }
    },

    _overlapRatio(a, b) {
        const w = Math.min(a.left + a.width, b.left + b.width) - Math.max(a.left, b.left);
        const h = Math.min(a.top + a.height, b.top + b.height) - Math.max(a.top, b.top);
        if (w <= 0 || h <= 0) return 0;
        const smaller = Math.min(a.width * a.height, b.width * b.height);
        return smaller > 0 ? (w * h) / smaller : 0;
    },

    /**
     * Vertical distance between stacked text blocks, measured in multiples of
     * the LOWER block's line height. Absolute pixels say nothing: 40 px is
     * generous under a caption and invisible under a headline.
     */
    _checkTextGaps(objects, findings) {
        const texts = objects.filter(function (o) { return o.type === "text"; });
        const self = this;
        for (let i = 0; i < texts.length; i++) {
            for (let j = i + 1; j < texts.length; j++) {
                const a = self._box(texts[i]);
                const b = self._box(texts[j]);
                // Only blocks that sit above one another.
                const horizontalOverlap =
                    Math.min(a.left + a.width, b.left + b.width) - Math.max(a.left, b.left);
                if (horizontalOverlap <= 0) continue;

                const upper = a.top <= b.top ? a : b;
                const lowerObject = a.top <= b.top ? texts[j] : texts[i];
                const lower = a.top <= b.top ? b : a;
                const gap = lower.top - (upper.top + upper.height);
                if (gap < 0) continue; // overlap is reported by _checkOverlap

                const lineHeight = (lowerObject.fontSize || 1) *
                    (lowerObject.scaleY || 1) * (lowerObject.lineHeight || 1);
                const ratio = lineHeight > 0 ? gap / lineHeight : 0;
                if (ratio < 0.25) {
                    self._add(findings, self.SEVERITY.ERROR, "textGap",
                        `Zwei Textblöcke stehen zu eng (${ratio.toFixed(2)} Zeilenhöhen)`,
                        { ratio: ratio });
                } else if (ratio < 0.4) {
                    self._add(findings, self.SEVERITY.WARNING, "textGap",
                        `Zwei Textblöcke stehen eng (${ratio.toFixed(2)} Zeilenhöhen)`,
                        { ratio: ratio });
                }
            }
        }
    },

    /** Text that fills almost the whole width, or a lonely short last line. */
    _checkTextWidth(objects, findings) {
        const usable = canvas.width - 2 * this._margin();
        const self = this;
        objects.filter(function (o) { return o.type === "text"; }).forEach(function (o) {
            const share = self._box(o).width / usable;
            if (share > 1) {
                self._add(findings, self.SEVERITY.ERROR, "textWidth",
                    `Ein Textblock ist breiter als die nutzbare Fläche (${Math.round(share * 100)} %)`,
                    { share: share });
            }

            const lines = String(o.text || "").split("\n").filter(function (l) { return l.trim(); });

            // A long headline on ONE line does not wrap — it shrinks. At 80
            // characters an artikel_23 headline renders around 62 px on a
            // 3000 px wide image, which reads as body copy, not a headline.
            // The break has to be put in by hand with \n.
            if (lines.length === 1 && lines[0].length > 45) {
                self._add(findings, self.SEVERITY.WARNING, "headlineLength",
                    `Eine Schlagzeile mit ${lines[0].length} Zeichen steht auf einer Zeile ` +
                    `und schrumpft dadurch — mit \n umbrechen`,
                    { characters: lines[0].length });
            }

            if (lines.length > 3) {
                self._add(findings, self.SEVERITY.WARNING, "textLines",
                    `Ein Textblock hat ${lines.length} Zeilen — drei sind das Maximum`,
                    { lines: lines.length });
            }
            if (lines.length > 1) {
                const last = lines[lines.length - 1].trim();
                const longest = lines.reduce(function (a, b) { return a.length > b.length ? a : b; });
                if (last.split(/\s+/).length === 1 && last.length < longest.length * 0.35) {
                    self._add(findings, self.SEVERITY.WARNING, "orphan",
                        `Letzte Zeile „${last}" steht allein`, { last: last });
                }
            }
        });
    },

    /**
     * Type size in the EXPORTED file, not on the editing canvas. A headline
     * that reads fine at screen scale can be unreadable in an A6 flyer.
     */
    _checkLegibility(objects, findings) {
        const template = TemplateConstants.getCurrentTemplate();
        const dpi = (template && template.dpi) || 200;
        const factor = dpi / 72;
        const self = this;
        objects.filter(function (o) { return o.type === "text"; }).forEach(function (o) {
            const rendered = (o.fontSize || 0) * (o.scaleY || 1) * factor;
            if (rendered > 0 && rendered < 24 * factor) {
                self._add(findings, self.SEVERITY.WARNING, "legibility",
                    `Ein Text ist mit ${Math.round(rendered)} px im Export sehr klein`,
                    { renderedPx: Math.round(rendered) });
            }
        });
    },

    /**
     * The brand guide allows one Störer per sujet — "mehrere heben sich
     * gegenseitig auf". Accents beyond that compete instead of accentuating.
     */
    _checkAccents(objects, findings) {
        const magenta = String(AppConstants.COLORS.PINK_CIRCLE).toLowerCase();
        let stoerer = 0;
        let accents = 0;
        objects.forEach(function (o) {
            const fill = String(o.fill || "").toLowerCase();
            const isMagenta = fill === magenta || fill === "rgb(225,0,120)";
            if (isMagenta) stoerer++;
            if (isMagenta || o.type === "circle" || (o.type === "text" && o.fontStyle === "italic")) {
                accents++;
            }
        });
        if (stoerer > 1) {
            this._add(findings, this.SEVERITY.ERROR, "accents",
                `${stoerer} Störer auf einem Sujet — laut CD ist einer erlaubt`,
                { stoerer: stoerer });
        }
        if (accents > 2) {
            this._add(findings, this.SEVERITY.WARNING, "accents",
                `${accents} konkurrierende Akzente`, { accents: accents });
        }
    },

    /**
     * Where the visual weight sits. Vertical asymmetry is normal — the logo is
     * at the bottom — so only the horizontal axis is judged, and only loosely.
     */
    _checkBalance(objects, findings) {
        if (objects.length < 2) return;
        let mass = 0;
        let sum = 0;
        const self = this;
        objects.forEach(function (o) {
            const b = self._box(o);
            const weight = b.width * b.height * (o.type === "text" ? 1.5 : 1);
            mass += weight;
            sum += weight * (b.left + b.width / 2);
        });
        if (mass <= 0) return;
        const offset = (sum / mass - canvas.width / 2) / canvas.width;
        if (Math.abs(offset) > 0.18) {
            this._add(findings, this.SEVERITY.WARNING, "balance",
                `Die Komposition hängt ${offset > 0 ? "nach rechts" : "nach links"} ` +
                `(${Math.round(Math.abs(offset) * 100)} % aus der Mitte)`,
                { offset: offset });
        }
    },

    /**
     * The central brand rule: type sits on green. Without a background photo
     * the whole surface is green and nothing can go wrong. With one, every
     * text needs a panel behind it.
     */
    _checkTypeOnGreen(objects, findings) {
        if (!contentImage) return;
        const self = this;
        const panels = objects.filter(function (o) {
            return o.type === "rect" &&
                String(o.fill || "").toLowerCase() ===
                String(AppConstants.COLORS.BACKGROUND_SECONDARY).toLowerCase();
        });

        objects.filter(function (o) { return o.type === "text"; }).forEach(function (text) {
            const box = self._box(text);
            const covered = panels.some(function (panel) {
                const p = self._box(panel);
                return p.left <= box.left + 1 && p.top <= box.top + 1 &&
                    p.left + p.width >= box.left + box.width - 1 &&
                    p.top + p.height >= box.top + box.height - 1;
            });
            if (!covered) {
                self._add(findings, self.SEVERITY.ERROR, "typeOnGreen",
                    "Text liegt auf dem Foto ohne grüne Fläche darunter — " +
                    "das CD verlangt Typografie auf Grün",
                    { text: String(text.text || "").slice(0, 40) });
            }
        });
    },

    _name(o) {
        if (o.type === "text") return `Text „${String(o.text || "").slice(0, 20)}"`;
        if (o.type === "circle") return "Störer";
        if (o.type === "rect") return "Fläche";
        return "Bild";
    },
};

if (typeof window !== "undefined") {
    window.QualityCheck = QualityCheck;
}


// === resources/js/api.js ===
/**
 * Bildgenerator — programmatic entry point (window.Bildgenerator).
 *
 * Covers everything the two tools on this page can do: the image generator
 * (template, logo, background, text, shapes, images, QR on the image, export)
 * and the standalone QR generator.
 *
 * WHY THIS EXISTS
 *
 * The wizard is built for people. State lives in form controls, steps hide and
 * show one another, sections start collapsed, and the logo select sits behind a
 * search box that keeps its own label. Driving that from outside — a headless
 * browser, an AI assistant with a browser sandbox — means knowing a handful of
 * things the markup does not reveal:
 *
 *   1. #meme-input is the background-image FILE upload, not a text field.
 *   2. Text goes into #text and is committed with #add-text — and the section
 *      holding both starts collapsed, as do the element and QR sections.
 *   3. #logo-selection is a hidden <select> behind a search box that fires
 *      'change' itself; dispatching another one adds the logo twice.
 *   4. CanvasUtils.exportCanvas() resolves to { dataURL, actualDPI, targetDPI },
 *      not to a string.
 *   5. #add-circle does not add a circle — it CROPS the selected object into
 *      one. The circle you can add is #add-pink-circle.
 *   6. #add-image takes a File, so a caller holding a data: URL has to build
 *      one before the handler will look at it.
 *
 * NOT A SECOND RENDERER. Every method drives the same objects and handlers the
 * UI drives, so there is exactly one rendering path and the visual regression
 * suite still covers it. The gain is coupling: when the wizard changes, the
 * breakage surfaces in e2e/api.spec.js instead of in somebody else's script.
 */
const Bildgenerator = {
    VERSION: 15,

    /** The object the most recent add* call put on the canvas. */
    _lastAdded: null,

    // ---------------------------------------------------------------- lookup

    /** Template names accepted by setTemplate / render({ template }). */
    templates() {
        return Object.keys(TemplateConstants.TEMPLATES);
    },

    /** Organisation values accepted by setLogo / render({ logo }). */
    logos() {
        return this._optionValues("#logo-selection");
    },

    /**
     * Every remaining choice, read from the live controls rather than hardcoded
     * — a new colour in the markup shows up here without touching this file.
     */
    options() {
        return {
            textColors: this._optionValues("#text-color"),
            fontStyles: AppConstants.FONTS.OPTIONS.map(function (o) { return o.id; }),
            lineHeights: this._optionValues("#line-height"),
            alignments: ["left", "center", "right"],
            shapes: ["pinkCircle", "cross", "panel"],
            clipSizes: this._optionValues("#circle-radius"),
            qrColorsOnImage: this._optionValues("#qr-color"),
            qrColors: this._optionValues("#qr-color-select"),
            qrBackgrounds: this._optionValues("#qr-background-select"),
            formats: ["png", "jpeg"],
            positions: [
                "top-left", "top", "top-right",
                "left", "center", "right",
                "bottom-left", "bottom", "bottom-right",
            ],
        };
    },

    // ----------------------------------------------------------- step 1: format

    /**
     * Select a template and wait until the canvas has been rebuilt at its
     * dimensions. The change replaces the canvas and discards everything on it,
     * so this belongs first.
     */
    async setTemplate(name) {
        const template = TemplateConstants.getTemplate(name);
        if (!template) {
            throw new Error(
                `Unknown template "${name}". Available: ${this.templates().join(", ")}`
            );
        }
        this._lastAdded = null; // the canvas is discarded and rebuilt
        jQuery("#canvas-template").val(name).trigger("change");
        await this._waitFor(
            () => canvas && canvas.width === template.width && canvas.height === template.height,
            `canvas to be rebuilt at ${template.width}x${template.height}`
        );
        // Matching dimensions are not the end of it: replaceCanvas() re-adds the
        // logo asynchronously afterwards. Returning here would let the next call
        // race that load and stack a second logo on the first.
        await this._waitUntilQuiet();
        return this;
    },

    /** Turn the automatic organisation logo on or off. */
    async setLogoEnabled(enabled) {
        LogoState.setLogoEnabled(Boolean(enabled));
        // addLogo() reads the state itself: it removes the placed logo when the
        // feature is off and re-adds it when it is on.
        addLogo();
        await this._waitUntilQuiet();
        return this;
    },

    /**
     * Whether the region name on the logo bar is transparent (a knockout) or
     * filled with the brand green.
     *
     * Transparent shows whatever lies behind the logo through the letters. On
     * the plain green canvas that is the green and looks right; over a
     * background photo the photo shows through and the name becomes
     * unreadable. setBackground() therefore turns this off by itself the
     * first time a photo arrives — call this afterwards to override.
     */
    async setLogoKnockout(enabled) {
        LogoState.setKnockoutEnabled(Boolean(enabled));
        if (LogoState.isLogoEnabled()) {
            addLogo();
            await this._waitUntilQuiet();
        }
        return this;
    },

    /**
     * Place an organisation logo. Sets the value and fires exactly one change
     * event — the handler behind it adds the logo, so a second event would
     * stack a duplicate on the first.
     */
    async setLogo(value) {
        if (!this.logos().includes(value)) {
            throw new Error(`Unknown logo "${value}". See Bildgenerator.logos().`);
        }
        LogoState.setLogoEnabled(true);
        jQuery("#logo-selection").val(value).trigger("change");
        // addLogo() loads the logo, bakes the knockout and loads the result
        // again — two async hops with no completion signal. window.logo is no
        // help: main.js snapshots it once during init and never syncs it.
        await this._waitUntilQuiet();
        return this;
    },

    // ------------------------------------------------------- step 2: background

    /**
     * Set the background image from a data: URL or an absolute URL. Goes
     * straight to processMeme() rather than through the #meme-input file
     * picker, so callers never have to synthesise a File.
     *
     * The photo is cover-scaled, so one axis overflows and gets cropped.
     * `focusX` / `focusY` decide which part survives: 0.5 is the centre and
     * the long-standing default, 0 pins the left or top edge, 1 the right or
     * bottom. A portrait in a landscape template usually wants focusY near 0
     * so the face is not cut off — centring is a guess, and often the wrong
     * one.
     *
     * @param {string} url
     * @param {Object} [options]
     * @param {number} [options.focusX=0.5]
     * @param {number} [options.focusY=0.5]
     */
    async setBackground(url, options) {
        if (typeof url !== "string" || !url) {
            throw new Error("setBackground() needs a data: URL or an absolute URL");
        }
        const opts = options || {};
        const focus = function (v) {
            if (v === undefined) return undefined;
            if (typeof v !== "number" || v < 0 || v > 1) {
                throw new Error("setBackground() focus values run from 0 to 1");
            }
            return v;
        };
        // Set before the image loads: positionBackgroundImage() reads it.
        CanvasUtils.setBackgroundFocus(focus(opts.focusX), focus(opts.focusY));

        // Turn a remote URL into a data: URL first.
        //
        // A cross-origin image loaded straight into the canvas is blocked by
        // CORS, and fabric still calls its callback — with an image that has
        // no dimensions. processMeme() then assigns it, so every check for
        // "is there a background now" says yes and the export comes back a
        // plausible size with the photo simply absent. A field report of the
        // first production use caught exactly that: three images, each one a
        // blank green surface, and nothing anywhere said so.
        //
        // Fetching first either works — then the image really is embedded —
        // or fails loudly here, where the caller can act on it.
        const source = url.startsWith("data:") ? url : await this._asDataURL(url);

        // Wait for a DIFFERENT image, not merely for one to exist.
        // contentImage is a one-shot latch: replaceCanvas() and resetWizard()
        // dispose the canvas without clearing it, so after a template change
        // it is still truthy and a replacement would return immediately —
        // before the new photo had loaded.
        const previous = contentImage;
        processMeme({ url: source });
        await this._waitFor(
            () => !!contentImage && contentImage !== previous,
            "background image to load"
        );
        // fabric hands back an image with no dimensions when the load failed.
        if (!contentImage.width || !contentImage.height) {
            throw new Error(
                "Das Hintergrundbild wurde angenommen, enthält aber keine Bilddaten. " +
                "Bei einer fremden URL liegt das meist an CORS."
            );
        }
        await this._waitUntilQuiet();
        return this;
    },

    /**
     * Fetch a remote image and return it as a data: URL.
     *
     * Throws with the reason rather than letting a blocked request end as a
     * silently empty background — the failure mode this whole interface
     * exists to prevent, applied to images instead of fonts.
     */
    async _asDataURL(url) {
        let response;
        try {
            response = await fetch(url, { mode: "cors" });
        } catch (error) {
            throw new Error(
                `Das Bild von ${url} konnte nicht geladen werden (${error.message}). ` +
                `Fremde Server müssen CORS erlauben — übergib das Bild sonst als data:-URL.`
            );
        }
        if (!response.ok) {
            throw new Error(`Das Bild von ${url} antwortete mit HTTP ${response.status}.`);
        }
        const blob = await response.blob();
        if (!String(blob.type).startsWith("image/")) {
            throw new Error(`${url} lieferte ${blob.type || "keinen Bildtyp"}, kein Bild.`);
        }
        return await new Promise(function (resolve, reject) {
            const reader = new FileReader();
            reader.onload = function () { resolve(reader.result); };
            reader.onerror = function () { reject(new Error(`${url} ließ sich nicht lesen.`)); };
            reader.readAsDataURL(blob);
        });
    },

    // ----------------------------------------------------------- step 3: design

    /**
     * Add a headline.
     *
     * @param {string} text
     * @param {Object} [options]
     * @param {string} [options.color]      value from options().textColors
     * @param {string} [options.fontStyle]  id from options().fontStyles
     * @param {string} [options.align]      left | center | right — how the lines
     *                                        sit inside the text block. To move
     *                                        the block itself, use place().
     * @param {string} [options.lineHeight] value from options().lineHeights
     * @param {number} [options.shadow]     shadow depth
     * @param {boolean|Object} [options.panel] put a green surface behind this
     *                                        text — required by the brand rule
     *                                        when a background photo is set
     */
    async addText(text, options) {
        const opts = options || {};
        const validation = ValidationUtils.validateTextInput(text);
        if (!validation.isValid) {
            throw new Error(`Invalid text: ${validation.error}`);
        }

        this._openSection("text-section");

        // Set EVERY control, not only the ones the caller named. The handler
        // reads all of them, so an omitted field would otherwise inherit
        // whatever the last call left behind: one yellow right-aligned text
        // and every later one is yellow and right-aligned too. The same spec
        // has to produce the same text.
        jQuery("#text-color").val(opts.color || this._optionValues("#text-color")[0]);
        jQuery("#font-style-select").val(opts.fontStyle || AppConstants.FONTS.OPTIONS[0].id);
        jQuery(`input[name="align"]#${opts.align || "center"}`).prop("checked", true);
        jQuery("#line-height").val(opts.lineHeight || this._optionValues("#line-height")[0]);
        jQuery("#shadow-depth").val(typeof opts.shadow === "number" ? opts.shadow : 0);

        await this._addTracking(function () {
            jQuery("#text").val(text);
            jQuery("#add-text").trigger("click");
        }, "text object to be added to the canvas");

        // Convenience for the brand rule: put a green surface under this text,
        // sized to it. Doing it here rather than leaving the arithmetic to the
        // caller is the difference between a rule that gets followed and one
        // that gets skipped.
        if (opts.panel) {
            await this._panelBehind(this.lastAdded(), opts.panel);
        }
        return this;
    },

    /**
     * Add a decorative element: 'pinkCircle' (magenta circle) or 'cross' (the
     * Wahlkreuz). Note that the UI's circle button does something else
     * entirely — see clipToCircle().
     */
    async addShape(kind) {
        const buttons = {
            pinkCircle: "#add-pink-circle",
            cross: "#add-cross",
            panel: "#add-panel",
        };
        const selector = buttons[kind];
        if (!selector) {
            throw new Error(
                `Unknown shape "${kind}". Available: ${Object.keys(buttons).join(", ")}`
            );
        }
        this._openSection("elements-section");
        await this._addTracking(function () {
            jQuery(selector).trigger("click");
        }, `shape "${kind}" to be added`);
        return this;
    },

    /**
     * Put a green surface behind one text, sized to it with padding, and keep
     * the text on top. The padding is a share of the text's own height, so it
     * scales with the type rather than with the canvas.
     */
    async _panelBehind(text, options) {
        const opts = typeof options === "object" && options !== null ? options : {};
        await this.addPanel();
        const panel = this.lastAdded();

        // Bind the two together. Without this a later place() moves the text
        // and leaves the surface where it was — the text ends up half off its
        // own panel, which is worse than having no panel at all.
        text._gatPanel = panel;
        text._gatPanelPadding = typeof opts.padding === "number" ? opts.padding : 0.35;
        this._refitPanel(text);

        // The text stays the tracked element: a caller writing
        // addText(t, { panel: true }) then place(...) means the text.
        this._lastAdded = text;
        return this;
    },

    /**
     * Re-size and re-centre a text's green surface on the text. Called after
     * anything that moves or scales the text, so the two never come apart.
     */
    _refitPanel(text) {
        const panel = text && text._gatPanel;
        if (!panel || !canvas.getObjects().includes(panel)) return;

        const padding = text._gatPanelPadding || 0.35;
        const width = text.getScaledWidth();
        const height = text.getScaledHeight();

        panel.set({
            scaleX: (width + height * padding * 2) / panel.width,
            scaleY: (height + height * padding) / panel.height,
        });
        panel._gatBaseScale = panel.scaleX;
        panel.setCoords();
        panel.set({
            left: text.left + width / 2 - panel.getScaledWidth() / 2,
            top: text.top + height / 2 - panel.getScaledHeight() / 2,
        });
        panel.setCoords();

        canvas.bringToFront(text);
        CanvasUtils.bringLogoToFront();
        canvas.renderAll();
    },

    /**
     * Add a green surface for type to sit on.
     *
     * The brand guide requires typography to be combined with green. Without
     * a photo the whole canvas already is green, so this is only needed over
     * a background image — but there it is not optional: white text laid
     * straight over a photograph breaks the rule, however legible it looks.
     *
     * The panel lands behind every text already on the canvas.
     *
     * @param {Object} [options]
     * @param {number} [options.width=0.8]  share of the usable width
     * @param {number} [options.height=0.28] share of the usable height
     */
    async addPanel(options) {
        const opts = options || {};
        this._openSection("elements-section");
        const panel = await this._addTracking(function () {
            jQuery("#add-panel").trigger("click");
        }, "green panel to be added");

        if (typeof opts.width === "number" || typeof opts.height === "number") {
            const width = (typeof opts.width === "number" ? opts.width : 0.8) * contentRect.width;
            const height = (typeof opts.height === "number" ? opts.height : 0.28) * contentRect.height;
            panel.set({ scaleX: width / panel.width, scaleY: height / panel.height });
            panel._gatBaseScale = panel.scaleX;
            panel.setCoords();
            canvas.centerObject(panel);
            canvas.renderAll();
        }
        return this;
    },

    /**
     * Add a free-standing image on top of the design (not the background).
     * The handler reads a File from the input, so the URL is wrapped in one
     * here rather than bypassing the app's own validation.
     */
    async addImage(url) {
        if (typeof url !== "string" || !url) {
            throw new Error("addImage() needs a data: URL or an absolute URL");
        }
        this._openSection("elements-section");

        const response = await fetch(url);
        const blob = await response.blob();
        const file = new File([blob], "bild.png", { type: blob.type || "image/png" });
        const transfer = new DataTransfer();
        transfer.items.add(file);

        const input = document.getElementById("add-image");
        input.files = transfer.files;

        await this._addTracking(function () {
            jQuery(input).trigger("input");
        }, "image to be added to the canvas");
        return this;
    },

    /**
     * Crop the most recently added object into a circle. This is what the UI
     * labels "Kreis": it does not add anything, it clips what is selected.
     *
     * @param {string|number} [size] value from options().clipSizes
     *                               (2 = gross 90%, 3 = mittel 70%, 4 = klein 50%)
     */
    async clipToCircle(size) {
        this._openSection("elements-section");
        if (this._lastAdded && canvas.getObjects().includes(this._lastAdded)) {
            canvas.setActiveObject(this._lastAdded);
        }
        if (size) jQuery("#circle-radius").val(String(size));
        jQuery("#add-circle").trigger("click");
        await this._waitUntilQuiet();
        return this;
    },

    /**
     * Place a QR code on the image.
     *
     * @param {Object} spec
     * @param {string} spec.text    the encoded content
     * @param {string} [spec.color] value from options().qrColorsOnImage
     */
    async addQRCode(spec) {
        const opts = spec || {};
        const payload = opts.text || this.qrPayload(opts);
        if (!payload) {
            throw new Error(
                "addQRCode() needs { text }, or a { type } of " +
                "text | url | email | vcard with its fields"
            );
        }

        this._openSection("qr-section", "#show-qr-section");

        if (opts.color) jQuery("#qr-color").val(opts.color);
        await this._addTracking(function () {
            jQuery("#qr-text").val(payload);
            jQuery("#add-qr-code").trigger("click");
        }, "QR code to be added to the canvas");
        return this;
    },

    /**
     * Move the most recently added object to a named position.
     *
     * Everything the wizard adds lands centred — text, QR, shapes and images
     * all pile up on the same spot. Dragging them apart is what a person does
     * next; this is that drag.
     *
     * The target box is the canvas inset by the brand's protective margin
     * M = 0.06 x short edge, which no element may enter. When an organisation
     * logo is present, the bottom row stops above it instead of overlapping.
     *
     * @param {string} position from options().positions
     * @param {Object} [options]
     * @param {fabric.Object} [options.target] object to move (default: the last one)
     */
    async place(position, options) {
        const opts = options || {};
        const objects = canvas.getObjects();
        const target = opts.target !== undefined
            ? this._resolveTarget(opts.target)
            : this._lastAdded;
        if (!target || !objects.includes(target)) {
            throw new Error("place() needs an element that was added first");
        }
        this._assertEditable(target, "place");

        const margin = this.protectiveMargin();
        const width = target.getScaledWidth();
        const height = target.getScaledHeight();

        // Free positioning: { x, y } as fractions of the canvas, 0 = left/top
        // edge of the usable area, 1 = right/bottom. Still clamped to the
        // protective margin, because that is a brand rule and not a default.
        // Centre on another element — the Stoerer pattern from the brand guide
        // is a magenta circle with a short text on it, which needs exactly
        // this and nothing else.
        if (position && typeof position === "object" && position.onto !== undefined) {
            const host = this._resolveTarget(position.onto);
            if (host === target) throw new Error("place({ onto }) needs a different element");
            target.set({
                left: host.left + (host.getScaledWidth() - width) / 2,
                top: host.top + (host.getScaledHeight() - height) / 2,
            });
            target.setCoords();
            this._refitPanel(target);
            canvas.renderAll();
            return this;
        }

        if (position && typeof position === "object") {
            const inRange = function (v) {
                return v === undefined || (typeof v === "number" && v >= 0 && v <= 1);
            };
            if (!inRange(position.x) || !inRange(position.y)) {
                throw new Error(
                    "place({ x, y }) takes fractions between 0 and 1; the " +
                    "protective margin is not optional"
                );
            }
            const usableWidth = canvas.width - 2 * margin - width;
            const usableHeight = canvas.height - 2 * margin - height;
            target.set({
                left: margin + Math.max(0, usableWidth) * (position.x || 0),
                top: margin + Math.max(0, usableHeight) * (position.y || 0),
            });
            target.setCoords();
            this._refitPanel(target);
            canvas.renderAll();
            return this;
        }

        const valid = this.options().positions;
        if (!valid.includes(position)) {
            throw new Error(
                `Unknown position "${position}". Available: ${valid.join(", ")}, ` +
                `or { x, y } with values between 0 and 1`
            );
        }

        // The logo is placed by the app at the bottom; keep clear of it.
        let bottomLimit = canvas.height - margin;
        const placedLogo = this._logoObject();
        if (placedLogo && placedLogo !== target) {
            bottomLimit = Math.min(bottomLimit, placedLogo.top - margin);
        }

        const [row, column] = this._resolvePosition(position);
        let left = {
            start: margin,
            centre: (canvas.width - width) / 2,
            end: canvas.width - margin - width,
        }[column];
        let top = {
            start: margin,
            centre: (canvas.height - height) / 2,
            end: bottomLimit - height,
        }[row];

        // An element wider or taller than the usable area would otherwise be
        // pushed off the canvas entirely. Clamp instead — objects() still
        // reports insideMargin false, so the caller can see it does not fit.
        left = Math.max(0, left);
        top = Math.max(0, top);

        target.set({ left: left, top: top });
        target.setCoords();
        this._refitPanel(target);
        canvas.renderAll();
        return this;
    },

    /**
     * Scale an element. The wizard exposes this as a slider on the selected
     * object; there is no font-size field, so this is how text gets bigger or
     * smaller too.
     *
     * The factor is RELATIVE TO THE SIZE IT WAS ADDED AT: 1 leaves it as the
     * app placed it, 0.5 is half, 2 is double.
     *
     * Relative on purpose. The app inserts a text at whatever scale makes it
     * fit 80 % of the canvas — for a longer headline that is around 0.1. An
     * absolute resize(0.35) would therefore make it roughly three times WIDER
     * than the canvas, the opposite of what the number suggests.
     *
     * @param {number} factor relative to the inserted size, must be positive
     * @param {Object} [options]
     * @param {fabric.Object} [options.target] default: the last element added
     */
    async resize(factor, options) {
        const opts = options || {};
        const target = opts.target !== undefined
            ? this._resolveTarget(opts.target)
            : this.lastAdded();
        if (!target) throw new Error("resize() needs an element that was added first");
        this._assertEditable(target, "resize");
        if (typeof factor !== "number" || !(factor > 0)) {
            throw new Error("resize() needs a positive number");
        }
        const base = target._gatBaseScale || target.scaleX || 1;
        target.scale(base * factor).setCoords();
        this._refitPanel(target);
        canvas.renderAll();
        return this;
    },

    /**
     * Scale an element so it fits inside another one, leaving a little air.
     * Pairs with place({ onto }) for the Stoerer pattern.
     *
     * @param {number|Object} host   index from objects(), or an element
     * @param {number} [ratio=0.6]   share of the host's width to occupy
     */
    async fitInto(host, ratio) {
        const target = this.lastAdded();
        if (!target) throw new Error("fitInto() needs an element that was added first");
        const into = this._resolveTarget(host);
        const share = typeof ratio === "number" ? ratio : 0.6;
        const current = target.getScaledWidth();
        if (!current) throw new Error("fitInto() cannot measure the element");
        const wanted = into.getScaledWidth() * share;
        target.scale((target.scaleX || 1) * (wanted / current)).setCoords();
        canvas.renderAll();
        return this;
    },

    /**
     * Rotate an element. The wizard offers this as the rotation handle on a
     * selected object, and while dragging it snaps to 0, 90, 180 and 270
     * degrees within a 2 degree tolerance. Programmatic rotation sets the
     * angle exactly and does not snap — pass the right angle.
     *
     * @param {number} degrees clockwise, absolute (not relative)
     * @param {Object} [options]
     * @param {fabric.Object} [options.target] default: the last element added
     */
    async rotate(degrees, options) {
        const opts = options || {};
        const target = opts.target !== undefined
            ? this._resolveTarget(opts.target)
            : this.lastAdded();
        if (!target) throw new Error("rotate() needs an element that was added first");
        this._assertEditable(target, "rotate");
        if (typeof degrees !== "number" || !isFinite(degrees)) {
            throw new Error("rotate() needs a number of degrees");
        }
        target.rotate(((degrees % 360) + 360) % 360);
        target.setCoords();
        this._refitPanel(target);
        canvas.renderAll();
        return this;
    },

    /**
     * The brand's protective margin in canvas pixels: M = 0.06 x short edge.
     * No element may fall inside it. Exposed so callers can reason about
     * placement themselves.
     */
    protectiveMargin() {
        return 0.06 * Math.min(canvas.width, canvas.height);
    },

    /**
     * The object the most recent add* call put on the canvas.
     *
     * Deliberately public: callers that inspect the canvas themselves hit the
     * same trap the facade had to solve. canvas.getObjects() ends with the
     * organisation LOGO, not with the element just added — addLogo() and the
     * QR handler both call bringLogoToFront(). Reading the last entry gives
     * you the logo.
     */
    lastAdded() {
        const objects = canvas.getObjects();
        return this._lastAdded && objects.includes(this._lastAdded) ? this._lastAdded : null;
    },

    /**
     * The placed organisation logo, or null. addLogo() flattens it into a
     * non-selectable image and keeps it in front, so it is the last
     * non-selectable image on the canvas.
     */
    _logoObject() {
        const images = canvas.getObjects().filter(function (o) {
            return o.type === "image" && o.selectable === false;
        });
        return images.length ? images[images.length - 1] : null;
    },

    _resolvePosition(position) {
        const rows = { top: "start", bottom: "end" };
        const columns = { left: "start", right: "end" };
        const parts = position.split("-");
        let row = "centre";
        let column = "centre";
        for (const part of parts) {
            if (rows[part]) row = rows[part];
            else if (columns[part]) column = columns[part];
        }
        return [row, column];
    },

    // ------------------------------------------------- inspect and edit what is there

    /**
     * Everything currently on the canvas, in stacking order (last = in front).
     *
     * Without this a caller is blind: it can add elements but never check what
     * it built, and it cannot address anything but the most recent element.
     * `index` is what select(), remove(), update() and the *target* options
     * take.
     */
    objects() {
        const margin = this.protectiveMargin();
        const logoObject = this._logoObject();
        return canvas.getObjects().map(function (o, index) {
            return {
                index: index,
                type: o.type,
                // Only the ONE rect the app created as the canvas surface is
                // "canvas". Any other rect is a green panel a caller added,
                // and must not inherit the canvas's protected status.
                role: o === logoObject ? "logo"
                    : o === contentImage ? "background"
                    : o === contentRect ? "canvas"
                    : o.type === "rect" ? "panel"
                    : o.type,
                text: o.type === "text" ? o.text : undefined,
                color: o.fill,
                left: Math.round(o.left),
                top: Math.round(o.top),
                width: Math.round(o.getScaledWidth()),
                height: Math.round(o.getScaledHeight()),
                angle: Math.round(o.angle || 0),
                scale: Math.round((o.scaleX || 1) * 1000) / 1000,
                // All four edges, not just the origin. An element wider or
                // taller than the usable area sits at the margin on the left
                // and still runs off the right — reporting that as "inside"
                // is worse than not reporting at all.
                insideMargin:
                    o.left >= margin - 0.5 &&
                    o.top >= margin - 0.5 &&
                    o.left + o.getScaledWidth() <= canvas.width - margin + 0.5 &&
                    o.top + o.getScaledHeight() <= canvas.height - margin + 0.5,
                editable: o !== logoObject && o !== contentImage && o.type !== "rect",
            };
        });
    },

    /**
     * Make an element the active one and the target of the next edit.
     * Accepts the index from objects(), or a fabric object.
     */
    select(which) {
        const target = this._resolveTarget(which);
        canvas.setActiveObject(target);
        canvas.renderAll();
        this._lastAdded = target;
        return this;
    },

    /**
     * Change properties of an element that is already on the canvas.
     *
     * The wizard does this by selecting an object and touching the colour,
     * alignment, line-height or shadow control; the handlers write straight
     * onto the active object. Without this a caller has to delete and rebuild
     * an element to change its colour.
     *
     * @param {Object} changes  text, color, align, lineHeight, shadow, fontStyle
     * @param {Object} [options]
     * @param {number|Object} [options.target] index from objects(), or object
     */
    async update(changes, options) {
        const opts = options || {};
        const target = this._resolveTarget(
            opts.target !== undefined ? opts.target : this.lastAdded()
        );
        this._assertEditable(target, "update");
        const spec = changes || {};

        if (spec.text !== undefined) {
            const validation = ValidationUtils.validateTextInput(spec.text);
            if (!validation.isValid) throw new Error(`Invalid text: ${validation.error}`);
            target.set("text", spec.text);
        }
        if (spec.color !== undefined) target.set("fill", spec.color);
        if (spec.align !== undefined) target.set("textAlign", spec.align);
        if (spec.lineHeight !== undefined) target.set("lineHeight", parseFloat(spec.lineHeight));
        if (spec.shadow !== undefined) target.set("shadow", createShadow("#000000", spec.shadow));
        if (spec.fontStyle !== undefined) {
            const option = AppConstants.FONTS.OPTIONS.find(function (o) {
                return o.id === spec.fontStyle;
            });
            if (!option) {
                throw new Error(
                    `Unknown fontStyle "${spec.fontStyle}". ` +
                    `Available: ${AppConstants.FONTS.OPTIONS.map((o) => o.id).join(", ")}`
                );
            }
            target.set({
                fontFamily: option.family,
                fontWeight: option.weight,
                fontStyle: option.style,
            });
        }

        // Text caches its metrics, so a changed string or font needs a
        // re-measure or the old dimensions stick.
        if (typeof target.initDimensions === "function") {
            target.dirty = true;
            target.initDimensions();
        }
        target.setCoords();
        this._refitPanel(target);
        canvas.renderAll();
        return this;
    },

    /**
     * Remove an element. The organisation logo and the background image are
     * protected, exactly as the wizard's delete button protects them.
     */
    async remove(which) {
        const target = this._resolveTarget(which !== undefined ? which : this.lastAdded());
        if (target === this._logoObject()) {
            throw new Error("The organisation logo cannot be removed; use setLogoEnabled(false)");
        }
        if (target === contentImage) {
            throw new Error("The background image cannot be removed; set a different one");
        }
        if (target === contentRect) {
            throw new Error("The canvas surface cannot be removed");
        }
        canvas.remove(target);
        if (this._lastAdded === target) this._lastAdded = null;
        canvas.renderAll();
        return this;
    },

    /**
     * Move an element to the front of the stack.
     *
     * The UI's own handler guards contentImage and contentRect
     * (event-handlers.js) — bringing the canvas surface to the front would
     * cover the entire image in flat green.
     */
    async bringToFront(which) {
        const target = this._resolveTarget(which !== undefined ? which : this.lastAdded());
        this._assertEditable(target, "bringToFront");
        canvas.bringToFront(target);
        // The organisation logo is meant to stay on top.
        CanvasUtils.bringLogoToFront();
        canvas.renderAll();
        return this;
    },

    /**
     * Refuse to move, scale, rotate or restyle the things objects() reports as
     * editable:false — the plain canvas rectangle, the background photo and
     * the organisation logo.
     *
     * The UI protects these through its own handlers; the facade advertised
     * them as off-limits in objects() but let every other method touch them.
     * Dragging the logo out of its position or scaling the background breaks
     * the brand, and the caller would have no way to put it back.
     */
    _assertEditable(target, what) {
        if (target === this._logoObject()) {
            throw new Error(
                `${what}() will not touch the organisation logo — ` +
                `use setLogoEnabled(false) or setLogo() instead`
            );
        }
        if (target === contentImage) {
            throw new Error(
                `${what}() will not touch the background image — ` +
                `use setBackground() to replace it`
            );
        }
        if (target === contentRect) {
            throw new Error(`${what}() will not touch the canvas surface`);
        }
    },

    /**
     * Move an element one step back, or all the way behind everything except
     * the canvas surface and the background photo.
     *
     * Fabric has both; the app wires neither, and the UI has only a
     * bring-to-front button. Without them a caller can never undo an overlap
     * — every new element goes on top and stays there.
     */
    async sendToBack(which) {
        const target = this._resolveTarget(which !== undefined ? which : this.lastAdded());
        this._assertEditable(target, "sendToBack");
        canvas.sendToBack(target);
        // Never behind the surface or the photo — that would hide it entirely.
        if (contentRect) canvas.sendToBack(contentRect);
        if (contentImage) canvas.sendToBack(contentImage);
        canvas.renderAll();
        return this;
    },

    /** Move an element one step back in the stack. */
    async sendBackwards(which) {
        const target = this._resolveTarget(which !== undefined ? which : this.lastAdded());
        this._assertEditable(target, "sendBackwards");
        canvas.sendBackwards(target);
        if (contentRect) canvas.sendToBack(contentRect);
        if (contentImage) canvas.sendToBack(contentImage);
        canvas.renderAll();
        return this;
    },

    /**
     * Add the photo credit or the AI disclosure as a small line inside the
     * margin. Both are obligations rather than design choices, so they get a
     * fixed relative size instead of the automatic 80 % fit — a credit as
     * large as a headline is not a credit.
     *
     * @param {string} text
     * @param {Object} [options]
     * @param {string} [options.position='bottom-left'] from options().positions
     * @param {number} [options.size=0.018] share of the short edge
     */
    async addCredit(text, options) {
        const opts = options || {};
        await this.addText(text, { color: "#FFFFFF", align: "left" });
        const credit = this.lastAdded();

        const shortEdge = Math.min(canvas.width, canvas.height);
        const wanted = shortEdge * (typeof opts.size === "number" ? opts.size : 0.018);
        const current = credit.getScaledHeight();
        if (current) {
            credit.scale((credit.scaleX || 1) * (wanted / current)).setCoords();
            credit._gatBaseScale = credit.scaleX;
        }
        await this.place(opts.position || "bottom-left");
        return this;
    },

    /**
     * Repeat the most recently added element. Clones the fabric object rather
     * than fetching and decoding the image again — a row of six bicycles for
     * a bike-swap poster is one decode, not six.
     *
     * The clones are returned so the caller can place each one; the tracked
     * element stays the original.
     *
     * @param {Object} [options]
     * @param {number} [options.count=1]
     * @returns {Promise<Array>} the new objects, in creation order
     */
    async duplicate(options) {
        const opts = options || {};
        const source = this.lastAdded();
        if (!source) throw new Error("duplicate() needs an element that was added first");
        this._assertEditable(source, "duplicate");

        const count = typeof opts.count === "number" ? Math.floor(opts.count) : 1;
        if (!(count > 0)) throw new Error("duplicate() needs a positive count");

        const clones = [];
        for (let i = 0; i < count; i++) {
            const clone = await new Promise(function (resolve) {
                source.clone(resolve);
            });
            clone._gatBaseScale = source._gatBaseScale || source.scaleX || 1;
            canvas.add(clone);
            clones.push(clone);
        }
        CanvasUtils.bringLogoToFront();
        canvas.renderAll();
        return clones;
    },

    _resolveTarget(which) {
        if (which && typeof which === "object") return which;
        if (typeof which === "number") {
            const target = canvas.getObjects()[which];
            if (!target) {
                throw new Error(
                    `No element at index ${which}. See Bildgenerator.objects().`
                );
            }
            return target;
        }
        throw new Error("Expected an index from objects(), or an element that was added first");
    },

    /**
     * Measure the current design against the checkable rules.
     *
     * Taste is not testable, but several of the things that make a sujet look
     * wrong are — elements that collide, type breaking the protective margin,
     * text too small to survive the export, a composition leaning to one side,
     * accents competing with each other.
     *
     * Everything is computed from the canvas object model; there is no second
     * rendering pass. Errors are brand rules, warnings are judgement calls.
     *
     * @returns {{ok: boolean, errors: Array, warnings: Array, findings: Array}}
     */
    check() {
        return QualityCheck.run();
    },

    // ----------------------------------------------------------- step 4: export

    /**
     * Export the canvas. Unwraps CanvasUtils.exportCanvas() and reports the
     * pixel dimensions the caller actually gets — the template's dimensions
     * multiplied by dpi/72.
     *
     * @param {Object} [options]
     * @param {string} [options.format='png']  png | jpeg
     * @param {number} [options.quality=1]     0-1, only meaningful for jpeg
     * @param {number} [options.dpi=200]
     */
    async export(options) {
        const opts = options || {};

        // The same gate the download button applies (event-handlers.js).
        // Without it an image rendered before a logo was chosen ships a blank
        // white logo bar — the app draws the bar unconditionally and only the
        // region name is missing, so nothing looks broken until someone
        // publishes it.
        const validation = ValidationUtils.validateDownload();
        if (!validation.isValid) {
            throw new Error(validation.error);
        }

        // Report the measurable problems alongside the image rather than
        // blocking on them: only the caller knows whether a 12 % overlap is a
        // mistake or the intended composition. Silence, though, would make
        // the whole checker pointless — nobody calls check() by habit.
        const quality = opts.check === false ? null : QualityCheck.run();
        // The download button reads the DPI from the template
        // (event-handlers.js); a hardcoded 200 silently produced a different
        // resolution than the same template yields for a person.
        const template = TemplateConstants.getCurrentTemplate();
        const defaultDpi = (template && template.dpi) || 200;
        const result = await CanvasUtils.exportCanvas(
            opts.format || "png",
            typeof opts.quality === "number" ? opts.quality : 1,
            typeof opts.dpi === "number" ? opts.dpi : defaultDpi
        );
        const scale = result.actualDPI / 72;
        return {
            dataURL: result.dataURL,
            width: Math.round(canvas.width * scale),
            height: Math.round(canvas.height * scale),
            dpi: result.actualDPI,
            quality: quality,
        };
    },

    // ------------------------------------------------------------ all at once

    /**
     * Build one image end to end. Order matters and is fixed here: the template
     * rebuilds the canvas, the background sits behind everything, and the rest
     * is centred against what is already there. Text goes last so it stays on
     * top of the elements.
     *
     * @param {Object} spec
     * @param {string}   spec.template      from templates()
     * @param {string}   [spec.background]  data: URL or absolute URL
     * @param {string}   [spec.logo]        from logos()
     * @param {boolean}  [spec.logoEnabled] false to leave the logo off
     * @param {boolean}  [spec.logoKnockout] transparent region name; turns
     *                                       itself off when a background is set
     * @param {string}   [spec.text]        a single headline
     * @param {Array}    [spec.texts]       several texts, each
     *                                      { text, color, fontStyle, align,
     *                                        lineHeight, shadow, size, rotate,
     *                                        position }
     * @param {string}   [spec.textColor]   from options().textColors
     * @param {string}   [spec.fontStyle]   from options().fontStyles
     * @param {string}   [spec.align]       left | center | right — alignment
     *                                      WITHIN the text block, not on the canvas
     * @param {string}   [spec.lineHeight]  from options().lineHeights
     * @param {number}   [spec.shadow]      shadow depth
     * @param {number}   [spec.textSize]    scale, 1 = as added
     * @param {number}   [spec.textRotate]  degrees clockwise
     * @param {string}   [spec.textPosition] from options().positions
     * @param {Array}    [spec.shapes]      names from options().shapes, or
     *                                      { kind, position, size, rotate } objects
     * @param {Array}    [spec.images]      URLs, or { url, position } objects
     * @param {Object}   [spec.qr]          { text, color, position }
     * @param {string}   [spec.format='png']
     * @param {number}   [spec.quality]
     * @param {number}   [spec.dpi=200]
     * @returns {Promise<{dataURL: string, width: number, height: number, dpi: number}>}
     */
    async render(spec) {
        if (!spec || typeof spec !== "object" || !spec.template) {
            throw new Error("render() needs at least { template }");
        }
        await this.setTemplate(spec.template);
        // LogoState persists across calls, so a previous render({logoEnabled:
        // false}) would silently drop the logo from the next one. Derive the
        // state from the spec instead: a logo is wanted when one is named, or
        // when logoEnabled says so explicitly. Naming neither means none —
        // otherwise export() would refuse the render for a logo the caller
        // never asked for.
        await this.setLogoEnabled(spec.logoEnabled === true || !!spec.logo);
        if (spec.background) await this.setBackground(spec.background);
        if (spec.logo) await this.setLogo(spec.logo);
        // After the logo, so it survives addLogo() being called again.
        if (spec.logoKnockout !== undefined) await this.setLogoKnockout(spec.logoKnockout);

        // Everything lands centred, so each element is placed right after it is
        // added — otherwise the next one would cover it.
        for (const image of spec.images || []) {
            const url = typeof image === "string" ? image : image.url;
            await this.addImage(url);
            if (image.position) await this.place(image.position);
        }
        for (const shape of spec.shapes || []) {
            const kind = typeof shape === "string" ? shape : shape.kind;
            await this.addShape(kind);
            if (typeof shape.rotate === "number") await this.rotate(shape.rotate);
            if (typeof shape.size === "number") await this.resize(shape.size);
            if (shape.position) await this.place(shape.position);
        }
        if (spec.qr) {
            await this.addQRCode(spec.qr);
            if (spec.qr.position) await this.place(spec.qr.position);
        }
        // One headline via `text`, or several via `texts`. Each entry carries
        // its own colour, size and position — a second text would otherwise
        // land centred on top of the first.
        const texts = spec.texts
            ? spec.texts.slice()
            : (spec.text
                ? [{
                    text: spec.text,
                    color: spec.textColor,
                    fontStyle: spec.fontStyle,
                    align: spec.align,
                    lineHeight: spec.lineHeight,
                    shadow: spec.shadow,
                    position: spec.textPosition,
                    size: spec.textSize,
                    rotate: spec.textRotate,
                }]
                : []);

        for (const entry of texts) {
            const item = typeof entry === "string" ? { text: entry } : entry;
            await this.addText(item.text, item);
            if (typeof item.size === "number") await this.resize(item.size);
            if (typeof item.rotate === "number") await this.rotate(item.rotate);
            if (item.position) await this.place(item.position);
        }
        return this.export({
            format: spec.format,
            quality: spec.quality,
            dpi: spec.dpi,
        });
    },

    // ------------------------------------------------ standalone QR generator

    /**
     * The second tool on this page: a QR code on its own, not placed on an
     * image. Calls the generator directly instead of walking its three-step
     * wizard — the wizard only collects these same values.
     *
     * @param {Object} spec
     * @param {string} spec.data         encoded content (URL, text, mailto:, vCard)
     * @param {string} [spec.color]      from options().qrColors
     * @param {string} [spec.background] from options().qrBackgrounds ('transparent' allowed)
     * @returns {Promise<{dataURL: string, width: number, height: number}>}
     */
    async renderQRCode(spec) {
        const opts = spec || {};
        const data = opts.data || this.qrPayload(opts);
        if (!data) {
            throw new Error(
                "renderQRCode() needs { data }, or a { type } of " +
                "text | url | email | vcard with its fields"
            );
        }

        const element = await generateQRCode(
            data,
            opts.color || "#000000",
            opts.background || "#FFFFFF"
        );
        return {
            dataURL: element.toDataURL("image/png"),
            width: element.width,
            height: element.height,
        };
    },

    /**
     * Build the payload string for one of the four content types the QR wizard
     * offers. Without this a caller has to hand-assemble mailto: query strings
     * and vCard records — error-prone, and the formatting rules already live
     * in the app.
     *
     * @param {Object} spec { type: 'text'|'url'|'email'|'vcard', ...fields }
     * @returns {string|null}
     */
    qrPayload(spec) {
        const opts = spec || {};
        switch (opts.type) {
            case "text":
                return opts.text || null;
            case "url":
                return opts.url ? QRFormatHelpers.formatURL(opts.url) : null;
            case "email":
                return opts.email
                    ? QRFormatHelpers.formatEmail(opts.email, opts.subject || "", opts.body || "")
                    : null;
            case "vcard":
                return QRFormatHelpers.formatVCard(opts);
            default:
                return null;
        }
    },

    // ----------------------------------------------------------- housekeeping

    /**
     * Back to a blank design. Calls resetWizard() directly — the #start-over
     * button guards itself with a confirm() dialog that nothing can answer in
     * a headless run.
     */
    async reset() {
        this._lastAdded = null;
        resetWizard();
        await this._waitUntilQuiet();
        return this;
    },

    // --------------------------------------------------------------- internals

    /**
     * Run an action that adds one object and remember which one it was.
     *
     * "The last object" is NOT the one just added: addLogo() and the QR
     * handler both call bringLogoToFront(), which moves the organisation logo
     * to the end of the list. Taking the last entry would hand back the logo —
     * and place() or clipToCircle() would then move or crop the logo instead
     * of the new element.
     */
    async _addTracking(action, description) {
        const before = new Set(canvas.getObjects());
        await action();
        await this._waitFor(
            () => canvas.getObjects().some((o) => !before.has(o)),
            description
        );
        await this._waitUntilQuiet();
        this._lastAdded = canvas.getObjects().find((o) => !before.has(o)) || null;
        if (this._lastAdded) {
            // The size the app gave it. resize() is relative to THIS, not to
            // fabric's raw scale: a text is inserted at roughly 0.1, so an
            // absolute resize(0.35) would make it three times wider than the
            // canvas instead of a third of its size.
            this._lastAdded._gatBaseScale = this._lastAdded.scaleX || 1;
        }
        return this._lastAdded;
    },

    _optionValues(selector) {
        return jQuery(`${selector} option`)
            .map(function () { return this.value; })
            .get()
            .filter(Boolean);
    },

    /**
     * Expand one of the collapsed panels in step 3. Some have a dedicated
     * opener button, the rest go through toggleSection().
     */
    _openSection(id, openerSelector) {
        const section = document.getElementById(id);
        if (!section || !section.classList.contains("hidden")) return;
        if (openerSelector && document.querySelector(openerSelector)) {
            jQuery(openerSelector).trigger("click");
        } else {
            toggleSection(id);
        }
    },

    /**
     * Wait until the canvas stops changing. The app signals readiness through
     * canvas state rather than events — addLogo() alone is two async image
     * loads deep — so quiescence is the only honest completion signal.
     */
    /**
     * A cheap fingerprint of everything the canvas currently shows.
     *
     * Counting objects is not enough. A late image load can replace an object
     * or move one without changing the count — addLogo() removes the old logo
     * and adds the flattened one, and replaceCanvas() re-adds a logo whose
     * geometry only settles once both image decodes finish. The count is
     * identical before and after; the picture is not.
     */
    _canvasSignature() {
        if (!canvas) return "";
        return canvas.getObjects().map(function (o) {
            return [
                o.type,
                Math.round(o.left),
                Math.round(o.top),
                Math.round(o.getScaledWidth()),
                Math.round(o.getScaledHeight()),
                Math.round(o.angle || 0),
                o.type === "text" ? o.text : "",
            ].join(",");
        }).join("|");
    },

    /**
     * Wait until the canvas stops changing. The app offers no completion
     * signal — addLogo() alone is two async image decodes deep — so stability
     * of the rendered state is the only honest one available.
     */
    async _waitUntilQuiet(settleMs = 500, timeout = 15000) {
        const started = Date.now();
        let last = null;
        let stableSince = 0;
        while (Date.now() - started < timeout) {
            const signature = this._canvasSignature();
            if (signature !== last) {
                last = signature;
                stableSince = Date.now();
            } else if (Date.now() - stableSince >= settleMs) {
                return;
            }
            await new Promise((resolve) => setTimeout(resolve, 50));
        }
        throw new Error(`Canvas never settled within ${timeout}ms`);
    },

    /**
     * Poll until a condition holds. Fails loudly with what it was waiting for:
     * a caller that times out here has hit a changed UI, and a silent partial
     * render would be worse than an error.
     */
    async _waitFor(condition, description, timeout = 15000) {
        const started = Date.now();
        while (Date.now() - started < timeout) {
            let ok = false;
            try {
                ok = !!condition();
            } catch (e) {
                ok = false;
            }
            if (ok) return;
            await new Promise((resolve) => setTimeout(resolve, 100));
        }
        throw new Error(`Timed out after ${timeout}ms waiting for ${description}`);
    },
};

if (typeof window !== "undefined") {
    window.Bildgenerator = Bildgenerator;
}

